import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  X,
  Printer,
  Calendar,
  Shield,
  Activity,
  Sparkles,
  Layers,
  FileText,
  CheckCircle,
  MapPin,
  History,
  PenTool,
  Trash2,
  Save,
  Share2,
  Upload,
  Mail,
  AlertTriangle,
  LogOut,
  LogIn,
  Copy,
  FolderOpen,
} from "lucide-react";
import { IQDoctorLogo } from "./IQDoctorLogo";
import { QRCodeSVG } from "qrcode.react";
import {
  Patient,
  Vitals,
  ClinicalRecord,
  Order,
  LaserTherapyEntry,
  MedicalHistory,
} from "../types";
import { LabTrendChart } from "./LabTrendChart";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import Markdown from "react-markdown";
import {
  initWorkspaceAuth,
  signInWithGoogleWorkspace,
  logoutWorkspace,
  sendGmailEmail,
  sendGmailEmailWithAttachment,
} from "../lib/firebase";
import SendTelegramButton from "./SendTelegramButton";

/**
 * CLINICAL IMAGE UTILITY: Safely sanitizes CSS property definitions to prevent html2canvas
 * parser crashes when encountering modern W3C color specs (such as oklch, oklab, and color-mix).
 * Uses extremely fast, optimized linear RegExp patterns protecting up to 2 levels of nested parentheses.
 */
const cleanStringOfModernColors = (str: string): string => {
  if (!str) return str;
  try {
    // Single-pass high-performance regular expressions mapping modern CSS parameters to slate-400
    return str
      .replace(/color-mix\([^()]*(\([^()]*\)[^()]*)*\)/gi, "rgb(148, 163, 184)")
      .replace(/oklch\([^()]*(\([^()]*\)[^()]*)*\)/gi, "rgb(148, 163, 184)")
      .replace(/oklab\([^()]*(\([^()]*\)[^()]*)*\)/gi, "rgb(148, 163, 184)")
      .replace(
        /light-dark\([^()]*(\([^()]*\)[^()]*)*\)/gi,
        "rgb(148, 163, 184)",
      );
  } catch (err) {
    console.error(
      "[Clinical Utility Error] Failed rapid CSS coloring sanitization:",
      err,
    );
    return str;
  }
};

interface PatientEhrPrintPreviewProps {
  patient: Patient;
  vitals: Vitals | null;
  clinicalRecords: ClinicalRecord[];
  orders: Order[];
  medicalHistory?: MedicalHistory | null;
  docType?:
    | "RECORD"
    | "REFERRAL"
    | "CONSENT"
    | "CONSENT_PRIVATE_EXAM"
    | "PATIENT_REPORT"
    | "PRESCRIPTION"
    | "INVESTIGATION"
    | "VISIT_ORDERS"
    | "LEAFLET_ACNE"
    | "LEAFLET_ECZEMA"
    | "LEAFLET_PSORIASIS"
    | "LEAFLET_CUSTOM"
    | "MEDICATION_PROFILE"
    | "BILL_10000"
    | "SICK_LEAVE";
  selectedOrderToPrint?: Order | null;
  activeSession?: {
    editingRecordId?: string | null;
    printableDocs?: string;
    physicianId?: string;
    physicianName?: string;
    gpNotes: string;
    soapSubjective?: string;
    soapObjective?: string;
    soapAssessment?: string;
    soapPlan?: string;
    icd10Codes?: string;
    prescriptions?: {
      name: string;
      dose: string;
      frequency: string;
      duration: string;
    }[];
    draftInvestigations?: { name: string; dept: "LAB" | "X_RAY" }[];
    faceMarks: LaserTherapyEntry[];
    minorSurgery: string;
    aiAnalysis: any;
    customLeafletResult?: string;
  };
  apiToken?: string;
  onClose: () => void;
}

interface SignaturePadProps {
  onSave: (dataUrl: string) => void;
  onClear: () => void;
  label: string;
}

function SignaturePad({ onSave, onClear, label }: SignaturePadProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.strokeStyle = "#312e81"; // IQ-Doctor blue stamp ink color
        ctx.lineWidth = 3.5; // Thicker line for realism and similarity to provided image
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
      }
    }
  }, []);

  const startDrawing = (
    e:
      | React.MouseEvent<HTMLCanvasElement>
      | React.TouchEvent<HTMLCanvasElement>,
  ) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let clientX, clientY;
    if ("touches" in e) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }

    const rect = canvas.getBoundingClientRect();
    ctx.beginPath();
    ctx.moveTo(clientX - rect.left, clientY - rect.top);
    setIsDrawing(true);
    setHasDrawn(true);
  };

  const draw = (
    e:
      | React.MouseEvent<HTMLCanvasElement>
      | React.TouchEvent<HTMLCanvasElement>,
  ) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let clientX, clientY;
    if ("touches" in e) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }

    const rect = canvas.getBoundingClientRect();
    ctx.lineTo(clientX - rect.left, clientY - rect.top);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const handleClear = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
    onClear();
  };

  const handleSave = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    onSave(canvas.toDataURL("image/png"));
  };

  return (
    <div className="flex flex-col gap-2 print:gap-1 no-print">
      <div className="flex justify-between items-center">
        <label className="text-xs print:text-sm font-bold text-slate-700 uppercase">
          {label}
        </label>
        <div className="flex gap-2 print:gap-1">
          <button
            type="button"
            onClick={handleClear}
            className="flex items-center gap-1 px-2 py-1 text-[10px] print:text-[11px] bg-rose-50 text-rose-600 rounded hover:bg-rose-100 transition border border-rose-200"
          >
            <Trash2 className="w-3 h-3" /> Clear
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!hasDrawn}
            className="flex items-center gap-1 px-2 py-1 text-[10px] print:text-[11px] bg-emerald-50 text-emerald-600 rounded hover:bg-emerald-100 transition border border-emerald-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save className="w-3 h-3" /> Lock Signature
          </button>
        </div>
      </div>
      <div className="border-2 border-dashed border-slate-300 rounded-lg bg-slate-50 relative overflow-hidden">
        <canvas
          ref={canvasRef}
          width={400}
          height={120}
          className="w-full touch-none cursor-crosshair bg-white"
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseOut={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
        />
        {!hasDrawn && (
          <div className="absolute inset-0 pointer-events-none flex items-center justify-center opacity-30 flex-col gap-1 text-slate-400">
            <PenTool className="w-6 h-6" />
            <span className="text-[10px] print:text-[11px] font-bold uppercase tracking-wider">
              Sign Here
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

export default function PatientEhrPrintPreview({
  patient,
  vitals,
  clinicalRecords,
  orders,
  medicalHistory,
  docType = "RECORD",
  selectedOrderToPrint,
  activeSession,
  apiToken,
  onClose,
}: PatientEhrPrintPreviewProps) {
  // ADDITIONAL CLINICAL RULES: Editable Patient Report fields for perfect bilingual execution

  const cachedUserStr = localStorage.getItem("clinic_user");
  let cachedUser = null;
  if (cachedUserStr) {
    try {
      cachedUser = JSON.parse(cachedUserStr);
    } catch (e) {
      console.error("Failed to parse clinic_user", e);
    }
  }
  // If printing a bill, the attending doctor is Dr. Watheq Abdul Khaliq Al-Chalabi like other printouts
  const rawDoctorName =
    docType === "BILL_10000"
      ? "Dr. Watheq"
      : activeSession?.physicianName ||
        clinicalRecords[0]?.physicianName ||
        cachedUser?.name ||
        "Doctor";
  const currentPhysicianEn =
    rawDoctorName?.includes("واثق") || rawDoctorName?.includes("Watheq")
      ? "Dr. Watheq AlChalabi"
      : rawDoctorName?.includes("جمانة")
        ? "Dr. Jumana Mustafa"
        : rawDoctorName;

  const isJumana =
    rawDoctorName?.includes("جمانة") ||
    rawDoctorName?.includes("Jumana") ||
    rawDoctorName?.includes("ali");
  const isWatheq =
    rawDoctorName?.includes("واثق") || rawDoctorName?.includes("Watheq");
  const currentPhysicianTitleEn = isWatheq
    ? "General Practitioner - Dermatology"
    : isJumana
      ? "Dentist & Aesthetics"
      : "Attending Physician";
  const currentPhysicianTitleAr = isWatheq
    ? "طبيب ممارس - أمراض جلدية"
    : isJumana
      ? "طبيبة أسنان وتجميل"
      : "طبيب ممارس";
  const currentRegNo = isWatheq ? "17582" : isJumana ? "65432" : "----";

  let parsedDocs: any = {};
  if (activeSession?.printableDocs) {
    try {
      parsedDocs = JSON.parse(activeSession.printableDocs);
    } catch (e) {
      console.error("Failed to parse printableDocs:", e);
    }
  }
  const displayPatient = parsedDocs.patientSnapshot ? { ...parsedDocs.patientSnapshot, id: patient?.id || "UNKNOWN" } : { ...(patient || {}), id: patient?.id || "UNKNOWN" } as any;

  const recordKey = activeSession?.editingRecordId || displayPatient?.id || "UNKNOWN";

  const [savedDate, setSavedDate] = useState<string>(() => {
    return (
      parsedDocs.savedDate ||
      localStorage.getItem(`doc_saved_date_${recordKey}`) ||
      ""
    );
  });

  const currentPhysicianAr =
    rawDoctorName?.includes("واثق") || rawDoctorName?.includes("Watheq")
      ? "د. واثق عبدالخالق الجلبي"
      : rawDoctorName?.includes("جمانة")
        ? "دكتوره جمانة مصطفى الجلبي"
        : rawDoctorName;

  const [patientSignature, setPatientSignature] = useState<string | null>(
    () => parsedDocs.patientSignature || localStorage.getItem(`patient_signature_${recordKey}`) || null
  );
  const [physicianSignature, setPhysicianSignature] = useState<string | null>(
    () => {
      return rawDoctorName?.includes("واثق") ||
        rawDoctorName?.includes("Watheq")
        ? "/watheq_signature.svg"
        : null;
    },
  );

  const [isSavingToAttachments, setIsSavingToAttachments] = useState(false);

  const [reportTitle, setReportTitle] = useState(
    () =>
      parsedDocs.reportTitle ||
      localStorage.getItem(`report_title_${recordKey}`) ||
      "Personal Medical Report / التقرير الطبي الشخصي",
  );
  const [reportSub, setReportSub] = useState(
    () =>
      parsedDocs.reportSub ||
      localStorage.getItem(`report_sub_${recordKey}`) ||
      "This report summarizes your recent clinical evaluation at Dr. Wathiq Abdul-Khaliq Al-Chalabi Clinic. / يلخص هذا التقرير تقييمك السريري الأخير في عيادة الدكتور واثق عبدالخالق الجلبي.",
  );
  const [reportInstructionsHeader, setReportInstructionsHeader] = useState(
    () =>
      parsedDocs.reportInstructionsHeader ||
      localStorage.getItem(`report_instr_hdr_${recordKey}`) ||
      "Clinical Instructions & Plan / التعليمات الطبية والخطة العلاجية",
  );
  const [reportInstructions, setReportInstructions] = useState(
    () =>
      parsedDocs.reportInstructions ||
      localStorage.getItem(`report_instr_${recordKey}`) ||
      "",
  );

  // Editable consultation bill values (Additive ONLY)
  const [billPrice, setBillPrice] = useState(
    () =>
      parsedDocs.billPrice ||
      localStorage.getItem(`bill_price_${recordKey}`) ||
      "10,000",
  );
  const [billPriceWords, setBillPriceWords] = useState(
    () =>
      parsedDocs.billPriceWords ||
      localStorage.getItem(`bill_price_words_${recordKey}`) ||
      "عشرة آلاف دينار عراقي فقط لا غير",
  );
  const [billCategory, setBillCategory] = useState(
    () =>
      parsedDocs.billCategory ||
      localStorage.getItem(`bill_category_${recordKey}`) ||
      "Medical Consultation Visit / كشفية طبية",
  );
  const [receiptName, setReceiptName] = useState(
    () =>
      parsedDocs.receiptName ||
      localStorage.getItem(`receipt_name_${recordKey}`) ||
      displayPatient.name,
  );
  const [paymentMethod, setPaymentMethod] = useState(
    () =>
      parsedDocs.paymentMethod ||
      localStorage.getItem(`payment_method_${recordKey}`) ||
      "نقداً / Cash",
  );
  const [paymentStatus, setPaymentStatus] = useState(
    () =>
      parsedDocs.paymentStatus ||
      localStorage.getItem(`payment_status_${recordKey}`) ||
      "مدفوعة بالكامل / Paid",
  );

  // Editable fields for Referral
  const [referralFindingsHeader, setReferralFindingsHeader] = useState(
    () =>
      parsedDocs.referralFindingsHeader ||
      localStorage.getItem(`referral_findings_hdr_${recordKey}`) ||
      "Recent Clinical Findings:",
  );
  const [referralFindings, setReferralFindings] = useState(() => {
    if (parsedDocs.referralFindings) return parsedDocs.referralFindings;
    const saved = localStorage.getItem(`referral_findings_${recordKey}`);
    if (saved !== null) return saved;
    return activeSession?.gpNotes || "";
  });
  const [referralAdditionalNotes, setReferralAdditionalNotes] = useState(
    () =>
      parsedDocs.referralAdditionalNotes ||
      localStorage.getItem(`referral_add_notes_${recordKey}`) ||
      "",
  );

  const [chaperoneAgreed, setChaperoneAgreed] = useState<boolean>(() => {
    if (parsedDocs.chaperoneAgreed !== undefined) return parsedDocs.chaperoneAgreed;
    return localStorage.getItem(`chaperone_agreed_${recordKey}`) === "true";
  });

  const [chaperoneRefused, setChaperoneRefused] = useState<boolean>(() => {
    if (parsedDocs.chaperoneRefused !== undefined) return parsedDocs.chaperoneRefused;
    return localStorage.getItem(`chaperone_refused_${recordKey}`) === "true";
  });

  const dispatchClinicalEvent = (action: string, details?: string) => {
    window.dispatchEvent(
      new CustomEvent("clinical_event", { detail: { action, details } })
    );
  };

  const initialRenderRef = useRef(true);
  useEffect(() => {
    if (initialRenderRef.current) {
      initialRenderRef.current = false;
      return;
    }
    if (patientSignature) dispatchClinicalEvent("تحديث المطبوعات", "تم توقيع المريض");
  }, [patientSignature]);

  useEffect(() => {
    if (initialRenderRef.current) return;
    if (physicianSignature) dispatchClinicalEvent("تحديث المطبوعات", "تم توقيع الطبيب");
  }, [physicianSignature]);

  useEffect(() => {
    if (initialRenderRef.current) return;
    dispatchClinicalEvent("تحديث المطبوعات", `حالة مرافق طبي (موافق: ${chaperoneAgreed}, أرفض: ${chaperoneRefused})`);
  }, [chaperoneAgreed, chaperoneRefused]);

  // Editable fields for Official Medical Report (RECORD) - Consolidated into a single, clean text field
  const [recordClinicalNotes, setRecordClinicalNotes] = useState(() => {
    if (parsedDocs.recordClinicalNotes) return parsedDocs.recordClinicalNotes;
    const saved = localStorage.getItem(`rec_clinical_notes_${recordKey}`);
    if (saved !== null) return saved;
    if (activeSession) {
      const parts = [];
      if (activeSession.soapSubjective) { const val = activeSession.soapSubjective.trim() === "N/A" ? " " : activeSession.soapSubjective.trim(); parts.push(`الأعراض والشكوى السريرية:\n${val}`); }
      if (activeSession.soapObjective) { const val = activeSession.soapObjective.trim() === "N/A" ? " " : activeSession.soapObjective.trim(); parts.push(`الأعراض الموضوعية والقياسات العيادية:\n${val}`); }
      if (activeSession.soapAssessment) { const val = activeSession.soapAssessment.trim() === "N/A" ? " " : activeSession.soapAssessment.trim(); parts.push(`التشخيص والتقييم السريري:\n${val}`); }
      if (activeSession.soapPlan) { const val = activeSession.soapPlan.trim() === "N/A" ? " " : activeSession.soapPlan.trim(); parts.push(`الخطة العلاجية والتوجيهات الطبية:\n${val}`); }
      if (parts.length > 0) {
        return parts.join("\n\n");
      }
    }
    return "لا توجد ملاحظات سريرية مسجلة للزيارة الحالية.";
  });

  useEffect(() => {
    if (
      activeSession &&
      !localStorage.getItem(`rec_clinical_notes_${recordKey}`)
    ) {
      const parts = [];
      if (activeSession.soapSubjective) { const val = activeSession.soapSubjective.trim() === "N/A" ? " " : activeSession.soapSubjective.trim(); parts.push(`الأعراض والشكوى السريرية:\n${val}`); }
      if (activeSession.soapObjective) { const val = activeSession.soapObjective.trim() === "N/A" ? " " : activeSession.soapObjective.trim(); parts.push(`الأعراض الموضوعية والقياسات العيادية:\n${val}`); }
      if (activeSession.soapAssessment) { const val = activeSession.soapAssessment.trim() === "N/A" ? " " : activeSession.soapAssessment.trim(); parts.push(`التشخيص والتقييم السريري:\n${val}`); }
      if (activeSession.soapPlan) { const val = activeSession.soapPlan.trim() === "N/A" ? " " : activeSession.soapPlan.trim(); parts.push(`الخطة العلاجية والتوجيهات الطبية:\n${val}`); }
      if (parts.length > 0) {
        setRecordClinicalNotes(parts.join("\n\n"));
      }
    }
  }, [activeSession, displayPatient.id]);

  useEffect(() => {
    if (activeSession?.gpNotes) {
      if (!localStorage.getItem(`report_instr_${recordKey}`))
        setReportInstructions(activeSession.gpNotes);
      if (!localStorage.getItem(`referral_findings_${recordKey}`))
        setReferralFindings(activeSession.gpNotes);
    } else if (!localStorage.getItem(`report_instr_${recordKey}`)) {
      setReportInstructions(
        "No specific instructions provided. / لا توجد تعليمات محددة.",
      );
    }
  }, [activeSession?.gpNotes, displayPatient.id]);

  // Synchronize all printable state to localStorage for the Dashboard to save
  useEffect(() => {
    const setOrRemove = (key: string, val: string | null) => {
      if (val !== null && val !== undefined) {
        localStorage.setItem(key, val);
      } else {
        localStorage.removeItem(key);
      }
    };
    
    setOrRemove(`report_title_${recordKey}`, reportTitle);
    setOrRemove(`report_sub_${recordKey}`, reportSub);
    setOrRemove(`report_instr_hdr_${recordKey}`, reportInstructionsHeader);
    setOrRemove(`report_instr_${recordKey}`, reportInstructions);
    setOrRemove(`bill_price_${recordKey}`, billPrice);
    setOrRemove(`bill_price_words_${recordKey}`, billPriceWords);
    setOrRemove(`bill_category_${recordKey}`, billCategory);
    setOrRemove(`receipt_name_${recordKey}`, receiptName);
    setOrRemove(`referral_findings_hdr_${recordKey}`, referralFindingsHeader);
    setOrRemove(`referral_findings_${recordKey}`, referralFindings);
    setOrRemove(`referral_add_notes_${recordKey}`, referralAdditionalNotes);
    setOrRemove(`rec_clinical_notes_${recordKey}`, recordClinicalNotes);
    setOrRemove(`payment_method_${recordKey}`, paymentMethod);
    setOrRemove(`payment_status_${recordKey}`, paymentStatus);
    setOrRemove(`patient_signature_${recordKey}`, patientSignature);
    setOrRemove(`physician_signature_${recordKey}`, physicianSignature);
    setOrRemove(`doc_saved_date_${recordKey}`, savedDate);
    setOrRemove(`chaperone_agreed_${recordKey}`, chaperoneAgreed ? "true" : "false");
    setOrRemove(`chaperone_refused_${recordKey}`, chaperoneRefused ? "true" : "false");
  }, [
    recordKey, reportTitle, reportSub, reportInstructionsHeader, reportInstructions,
    billPrice, billPriceWords, billCategory, receiptName, referralFindingsHeader,
    referralFindings, referralAdditionalNotes, recordClinicalNotes, paymentMethod,
    paymentStatus, patientSignature, physicianSignature, savedDate, chaperoneAgreed, chaperoneRefused
  ]);

  // Custom states for unified sharing (WhatsApp/Telegram)
  const [sharePdfData, setSharePdfData] = useState<{
    filename: string;
    pdfBlobUrl: string;
    shareUrl: string;
    blob?: Blob;
  } | null>(null);
  const [shareMessageText, setShareMessageText] = useState<string>("");
  const [sharePlatform, setSharePlatform] = useState<"whatsapp" | "telegram">(
    "telegram",
  );
  const [isGeneratingPdf, setIsGeneratingPdf] = useState<boolean>(false);

  // Attachment state inside Telegram Sharing dialogue
  const [isConvertingAttachment, setIsConvertingAttachment] =
    useState<boolean>(false);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState<boolean>(false);

  // Clinical Print Review: Interactive state variables governing on-screen layout simulation
  const [isPreviewMode, setIsPreviewMode] = useState<boolean>(false);
  const [simulatedMargin, setSimulatedMargin] = useState<string>("15mm");
  const [highContrastMode, setHighContrastMode] = useState<boolean>(true);
  const [showPageBreakGuides, setShowPageBreakGuides] = useState<boolean>(true);

  const printDate = savedDate || new Date().toLocaleString("en-GB");

  const waMessageContent = (() => {
    let docTitle = "";
    switch (docType) {
      case "REFERRAL":
        docTitle = "إحالة طبية رسمية - Formal Medical Referral";
        break;
      case "CONSENT":
        docTitle = "نموذج موافقة المريض المستنيرة - Patient Informed Consent";
        break;
      case "CONSENT_PRIVATE_EXAM":
        docTitle = "موافقة فحص المناطق الخاصة - Private Area Exam Consent";
        break;
      case "VISIT_ORDERS":
        docTitle = "الطلبات والوصفات الطبية للزيارة - Visit Orders & Rx";
        break;
      case "PRESCRIPTION":
        docTitle = "روشتة طبية إلكترونية - Electronic Prescription (e-Rx)";
        break;
      case "INVESTIGATION":
        docTitle = "طلب فحوصات وتحاليل طبية - Investigation Request";
        break;
      case "MEDICATION_PROFILE":
        docTitle = "الملف الدوائي الشامل للمريض - Medication Profile";
        break;
      case "PATIENT_REPORT":
        docTitle = "تقرير طبي شخصي - Personal Medical Report";
        break;
      case "RECORD":
        docTitle =
          "تقرير طبي سريري رسمي لمراجع رسمية - Official Clinical Medical Report";
        break;
      case "BILL_10000":
        docTitle = "وصل استلام نقدية الكتروني - Patient Electronic Receipt";
        break;
      default:
        if (docType && docType.startsWith("LEAFLET_")) {
          docTitle = "منشور طبي تثقيفي للمريض - Patient Leaflet";
        } else {
          docTitle = "سجل طبي سريري للمريض - Clinical Health Record";
        }
    }

    let text = `*الدكتور واثق الجلبي (Dr. Watheq Al-Chalabi Clinic)*\n`;
    text += `📢 *المستند:* ${docTitle}\n`;
    text += `👤 *المريض:* ${displayPatient.name}\n`;
    text += `🆔 *رقم الملف:* ${displayPatient.id}\n`;
    text += `📅 *التاريخ:* ${printDate.split(",")[0]}\n`;
    text += `---------------------------------\n\n`;

    if (vitals) {
      text += `🩺 *موجز المؤشرات الحيوية للزيارة (Vitals):*\n`;
      if (vitals.bloodPressure)
        text += `• ضغط الدم: ${vitals.bloodPressure.trim()} mmHg\n`;
      if (vitals.temp) text += `• الحرارة: ${vitals.temp} °C\n`;
      if (vitals.chiefComplaint)
        text += `• الأعراض والشكوى: ${vitals.chiefComplaint.trim()}\n`;
      text += `\n`;
    }

    if (activeSession?.gpNotes) {
      text += `📋 *تعليمات الطبيب وتوصيات الخطة الطبية:*\n`;
      text += `_${activeSession.gpNotes.trim()}_\n\n`;
    }

    text += `🏥 *الدكتور واثق الجلبي (Dr. Watheq Al-Chalabi Clinic)*\n`;
    text += `📍 العراق - بغداد - السيدية\n`;
    text += `🔒 نظام السجلات السريرية الآمن والموثق`;
    return text;
  })();

  // Gmail OAuth and Sending States
  const [showGmailModal, setShowGmailModal] = useState<boolean>(false);
  const [googleUser, setGoogleUser] = useState<any>(null);
  const [googleAccessToken, setGoogleAccessToken] = useState<string | null>(
    null,
  );
  const [isGoogleWorkspaceAuthenticated, setIsGoogleWorkspaceAuthenticated] =
    useState<boolean>(false);
  const [isGoogleWorkspaceLoggingIn, setIsGoogleWorkspaceLoggingIn] =
    useState<boolean>(false);
  const [gmailRecipient, setGmailRecipient] = useState<string>(
    patient.email || "",
  );
  const [gmailSubject, setGmailSubject] = useState<string>(() => {
    let docName = "مستند";
    if (docType === "REFERRAL") docName = "إحالة طبية";
    else if (docType === "CONSENT" || docType === "CONSENT_PRIVATE_EXAM")
      docName = "نموذج موافقة";
    else if (docType === "PRESCRIPTION") docName = "وصفة علاجية";
    else if (docType === "INVESTIGATION") docName = "طلب تحاليل وفحوصات";
    else if (docType === "SICK_LEAVE") docName = "إجازة مرضية";
    else if (docType === "BILL_10000") docName = "وصل طبي";
    else if (docType === "RECORD") docName = "سجل طبي";
    return `عيادة الدكتور واثق عبدالخالق الجلبي - ${docName} للمريض: ${displayPatient.name}`;
  });
  const [isGmailSending, setIsGmailSending] = useState<boolean>(false);
  const [gmailSuccessMessage, setGmailSuccessMessage] = useState<string | null>(
    null,
  );
  const [gmailErrorMessage, setGmailErrorMessage] = useState<string | null>(
    null,
  );
  const [saveEmailToPatientFile, setSaveEmailToPatientFile] =
    useState<boolean>(true);
  const [sickLeaveDays, setSickLeaveDays] = useState<number>(1);

  // Subscribe to google workspace auth state changes
  useEffect(() => {
    const unsubscribe = initWorkspaceAuth(
      (user, token) => {
        setGoogleUser(user);
        setGoogleAccessToken(token);
        setIsGoogleWorkspaceAuthenticated(true);
      },
      () => {
        setGoogleUser(null);
        setGoogleAccessToken(null);
        setIsGoogleWorkspaceAuthenticated(false);
      },
    );
    return () => unsubscribe();
  }, []);

  const handleConnectGmail = async () => {
    setIsGoogleWorkspaceLoggingIn(true);
    setGmailErrorMessage(null);
    try {
      const result = await signInWithGoogleWorkspace();
      if (result) {
        setGoogleUser(result.user);
        setGoogleAccessToken(result.accessToken);
        setIsGoogleWorkspaceAuthenticated(true);
      }
    } catch (err: any) {
      console.error("[Gmail Connect Error]:", err);
      setGmailErrorMessage(
        err.message ||
          "حدث خطأ أثناء الاتصال بحساب Google. يرجى مراجعة إعدادات الأمان وحاول مرة أخرى.",
      );
    } finally {
      setIsGoogleWorkspaceLoggingIn(false);
    }
  };

  const handleDisconnectGmail = async () => {
    if (
      window.confirm(
        "هل أنت متأكد من رغبتك في إلغاء ربط حساب Gmail الخاص بالعيادة؟",
      )
    ) {
      await logoutWorkspace();
      setGoogleUser(null);
      setGoogleAccessToken(null);
      setIsGoogleWorkspaceAuthenticated(false);
    }
  };

  /**
   * Generates a welcoming clinical HTML letter to the patient
   * to accompany the secure digital health document attachment.
   */
  const generateWelcomeLetterHtml = (): string => {
    let vitalsHtml = "";
    if (vitals) {
      vitalsHtml = `
        <div style="background-color: #f8fafc; border-right: 4px solid #0284c7; padding: 12px 15px; margin-bottom: 20px; font-size: 13px; border-radius: 4px; text-align: right; direction: rtl;">
          <strong style="color: #0f172a; display: block; margin-bottom: 6px;">🩺 ملخص صحي سريع للزيارة:</strong>
          <ul style="margin: 0; padding-right: 20px; color: #475569; line-height: 1.5; list-style-type: square; text-align: right; direction: rtl;">
            ${vitals.bloodPressure ? `<li><strong>ضغط الدم:</strong> ${vitals.bloodPressure.trim()} mmHg</li>` : ""}
            ${vitals.temp ? `<li><strong>درجة الحرارة:</strong> ${vitals.temp} °C</li>` : ""}
            ${vitals.chiefComplaint ? `<li><strong>الشكوى الرئيسية لزيارتك:</strong> ${vitals.chiefComplaint.trim()}</li>` : ""}
          </ul>
        </div>
      `;
    }

    return `
      <div dir="rtl" style="font-family: 'Cairo', 'Segoe UI', Tahoma, Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 25px; border: 1px solid #e2e8f0; border-radius: 12px; background-color: #ffffff; text-align: right; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05);">
        <!-- Header banner -->
        <div style="border-bottom: 2px solid #f1f5f9; padding-bottom: 15px; margin-bottom: 20px; text-align: center;">
          <h2 style="color: #0f172a; font-size: 20px; margin: 0; font-weight: bold;">أهلاً بك في الدكتور واثق الجلبي | Dr. Watheq Al-Chalabi Clinic</h2>
          <p style="color: #64748b; font-size: 11px; margin: 5px 0 0 0;">بوابة السجلات والوثائق السريرية المؤمنة والموثقة</p>
        </div>

        <!-- Body text -->
        <div style="color: #334155; font-size: 14px; line-height: 1.6;">
          <p style="margin: 0 0 15px 0; font-weight: bold; color: #1e293b;">عزيزي المريض ${displayPatient.name} المحترم،</p>
          <p style="margin: 0 0 15px 0;">
            يقدم لك الطاقم الطبي لالدكتور واثق الجلبي في بغداد أطيب التحيات والتقدير لثقتك بنا وبخدماتنا. نرجو من الله لك عموم الصحة والرفاه التام.
          </p>
          <p style="margin: 0 0 15px 0;">
            يسعدنا تزويدك بالتقرير الصحي والوثيقة الطبية الرسمية المصاحبة لزيارتك بموجب معايير الرعاية الصحية الشاملة.
            <strong>لقد قمنا بإرفاق وثيقتك الطبية الرسمية بأمان كملف PDF مدمج مع هذه الرسالة الإلكترونية.</strong>
          </p>
          <p style="margin: 0 0 20px 0;">
            يرجى تفقد قسم مرفقات هذا البريد الإلكتروني لتحميل المستند والحفاظ عليه في أرشيفك الطبي الشخصي للرجوع السهل إليه.
          </p>
        </div>

        <!-- Optional Vitals section -->
        ${vitalsHtml}

        <!-- Bilingual fallback divider -->
        <div dir="ltr" style="border-top: 1px solid #f1f5f9; margin-top: 25px; padding-top: 15px; text-align: left; font-family: 'Inter', system-ui, sans-serif; font-size: 12px; color: #64748b; line-height: 1.5;">
          <p style="margin: 0 0 8px 0; font-weight: bold; color: #475569;">Dear ${displayPatient.name || "Patient"},</p>
          <p style="margin: 0 0 8px 0;">
            Welcome to IQ-Doctor Specialist Clinic. We are pleased to provide you with your official digital clinical record.
          </p>
          <p style="margin: 0 0 15px 0;">
            Your clinical receipt or report is attached securely to this message as a high-fidelity PDF attachment. Kindly check your inbox attachments to read and save the file.
          </p>
          <p style="margin: 0; font-size: 10px; color: #94a3b8;">* This email is electronically signed and transmitted using an automated medical system. Do not reply directly.</p>
        </div>

        <!-- Footer signature -->
        <div style="margin-top: 30px; border-top: 1px solid #f1f5f9; padding-top: 15px; text-align: center; font-size: 10px; color: #64748b;">
          <p style="margin: 0; font-weight: bold;">الدكتور واثق الجلبي - بغداد - السيدية | IQ-Doctor Integrated Secure EHR</p>
        </div>
      </div>
    `;
  };

  /**
   * Generates a beautifully styled, clinical-grade HTML representation
   * of the medical report, prescription, or receipt suitable for high-fidelity emails.
   */
  const generateClinicalEmailHtml = (): string => {
    let docTitleAr = "مستند طبي رسمي";
    let docTitleEn = "Clinical Document";
    switch (docType) {
      case "REFERRAL":
        docTitleAr = "إحالة طبية رسمية";
        docTitleEn = "Formal Medical Referral";
        break;
      case "CONSENT":
        docTitleAr = "موافقة مستنيرة للمريض";
        docTitleEn = "Patient Informed Consent";
        break;
      case "CONSENT_PRIVATE_EXAM":
        docTitleAr = "موافقة فحص المناطق الحساسة";
        docTitleEn = "Private Area Exam Consent";
        break;
      case "VISIT_ORDERS":
        docTitleAr = "أوامر الزيارة والوصفات الطبية";
        docTitleEn = "Visit Orders & Prescriptions";
        break;
      case "PRESCRIPTION":
        docTitleAr = "وصفة طبية إلكترونية";
        docTitleEn = "Medical Prescription (e-Rx)";
        break;
      case "SICK_LEAVE":
        docTitleAr = "إجازة مرضية رسمية";
        docTitleEn = "Official Sick Leave Certificate";
        break;
      case "INVESTIGATION":
        docTitleAr = "طلب فحوصات وتحاليل طبية";
        docTitleEn = "Investigation Request Form";
        break;
      case "MEDICATION_PROFILE":
        docTitleAr = "الملف الدوائي الشامل";
        docTitleEn = "Comprehensive Medication Profile";
        break;
      case "BILL_10000":
        docTitleAr = "وصل استلام نقدية الكتروني";
        docTitleEn = "Patient Electronic Receipt";
        break;
      case "RECORD":
      default:
        docTitleAr = "تقرير طبي سريري";
        docTitleEn = "Clinical Record & SOAP Report";
        break;
    }

    const notesText = activeSession?.gpNotes || "";
    const soapS = activeSession?.soapSubjective || "";
    const soapO = activeSession?.soapObjective || "";
    const soapA = activeSession?.soapAssessment || "";
    const soapP = activeSession?.soapPlan || "";

    const ageVal =
      displayPatient.age ||
      (displayPatient.dob
        ? new Date().getFullYear() - new Date(displayPatient.dob).getFullYear()
        : "غير محدد");

    let rxHtml = "";
    let emailRxList: Array<{ name: string; dose?: string; frequency?: string; duration?: string; }> = [];
    if (selectedOrderToPrint && docType === "PRESCRIPTION") {
      const lines = selectedOrderToPrint.instruction.split("\n");
      emailRxList = lines.map(line => {
         let namePart = line;
         let dosePart = "As directed";
         let freqPart = "As directed";
         let durPart = "As directed";
         
         const parts = line.split("|");
         if (parts.length > 0) namePart = parts[0].trim().replace(/^\d+\.\s*/, '');
         if (parts.length > 1) dosePart = parts[1].replace("Dose:", "").trim();
         if (parts.length > 2) freqPart = parts[2].replace("Freq:", "").trim();
         if (parts.length > 3) durPart = parts[3].replace("Dur:", "").trim();
         
         return { name: namePart, dose: dosePart, frequency: freqPart, duration: durPart };
      }).filter(rx => rx.name.length > 0);
    } else if (activeSession?.prescriptions && activeSession.prescriptions.length > 0) {
      emailRxList = activeSession.prescriptions;
    }

    if (emailRxList.length > 0) {
      rxHtml = `
        <div style="margin-top: 20px; border: 1px solid #cbd5e1; border-radius: 8px; overflow: hidden;">
          <h4 style="color: #ffffff; background-color: #0d9488; margin: 0; padding: 10px 15px; font-size: 14px; text-align: right; font-weight: bold;">💊 الوصفة الطبية المعتمدة / e-Prescription (e-Rx)</h4>
          <table style="width: 100%; border-collapse: collapse; font-size: 12px; text-align: right; direction: rtl;">
            <thead>
              <tr style="background-color: #f8fafc; border-bottom: 2px solid #cbd5e1; font-weight: bold;">
                <th style="padding: 10px; border-left: 1px solid #e2e8f0; text-align: right;">العلاج / Medication</th>
                <th style="padding: 10px; border-left: 1px solid #e2e8f0; text-align: right;">الجرعة / Dose</th>
                <th style="padding: 10px; border-left: 1px solid #e2e8f0; text-align: right;">التكرار / Frequency</th>
                <th style="padding: 10px; text-align: right;">المدة / Duration</th>
              </tr>
            </thead>
            <tbody>
              ${emailRxList
                .map(
                  (rx: any) => `
                <tr style="border-bottom: 1px solid #e2e8f0;">
                  <td style="padding: 10px; border-left: 1px solid #e2e8f0; font-weight: bold; color: #1e293b; text-align: right;">${rx.name}</td>
                  <td style="padding: 10px; border-left: 1px solid #e2e8f0; color: #475569; text-align: right;">${rx.dose || "-"}</td>
                  <td style="padding: 10px; border-left: 1px solid #e2e8f0; color: #475569; text-align: right;">${rx.frequency || "-"}</td>
                  <td style="padding: 10px; color: #475569; text-align: right;">${rx.duration || "-"}</td>
                </tr>
              `,
                )
                .join("")}
            </tbody>
          </table>
        </div>
      `;
    }

    let vitalsHtml = "";
    if (vitals) {
      vitalsHtml = `
        <div style="background-color: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 8px; padding: 15px; margin-bottom: 20px; font-size: 12px; text-align: right; direction: rtl;">
          <h4 style="color: #166534; margin-top: 0; margin-bottom: 10px; border-bottom: 1px solid #bbf7d0; padding-bottom: 6px; font-size: 13px; font-weight: bold;">🩺 القياسات والمؤشرات الحيوية / Vitals Checklist</h4>
          <table style="width: 100%; border-collapse: collapse;">
            <tr>
              ${vitals.bloodPressure ? `<td style="padding: 4px;"><strong>ضغط الدم:</strong> ${vitals.bloodPressure} mmHg</td>` : ""}
              ${vitals.temp ? `<td style="padding: 4px;"><strong>الحرارة:</strong> ${vitals.temp} °C</td>` : ""}
              ${vitals.weight ? `<td style="padding: 4px;"><strong>الوزن:</strong> ${vitals.weight} kg</td>` : ""}
            </tr>
            <tr>
              ${vitals.height ? `<td style="padding: 4px;"><strong>الطول:</strong> ${vitals.height} cm</td>` : ""}
              ${vitals.bmi ? `<td style="padding: 4px;" colspan="2"><strong>مؤشر كتلة الجسم (BMI):</strong> ${vitals.bmi} kg/m²</td>` : ""}
            </tr>
          </table>
          ${vitals.chiefComplaint ? `<div style="margin-top: 8px; border-top: 1px dashed #bbf7d0; padding-top: 6px; color: #1e293b;"><strong>الأعراض والشكوى الحالية:</strong> ${vitals.chiefComplaint}</div>` : ""}
        </div>
      `;
    }

    let notesHtml = "";
    if (docType === "RECORD") {
      notesHtml = `
        <div style="margin-bottom: 20px; font-size: 13px; line-height: 1.6; color: #334155; text-align: right; direction: rtl;">
          ${soapS ? `<p style="margin-bottom: 10px;"><strong style="color: #0f172a;">👥 الشكوى والتاريخ الطبي (Subjective):</strong><br/>${soapS.replace(/\n/g, "<br/>")}</p>` : ""}
          ${soapO ? `<p style="margin-bottom: 10px;"><strong style="color: #0f172a;">🔍 الفحص السريري (Objective):</strong><br/>${soapO.replace(/\n/g, "<br/>")}</p>` : ""}
          ${soapA ? `<p style="margin-bottom: 10px;"><strong style="color: #0f172a;">🏷️ التشخيص والتقييم (Assessment):</strong><br/>${soapA.replace(/\n/g, "<br/>")}</p>` : ""}
          ${soapP ? `<p style="margin-bottom: 10px;"><strong style="color: #0f172a;">📋 الخطة الطبية (Plan):</strong><br/>${soapP.replace(/\n/g, "<br/>")}</p>` : ""}
        </div>
      `;
    } else {
      notesHtml = `
        <div style="background-color: #fafafa; border: 1px solid #e5e5e5; border-radius: 8px; padding: 15px; font-size: 13px; line-height: 1.6; color: #262626; text-align: right; direction: rtl; white-space: pre-line;">
          ${notesText || "لا توجد ملاحظات سريرية مدونة حالياً لهذا المستند."}
        </div>
      `;
    }

    return `
      <div dir="rtl" style="font-family: 'Cairo', 'Inter', system-ui, sans-serif; max-width: 600px; margin: 0 auto; padding: 25px; border: 1px solid #e2e8f0; border-radius: 12px; background-color: #ffffff; text-align: right; box-shadow: 0 4px 12px rgba(0,0,0,0.05);">
        <!-- Clinic Emblem Header -->
        <table style="width: 100%; border-bottom: 3px double #0f172a; padding-bottom: 15px; margin-bottom: 20px; direction: rtl;">
          <tr>
            <td style="text-align: right; vertical-align: middle;">
              <h1 style="font-size: 20px; color: #0f172a; margin: 0; font-weight: bold; font-family: 'Cairo', sans-serif;">الدكتور واثق الجلبي في بغداد</h1>
              <p style="font-size: 11px; color: #64748b; margin: 4px 0 0 0;">Dr. Watheq Al-Chalabi Clinical Node • النظام الرقمي لإدارة السجلات والوصفات الطبية</p>
            </td>
            <td style="text-align: left; vertical-align: middle; font-family: 'Inter', sans-serif; direction: ltr;">
              <div style="font-weight: bold; color: #0f172a; font-size: 18px; letter-spacing: -0.5px;">Dr. Watheq Al-Chalabi Clinician</div>
              <div style="font-size: 10px; color: #0d9488;">Integrative Secure EHR</div>
            </td>
          </tr>
        </table>

        <!-- Document Type Banner -->
        <div style="background-color: #0f172a; color: #ffffff; border-radius: 8px; padding: 12px; text-align: center; margin-bottom: 20px;">
          <h2 style="font-size: 16px; margin: 0; font-weight: bold; font-family: 'Cairo', sans-serif;">${docTitleAr}</h2>
          <h3 style="font-size: 11px; font-weight: normal; margin: 3px 0 0 0; color: #94a3b8; font-family: 'Inter', sans-serif;">${docTitleEn}</h3>
        </div>

        <!-- Demographics Record Card -->
        <div style="background-color: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 15px; margin-bottom: 20px; font-size: 12px; direction: rtl; text-align: right;">
          <table style="width: 100%; border-collapse: collapse;">
            <tr>
              <td style="padding: 5px; width: 50%;"><strong>👤 اسم المريض:</strong> ${displayPatient.name}</td>
              <td style="padding: 5px; width: 50%;"><strong>🪪 رقم الملف السريري:</strong> #${displayPatient.id}</td>
            </tr>
            <tr>
              <td style="padding: 5px;"><strong>🎂 العمر:</strong> ${ageVal} سنة</td>
              <td style="padding: 5px;"><strong>📅 تاريخ التحرير:</strong> ${printDate.split(",")[0]}</td>
            </tr>
            <tr>
              <td style="padding: 5px;"><strong>⚧️ الجنس:</strong> ${displayPatient.gender === "MALE" ? "ذكر" : displayPatient.gender === "FEMALE" ? "أنثى" : displayPatient.gender || "غير محدد"}</td>
              <td style="padding: 5px;"><strong>📌 حالة المستند:</strong> موثق بالكامل</td>
            </tr>
          </table>
        </div>

        <!-- Patient Vitals Section -->
        ${vitalsHtml}

        <!-- Medical Description Content -->
        <div style="margin-bottom: 25px;">
          <h4 style="color: #0f172a; border-bottom: 2px solid #cbd5e1; padding-bottom: 6px; margin-bottom: 12px; font-size: 14px; font-weight: bold; font-family: 'Cairo', sans-serif;">📋 التقرير والتشخيص السريري / Clinical Analysis</h4>
          ${notesHtml}
        </div>

        <!-- Electronic Prescription List Grid -->
        ${rxHtml}

        <!-- Regulatory stamps and doctor attestation -->
        <table style="width: 100%; border-top: 1px solid #cbd5e1; margin-top: 30px; padding-top: 15px; font-size: 11px; color: #64748b; direction: rtl; text-align: right;">
          <tr>
            <td style="vertical-align: top; padding-left: 15px;">
              <p style="margin: 0; font-weight: bold; color: #334155; font-size: 12px;">ملاحظة أمان وتأمين سري سريري:</p>
              <p style="margin: 4px 0 0 0; line-height: 1.5;">
                تعتبر هذه الرسالة مستنداً سرياً موجهاً للمريض فقط. إن استخدام هذا البريد يخضع لبروتوكول السرية الطبية المعتمدة ويحظر نقله أو مشاركته مع جهات غير مصرحة.
              </p>
            </td>
            <td style="vertical-align: top; width: 150px; border-right: 1px solid #cbd5e1; padding-right: 15px; text-align: center;">
              <p style="margin: 0; font-weight: bold; color: #334155;">التحقق والختم الإلكتروني</p>
              <div style="font-family: 'Amiri', serif; font-size: 13px; color: #0284c7; margin-top: 6px; font-weight: bold;">
                د. عيادة IQ-Doctor
              </div>
              <div style="font-size: 8px; color: #94a3b8; font-family: 'Inter', sans-serif; margin-top: 2px; text-transform: uppercase;">
                Verified Clinical Node
              </div>
            </td>
          </tr>
        </table>

        <!-- Node info and address footer -->
        <div style="margin-top: 35px; border-top: 2px solid #0f172a; padding-top: 15px; text-align: center; font-size: 10px; color: #64748b; direction: rtl;">
          <p style="margin: 0; font-weight: bold;">مركز الطب التكاملي والسجلات الرقمية - Dr. Watheq Al-Chalabi Clinic</p>
          <p style="margin: 3px 0 0 0;">العراق - بغداد - السيدية | تكنولوجيا الرعاية الصحية المتقدمة</p>
          <p style="margin: 6px 0 0 0; font-size: 8px; color: #94a3b8;">يرجى عدم الرد المباشر على هذه الرسالة لأنها مرسلة تلقائياً عبر نظام السحاب السريري المؤتمت.</p>
        </div>
      </div>
    `;
  };

  const handleSendGmailEmail = async () => {
    if (!gmailRecipient || !gmailRecipient.includes("@")) {
      setGmailErrorMessage(
        "الرجاء إدخال عنوان بريد إلكتروني صحيح وصالح للمريض.",
      );
      return;
    }

    setIsGmailSending(true);
    setGmailErrorMessage(null);
    setGmailSuccessMessage(null);

    try {
      if (!googleAccessToken) {
        throw new Error(
          "لم يتم العثور على مصادقة Google نشطة بالعيادة. الرجاء الاتصال أولاً.",
        );
      }

      // Generate the PDF attachment dynamically using html2canvas and jsPDF to capture the precise document on screen
      const element = document.getElementById("patient-ehr-print-area");
      if (!element) {
        throw new Error("لم يتم العثور على منطقة الطباعة لتوليد المرفق الطبي.");
      }

      const canvas = await html2canvas(element, {
        scale: 2, // 2x density for a balance of high fidelity and safe storage size
        useCORS: true,
        logging: false,
        backgroundColor: "#FFFFFF",
        onclone: (clonedDoc) => {
          try {
            // Reconstruct a sterile CSS stylesheet free of oklch. html2canvas parses document.styleSheets
            // directly but crashes on modern oklch color codes. Re-bundling stylesheet inputs solves this.
            let sanitizedCss = "";
            const styleSheets = Array.from(clonedDoc.styleSheets);

            for (const sheet of styleSheets) {
              try {
                const rules = sheet.cssRules || sheet.rules;
                if (rules) {
                  for (let i = 0; i < rules.length; i++) {
                    const ruleText = rules[i].cssText;
                    if (ruleText) {
                      sanitizedCss += ruleText + "\n";
                    }
                  }
                }
              } catch (sheetErr) {
                // If permission denied or other error, fallback to ownerNode content if stylesheet is a style tag
                if (sheet.ownerNode && sheet.ownerNode.textContent) {
                  sanitizedCss += sheet.ownerNode.textContent + "\n";
                }
              }
            }

            // Clean oklch and oklab and color-mix color references with safe standard Web-RGB equivalents
            const cleanedCss = cleanStringOfModernColors(sanitizedCss);

            // Remove all existing styled components in cloned DOM to avoid redundant parsing or conflicts
            const oldStyles = clonedDoc.querySelectorAll(
              "style, link[rel='stylesheet']",
            );
            oldStyles.forEach((node) => node.parentNode?.removeChild(node));

            // Inject the clean compiled stylesheet into the cloned head
            const safeStyleElement = clonedDoc.createElement("style");
            safeStyleElement.textContent = cleanedCss;
            clonedDoc.head.appendChild(safeStyleElement);

            // Inject Cairo and Amiri fonts into clone explicitly for flawless Arabic rendering and support
            const fontLink = clonedDoc.createElement("link");
            fontLink.rel = "stylesheet";
            fontLink.href =
              "https://fonts.googleapis.com/css2?family=Amiri:wght@400;700&family=Cairo:wght@400;600;700&display=swap";
            clonedDoc.head.appendChild(fontLink);

            // Inject global Arabic normalization styles directly into cloned head to bypass html2canvas letter-splitting bugs
            const arabicNormalizationStyle = clonedDoc.createElement("style");
            arabicNormalizationStyle.textContent = `
              /* Normalise letter-spacing & force custom ligatures on all RTL contexts to connect Arabic letters perfectly */
              .font-arabic, .font-signature, [dir="rtl"], [dir="auto"], th, td, p, span, div, h1, h2, h3, h4, h5, h6 {
                letter-spacing: normal !important;
                word-spacing: normal !important;
                font-feature-settings: "liga" on, "clig" on, "calt" on !important;
                -webkit-font-feature-settings: "liga" on, "clig" on, "calt" on !important;
              }
              .font-signature {
                font-family: 'Amiri', serif !important;
              }
              .font-arabic, [dir="rtl"], [dir="auto"] {
                font-family: 'Cairo', sans-serif !important;
              }
            `;
            clonedDoc.head.appendChild(arabicNormalizationStyle);

            const compactPrintStyle = clonedDoc.createElement("style");
            compactPrintStyle.textContent = `
              #patient-ehr-print-area { line-height: 1.3 !important; display: block !important; height: auto !important; }
              .\\print\\:p-8, .\\print\\:p-6, .\\print\\:p-4 { padding: 6px !important; }
              .\\print\\:p-2, .\\print\\:p-3 { padding: 4px !important; }
              .\\print\\:p-1 { padding: 2px !important; }
              .\\print\\:mb-8, .\\print\\:mb-6, .\\print\\:mb-4 { margin-bottom: 6px !important; }
              .\\print\\:mb-2, .\\print\\:mb-3 { margin-bottom: 4px !important; }
              .\\print\\:mb-1 { margin-bottom: 2px !important; }
              .\\print\\:mt-8, .\\print\\:mt-6, .\\print\\:mt-4 { margin-top: 6px !important; }
              .\\print\\:mt-2, .\\print\\:mt-3 { margin-top: 4px !important; }
              .\\print\\:mt-1 { margin-top: 2px !important; }
              .\\print\\:gap-8, .\\print\\:gap-6 { gap: 8px !important; }
              .\\print\\:gap-4 { gap: 6px !important; }
              .\\print\\:gap-2 { gap: 4px !important; }
              .\\print\\:text-lg, .\\print\\:text-xl { font-size: 14px !important; }
              .\\print\\:text-base { font-size: 12px !important; }
              .\\print\\:text-sm { font-size: 10px !important; }
              .\\print\\:text-xs { font-size: 9px !important; }
              .\\print\\:text-\\[11px\\], .\\print\\:text-\\[12px\\] { font-size: 9px !important; }
              .\\print\\:text-\\[10px\\] { font-size: 8px !important; }
              .\\print\\:text-\\[9px\\] { font-size: 8px !important; }
              .\\print\\:text-\\[8px\\] { font-size: 7px !important; }
              .border, .border-b, .border-t, .border-l, .border-r, .print\\:break-inside-avoid { break-inside: avoid !important; page-break-inside: avoid !important; }
            `;
            clonedDoc.head.appendChild(compactPrintStyle);

            // Force visibility and font-family for all elements in Arabic contexts with robust connected text fallbacks
            const allNodesInClone = clonedDoc.querySelectorAll("*");
            allNodesInClone.forEach((node) => {
              const htmlEl = node as HTMLElement;

              const isRtl =
                htmlEl.getAttribute("dir") === "rtl" ||
                htmlEl.getAttribute("dir") === "auto" ||
                htmlEl.classList.contains("font-arabic") ||
                htmlEl.classList.contains("font-signature") ||
                htmlEl.style.direction === "rtl";

              // Fix html2canvas letter splitting by strictly resetting letter-spacing and forcing ligatures
              if (isRtl) {
                htmlEl.style.letterSpacing = "0px";
                htmlEl.style.wordSpacing = "normal";
              }

              // Force remove background highlights for clean PDF prints
              htmlEl.style.backgroundColor = "transparent";

              // Apply explicit connected font-families with local defaults to bypass cross-origin browser sandbox blocks
              if (htmlEl.classList.contains("font-signature")) {
                htmlEl.style.fontFamily =
                  "'Amiri', 'Times New Roman', 'Traditional Arabic', 'Sakkal Majalla', Georgia, serif";
                htmlEl.style.letterSpacing = "0px";
              } else if (
                htmlEl.classList.contains("font-arabic") ||
                htmlEl.closest(".font-arabic") ||
                htmlEl.getAttribute("dir") === "rtl" ||
                htmlEl.getAttribute("dir") === "auto"
              ) {
                htmlEl.style.fontFamily =
                  "'Cairo', 'Segoe UI', Tahoma, 'Geeza Pro', 'Noto Sans Arabic', Arial, sans-serif";
                htmlEl.style.letterSpacing = "0px";
              }

              // Fix html2canvas textarea alignment and wrapping bugs
              if (
                htmlEl.tagName.toLowerCase() === "textarea" ||
                htmlEl.tagName.toLowerCase() === "select" ||
                (htmlEl.tagName.toLowerCase() === "input" &&
                  (htmlEl as HTMLInputElement).type === "text")
              ) {
                const el = htmlEl as
                  | HTMLTextAreaElement
                  | HTMLInputElement
                  | HTMLSelectElement;
                const div = clonedDoc.createElement("div");

                let visibleText = el.value || "";
                if (htmlEl.tagName.toLowerCase() === "select") {
                  const selectEl = el as HTMLSelectElement;
                  if (
                    selectEl.selectedIndex >= 0 &&
                    selectEl.options[selectEl.selectedIndex]
                  ) {
                    visibleText = selectEl.options[selectEl.selectedIndex].text;
                  }
                }

                div.innerHTML = visibleText.replace(/\n/g, "<br/>");
                div.className = el.className;

                div.style.whiteSpace = "pre-wrap";
                div.style.wordWrap = "break-word";
                div.style.textAlign =
                  el.style.textAlign || (isRtl ? "right" : "left");
                div.style.direction = isRtl ? "rtl" : "ltr";
                div.style.cssText += el.style.cssText;

                el.parentNode?.replaceChild(div, el);
              }
            });

            // Force visibility for signature images to ensure they are captured by html2canvas
            const signatureImages = clonedDoc.querySelectorAll(
              'img[alt*="Signature"]',
            );
            signatureImages.forEach((img) => {
              (img as HTMLElement).style.visibility = "visible";
              (img as HTMLElement).style.display = "block";
              (img as HTMLElement).style.opacity = "1";
            });

            // Clean inline style attribute oklch, oklab and color-mix definitions
            const allElements = clonedDoc.getElementsByTagName("*");
            for (let i = 0; i < allElements.length; i++) {
              const el = allElements[i] as HTMLElement;
              if (el && typeof el.getAttribute === "function") {
                const inlineStyle = el.getAttribute("style");
                if (
                  inlineStyle &&
                  (inlineStyle.toLowerCase().includes("oklch") ||
                    inlineStyle.toLowerCase().includes("oklab") ||
                    inlineStyle.toLowerCase().includes("color-mix"))
                ) {
                  el.setAttribute(
                    "style",
                    cleanStringOfModernColors(inlineStyle),
                  );
                }
              }
            }
          } catch (cleanError) {
            console.error(
              "Clinical style sheet sanitization routine failed in Gmail generator:",
              cleanError,
            );
          }
        },
      });

      const imgData = canvas.toDataURL("image/jpeg", 0.80);

      // Setup portrait standard A4 page dimensions
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      const imgWidth = 210; // A4 width in mm
      const pageHeight = 297; // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, "JPEG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      // Handle multi-page documents (e.g. comprehensive logs, multiple visits, leaflets)
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "JPEG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      // Generate descriptive file and document titles
      const docSlug = docType
        ? docType.toLowerCase().replace(/_/g, "-")
        : "document";
      const cleanedPatientName = displayPatient.id
        ? `Patient_${displayPatient.id}`
        : "Patient";
      const filename = `${cleanedPatientName}_${docSlug}_${new Date().toISOString().split("T")[0]}.pdf`;

      // Export file as direct base64 encoded payload to attach to the raw MIME email
      const pdfBaseUri = pdf.output("datauristring");
      const pdfBase64 = pdfBaseUri.substring(pdfBaseUri.indexOf(",") + 1);

      // Set greeting body as clean welcoming HTML letter
      const bodyHtml = generateWelcomeLetterHtml();

      // Dispatch full multipart email containing PDF attachment and welcome letter
      const sendResult = await sendGmailEmailWithAttachment(
        googleAccessToken,
        gmailRecipient,
        gmailSubject,
        bodyHtml,
        {
          filename,
          contentType: "application/pdf",
          base64Data: pdfBase64,
        },
      );

      if (!sendResult.success) {
        throw new Error(
          sendResult.error || "شاب فشل عملية إرسال البريد عبر خوادم Google.",
        );
      }

      // If opting to save the email back to the patient's record DB
      if (saveEmailToPatientFile) {
        let safeToken = apiToken || localStorage.getItem("clinic_token") || localStorage.getItem("token") || localStorage.getItem("iq_doctor_jwt") || "";
        if (safeToken.startsWith('"') && safeToken.endsWith('"')) safeToken = safeToken.slice(1, -1);
        if (safeToken) {
          await fetch(`/api/patients/${displayPatient.id}`, {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${safeToken}`,
            },
            body: JSON.stringify({ email: gmailRecipient }),
          }).catch((err) =>
            console.error("Could not update patient email in record:", err),
          );

          // Apply update in active props context directly so they persist in active session
          patient.email = gmailRecipient;
        }
      }

      setGmailSuccessMessage(
        "تم إرسال المستند الطبي بنجاح للمريض مباشرة عبر بريدك الإلكتروني Google Gmail! 🚀",
      );
      setTimeout(() => {
        setIsGmailSending(false);
        setGmailSuccessMessage(null);
        setShowGmailModal(false);
      }, 3000);
    } catch (err: any) {
      console.error("[Gmail Send Dispatch Error]:", err);
      const errMsg = err.message || "";
      if (
        errMsg.toLowerCase().includes("credentials") ||
        errMsg.toLowerCase().includes("credential") ||
        errMsg.toLowerCase().includes("auth") ||
        errMsg.toLowerCase().includes("token") ||
        errMsg.toLowerCase().includes("unauthorized") ||
        errMsg.toLowerCase().includes("invalid_grant") ||
        errMsg.toLowerCase().includes("expired")
      ) {
        // Clear expired Google states to let user reconnect easily
        setGoogleAccessToken(null);
        setGoogleUser(null);
        setIsGoogleWorkspaceAuthenticated(false);
        setGmailErrorMessage(
          "⚠️ انتهت صلاحية جلسة العمل أو رمز المصادقة الخاص بـ Google بالعيادة. " +
            "يرجى الضغط على زر「ربط الحساب / Connect Google」بالأعلى لإعادة الاتصال وتجديد صلاحية الرمز، ثم أعد المحاولة بالإرسال.",
        );
      } else {
        setGmailErrorMessage(
          errMsg || "حدث خطأ غير متوقع أثناء إرسال البريد الإلكتروني.",
        );
      }
      setIsGmailSending(false);
    }
  };

  /* Clinical Security & Auto-Print workflow has been disabled to allow preview and digital signature. */
  useEffect(() => {
    // We no longer auto-trigger window.print() or auto-close when printing is complete,
    // allowing the user to review the document and sign before manually printing.
  }, []);

  // Consolidate cosmetic laser treatments from historical reports and the current active live state
  const allLaserTreatments: Array<LaserTherapyEntry & { date: string }> = [];

  // Add active unsaved aesthetic treatments
  if (activeSession && activeSession.faceMarks && activeSession.faceMarks.length > 0) {
    activeSession.faceMarks.forEach((m) => {
      allLaserTreatments.push({
        date: "Current Session (Draft)",
        treatmentArea: m.treatmentArea,
        laserType: m.laserType,
        spotSize: m.spotSize,
        fluence: m.fluence,
        pulseDuration: m.pulseDuration,
        cooling: m.cooling,
        passes: m.passes,
      });
    });
  }

  // Parse historical clinical records for laser logs
  if (clinicalRecords && Array.isArray(clinicalRecords)) {
    clinicalRecords.forEach((record) => {
      if (record.faceMapJson) {
        try {
          const marks: LaserTherapyEntry[] = JSON.parse(record.faceMapJson);
          const dateStr = record.createdAt
            ? new Date(record.createdAt).toLocaleDateString("en-GB")
            : "Historical";
          if (marks && Array.isArray(marks)) {
            marks.forEach((m) => {
              allLaserTreatments.push({
                date: dateStr,
                treatmentArea: m.treatmentArea,
                laserType: m.laserType,
                spotSize: m.spotSize,
                fluence: m.fluence,
                pulseDuration: m.pulseDuration,
                cooling: m.cooling,
                passes: m.passes,
              });
            });
          }
        } catch (e) {
          console.error("Failed to parse historical laser logs", e);
        }
      }
    });
  }

  const [isExplicitSaving, setIsExplicitSaving] = useState(false);
  const [isExplicitSaved, setIsExplicitSaved] = useState(false);

  const handleExplicitSave = async () => {
    setIsExplicitSaving(true);
    try {
      const docKey = recordKey;
      const savedDateStr = savedDate || new Date().toLocaleString("en-GB", {
        timeZone: "Asia/Baghdad",
      });
      const printableDocsPayload = {
        reportTitle: localStorage.getItem(`report_title_${docKey}`),
        reportSub: localStorage.getItem(`report_sub_${docKey}`),
        reportInstructionsHeader: localStorage.getItem(
          `report_instr_hdr_${docKey}`,
        ),
        reportInstructions: localStorage.getItem(`report_instr_${docKey}`),
        billPrice: localStorage.getItem(`bill_price_${docKey}`),
        billPriceWords: localStorage.getItem(`bill_price_words_${docKey}`),
        billCategory: localStorage.getItem(`bill_category_${docKey}`),
        receiptName: localStorage.getItem(`receipt_name_${docKey}`),
        paymentMethod: localStorage.getItem(`payment_method_${docKey}`),
        paymentStatus: localStorage.getItem(`payment_status_${docKey}`),
        referralFindingsHeader: localStorage.getItem(
          `referral_findings_hdr_${docKey}`,
        ),
        referralFindings: localStorage.getItem(`referral_findings_${docKey}`),
        referralAdditionalNotes: localStorage.getItem(
          `referral_add_notes_${docKey}`,
        ),
        recordClinicalNotes: localStorage.getItem(
          `rec_clinical_notes_${docKey}`,
        ),
        patientSignature,
        physicianSignature,
        savedDate: savedDateStr,
      };
      if (patientSignature) localStorage.setItem(`patient_signature_${docKey}`, patientSignature);

      if (activeSession?.editingRecordId) {
        let safeToken = apiToken || localStorage.getItem("clinic_token") || localStorage.getItem("token") || localStorage.getItem("iq_doctor_jwt") || "";
        if (safeToken.startsWith('"') && safeToken.endsWith('"')) safeToken = safeToken.slice(1, -1);
        const res = await fetch(`/api/clinical/records/${activeSession.editingRecordId}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${safeToken}`,
          },
          body: JSON.stringify({
            printableDocs: JSON.stringify(printableDocsPayload),
          }),
        });
        if (!res.ok) {
          throw new Error(`Server status ${res.status}`);
        }
      }
      setSavedDate(savedDateStr);

      localStorage.setItem(`doc_saved_date_${docKey}`, savedDateStr);
      setSavedDate(savedDateStr);
      localStorage.setItem(`doc_completed_${docType}_${docKey}`, "true");
      setIsExplicitSaved(true);
      setTimeout(() => setIsExplicitSaved(false), 3000);
    } catch (err: any) {
      console.error(err);
      alert("⚠️ عذراً، فشل حفظ المستند في قاعدة البيانات. يرجى التحقق من الاتصال بالشبكة وإعادة المحاولة.\n\nتفاصيل الخطأ: " + (err.message || err));
    }
    setIsExplicitSaving(false);
  };
  const isDocAlreadyCompleted =
    localStorage.getItem(`doc_completed_${docType}_${recordKey}`) === "true";

  const handleSaveToAttachments = async () => {
    try {
      setIsSavingToAttachments(true);
      const element = document.getElementById("patient-ehr-print-area");
      if (!element) {
        alert("لم يتم العثور على منطقة الطباعة.");
        return;
      }

      // Apply temporary B&W and high contrast filter for solid black text without increasing scale
      const originalFilter = element.style.filter;
      element.style.filter = "grayscale(100%) contrast(300%)";

      // Keep scale optimized for small size, rely on contrast for clarity
      const canvas = await html2canvas(element, {
        scale: 2, // 3x density for high fidelity 
        useCORS: true,
        logging: false,
        backgroundColor: "#FFFFFF",
        onclone: (clonedDoc) => {
          try {
            // Reconstruct a sterile CSS stylesheet free of oklch. html2canvas parses document.styleSheets
            // directly but crashes on modern oklch color codes. Re-bundling stylesheet inputs solves this.
            let sanitizedCss = "";
            const styleSheets = Array.from(clonedDoc.styleSheets);

            for (const sheet of styleSheets) {
              try {
                const rules = sheet.cssRules || sheet.rules;
                if (rules) {
                  for (let i = 0; i < rules.length; i++) {
                    const ruleText = rules[i].cssText;
                    if (ruleText) {
                      sanitizedCss += ruleText + "\n";
                    }
                  }
                }
              } catch (sheetErr) {
                // If permission denied or other error, fallback to ownerNode content if stylesheet is a style tag
                if (sheet.ownerNode && sheet.ownerNode.textContent) {
                  sanitizedCss += sheet.ownerNode.textContent + "\n";
                }
              }
            }

            // Clean oklch and oklab and color-mix color references with safe standard Web-RGB equivalents
            const cleanedCss = cleanStringOfModernColors(sanitizedCss);

            // Remove all existing styled components in cloned DOM to avoid redundant parsing or conflicts
            const oldStyles = clonedDoc.querySelectorAll(
              "style, link[rel='stylesheet']",
            );
            oldStyles.forEach((node) => node.parentNode?.removeChild(node));

            // Inject the clean compiled stylesheet into the cloned head
            const safeStyleElement = clonedDoc.createElement("style");
            safeStyleElement.textContent = cleanedCss;
            clonedDoc.head.appendChild(safeStyleElement);

            // Inject Cairo and Amiri fonts into clone explicitly for flawless Arabic rendering and support
            const fontLink = clonedDoc.createElement("link");
            fontLink.rel = "stylesheet";
            fontLink.href =
              "https://fonts.googleapis.com/css2?family=Amiri:wght@400;700&family=Cairo:wght@400;600;700&display=swap";
            clonedDoc.head.appendChild(fontLink);

            // Inject global Arabic normalization styles directly into cloned head to bypass html2canvas letter-splitting bugs
            const arabicNormalizationStyle = clonedDoc.createElement("style");
            arabicNormalizationStyle.textContent = `
              /* Normalise letter-spacing & force custom ligatures on all RTL contexts to connect Arabic letters perfectly */
              .font-arabic, .font-signature, [dir="rtl"], [dir="auto"], th, td, p, span, div, h1, h2, h3, h4, h5, h6 {
                letter-spacing: normal !important;
                word-spacing: normal !important;
                font-feature-settings: "liga" on, "clig" on, "calt" on !important;
                -webkit-font-feature-settings: "liga" on, "clig" on, "calt" on !important;
              }
              /* Remove grey shadings for clean printing in attachments */
              .bg-slate-50, .bg-slate-100, .bg-slate-200, .bg-gray-50, .bg-gray-100, .bg-gray-200, .bg-indigo-50 {
                background-color: transparent !important;
              }
            `;
            clonedDoc.head.appendChild(arabicNormalizationStyle);

            const allElements = clonedDoc.getElementsByTagName("*");
            for (let i = 0; i < allElements.length; i++) {
              const el = allElements[i] as HTMLElement;
              if (el && typeof el.getAttribute === "function") {
                const inlineStyle = el.getAttribute("style");
                if (
                  inlineStyle &&
                  (inlineStyle.toLowerCase().includes("oklch") ||
                    inlineStyle.toLowerCase().includes("oklab") ||
                    inlineStyle.toLowerCase().includes("color-mix"))
                ) {
                  el.setAttribute(
                    "style",
                    cleanStringOfModernColors(inlineStyle),
                  );
                }
              }
            }
          } catch (cleanError) {
            console.error("Style sanitization failed:", cleanError);
          }
        }
      });

      // Restore filter
      element.style.filter = originalFilter;

      // Minimal compression for maximum legibility of medical details
      const imgData = canvas.toDataURL("image/jpeg", 0.80);

      const imgWidth = 210;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      const pageHeight = Math.max(imgHeight, 297);

      const pdf = new jsPDF({ 
        orientation: "portrait", 
        unit: "mm", 
        format: [imgWidth, pageHeight], 
        compress: true 
      });

      pdf.addImage(imgData, "JPEG", 0, 0, imgWidth, imgHeight, undefined, 'FAST');

      const pdfBaseUri = pdf.output("datauristring");
      const docSlug = docType ? docType.toLowerCase().replace(/_/g, "-") : "document";
      const filename = `Print_${docSlug}_${new Date().getTime()}.pdf`;

      let safeToken = apiToken || localStorage.getItem("clinic_token") || localStorage.getItem("token") || localStorage.getItem("iq_doctor_jwt") || "";
      if (safeToken.startsWith('"') && safeToken.endsWith('"')) safeToken = safeToken.slice(1, -1);

      if (safeToken) {
        const uploadRes = await fetch("/api/documents", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${safeToken}`,
          },
          body: JSON.stringify({
            patientId: displayPatient.id,
            title: filename,
            fileType: "application/pdf",
            fileData: pdfBaseUri,
            recordId: activeSession?.editingRecordId || recordKey || null,
            isPrintout: true,
          }),
        });

        if (!uploadRes.ok) {
          const errData = await uploadRes.json().catch(() => ({}));
          throw new Error(errData.error || `Server responded with status ${uploadRes.status}`);
        }
      } else {
        throw new Error("لم يتم العثور على رمز تسجيل الدخول (clinic_token). يرجى تسجيل الدخول مجدداً.");
      }

      // Saved to cloud only. No local download.
      alert("تم حفظ المطبوع في مرفقات المريض سحابياً بنجاح وبحجم صغير.");
      
      // Dispatch event to force UI to refresh attachments immediately
      window.dispatchEvent(new Event("refreshPatientDocuments"));
    } catch (err: any) {
      console.error(err);
      alert("حدث خطأ أثناء حفظ المرفق: " + (err.message || err));
    } finally {
      setIsSavingToAttachments(false);
    }
  };

  const handleTriggerPrint = () => {
    // EXPERT COMMENTARY: Automatically register printed consultation bill in the finance ledger system
    if (docType === "BILL_10000") {
      if (
        !billPrice ||
        parseInt(billPrice.replace(/,/g, "")) <= 0 ||
        !billCategory
      ) {
        alert(
          "عذراً دكتور، يرجى تحديد تكلفة الإجراء واسم الخدمة قبل الحفظ أو الطباعة.",
        );
        return;
      }
      try {
        const syncKey = `receipt_synced_${displayPatient.id}`;
        const lastSync = localStorage.getItem(syncKey);
        const now = Date.now();
        
        // Prevent duplicate invoicing if printed within the last 15 minutes
        if (!lastSync || now - parseInt(lastSync) > 15 * 60 * 1000) {
          const stored = localStorage.getItem("iq_doctor_standalone_ledger");
          const financials = stored ? JSON.parse(stored) : [];

          // Clean up price, converting common Iraqi Dinar formats to integer (e.g. "10,000" -> 10000)
          const cleanPrice = billPrice.replace(/,/g, "").trim();
          const numPrice = parseInt(cleanPrice, 10) || 10000;

        const currentUserName = cachedUser?.name || "Dr. Watheq AlChalabi";

        // Avoid adding duplicate entries if identical ID was somehow generated, or generate a unique random transaction ID
        const txId = `IQD-TX${Math.floor(1000 + Math.random() * 9000)}`;

        const newRecord = {
          id: txId,
          type: "RECEIPT" as const,
          patientId: displayPatient.id,
          patientName: displayPatient.name,
          patientPhone: displayPatient.phone || undefined,
          amount: numPrice,
          category: billCategory,
          notes: `${billCategory} - مستلمة وتنزيل الوصل آلياً من الطبيب للمريض (${numPrice.toLocaleString("en-GB")} د.ع)`,
          date: new Date().toISOString(),
          issuedBy: currentUserName,
        };

        const updated = [newRecord, ...financials];
        localStorage.setItem(
          "iq_doctor_standalone_ledger",
          JSON.stringify(updated),
        );

        // Hit the backend API to ensure the Super Admin can see it globally
        const apiToken = localStorage.getItem("iq_doctor_jwt");
        if (apiToken) {
          fetch("/api/finance/income", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${apiToken}`,
            },
            body: JSON.stringify({
              amount: numPrice,
              category: billCategory,
              description: newRecord.notes,
              patientId: displayPatient.id,
            }),
          })
            .then((res) => {
              if (res.ok) {
                localStorage.setItem(
                  `receipt_synced_${displayPatient.id}`,
                  Date.now().toString(),
                );
              }
            })
            .catch((err) =>
              console.error("Failed to sync receipt to backend", err),
            );
        }

        // Propagate updates via storage and custom handlers to real-time dashboards
        window.dispatchEvent(new Event("storage"));
        window.dispatchEvent(new CustomEvent("clinical_ledger_updated"));
        }
      } catch (err) {
        console.error(
          "[Clinical Accounting Link] Failed auto-registering receipt",
          err,
        );
      }
    }

    window.print();
  };

  /**
   * CLINICAL EXPORT SECURITY - SHARE VIA WHATSAPP (WITH REAL PDF ATTACHMENT EXPORT)
   *
   * Captured using html2canvas & jsPDF: Renders the crisp physical viewport of
   * the designated clinical paper document, downloads it locally, and redirects
   * to WhatsApp with an integrated professional clinical summary.
   */
  const handleShareTelegram = async () => handleShareDocument("telegram");
  const handleShareWhatsApp = async () => handleShareDocument("whatsapp");

  const handleShareDocument = async (platform: "telegram" | "whatsapp") => {
    try {
      setIsGeneratingPdf(true);
      const element = document.getElementById("patient-ehr-print-area");
      if (!element) {
        alert("فشلت المحاولة: لم يتم العثور على منطقة الطباعة.");
        setIsGeneratingPdf(false);
        return;
      }

      // Apply temporary high contrast filter for solid black text
      const originalFilter = element.style.filter;
      element.style.filter = "grayscale(100%) contrast(300%)";

      // High-resolution snapshot generation for accurate medical diagnosis
      const canvas = await html2canvas(element, {
        scale: 2, // 4x density for ultra-crisp retina quality text details
        useCORS: true,
        logging: false,
        backgroundColor: "#FFFFFF",
        onclone: (clonedDoc) => {
          try {
            // Reconstruct a sterile CSS stylesheet free of oklch. html2canvas parses document.styleSheets
            // directly but crashes on modern oklch color codes. Re-bundling stylesheet inputs solves this.
            let sanitizedCss = "";
            const styleSheets = Array.from(clonedDoc.styleSheets);

            for (const sheet of styleSheets) {
              try {
                const rules = sheet.cssRules || sheet.rules;
                if (rules) {
                  for (let i = 0; i < rules.length; i++) {
                    const ruleText = rules[i].cssText;
                    if (ruleText) {
                      sanitizedCss += ruleText + "\n";
                    }
                  }
                }
              } catch (sheetErr) {
                // If permission denied or other error, fallback to ownerNode content if stylesheet is a style tag
                if (sheet.ownerNode && sheet.ownerNode.textContent) {
                  sanitizedCss += sheet.ownerNode.textContent + "\n";
                }
              }
            }

            // Clean oklch and oklab and color-mix color references with safe standard Web-RGB equivalents
            // A typical slate grey / slate-500 equivalent replaces unknown functions safely avoiding the parser crash
            const cleanedCss = cleanStringOfModernColors(sanitizedCss);

            // Remove all existing styled components in cloned DOM to avoid redundant parsing or conflicts
            const oldStyles = clonedDoc.querySelectorAll(
              "style, link[rel='stylesheet']",
            );
            oldStyles.forEach((node) => node.parentNode?.removeChild(node));

            // Inject the clean compiled stylesheet into the cloned head
            const safeStyleElement = clonedDoc.createElement("style");
            safeStyleElement.textContent = cleanedCss;
            clonedDoc.head.appendChild(safeStyleElement);

            // Inject Cairo and Amiri fonts into clone explicitly for flawless Arabic rendering and support
            const fontLink = clonedDoc.createElement("link");
            fontLink.rel = "stylesheet";
            fontLink.href =
              "https://fonts.googleapis.com/css2?family=Amiri:wght@400;700&family=Cairo:wght@400;600;700&display=swap";
            clonedDoc.head.appendChild(fontLink);

            // Inject global Arabic normalization styles directly into cloned head to bypass html2canvas letter-splitting bugs
            const arabicNormalizationStyle = clonedDoc.createElement("style");
            arabicNormalizationStyle.textContent = `
              /* Normalise letter-spacing & force custom ligatures on all RTL contexts to connect Arabic letters perfectly */
              .font-arabic, .font-signature, [dir="rtl"], [dir="auto"], th, td, p, span, div, h1, h2, h3, h4, h5, h6 {
                letter-spacing: normal !important;
                word-spacing: normal !important;
                font-feature-settings: "liga" on, "clig" on, "calt" on !important;
                -webkit-font-feature-settings: "liga" on, "clig" on, "calt" on !important;
              }
              .font-signature {
                font-family: 'Amiri', serif !important;
              }
              .font-arabic, [dir="rtl"], [dir="auto"] {
                font-family: 'Cairo', sans-serif !important;
              }
            `;
            clonedDoc.head.appendChild(arabicNormalizationStyle);

            const compactPrintStyle = clonedDoc.createElement("style");
            compactPrintStyle.textContent = `
              #patient-ehr-print-area { line-height: 1.3 !important; display: block !important; height: auto !important; }
              .\\print\\:p-8, .\\print\\:p-6, .\\print\\:p-4 { padding: 6px !important; }
              .\\print\\:p-2, .\\print\\:p-3 { padding: 4px !important; }
              .\\print\\:p-1 { padding: 2px !important; }
              .\\print\\:mb-8, .\\print\\:mb-6, .\\print\\:mb-4 { margin-bottom: 6px !important; }
              .\\print\\:mb-2, .\\print\\:mb-3 { margin-bottom: 4px !important; }
              .\\print\\:mb-1 { margin-bottom: 2px !important; }
              .\\print\\:mt-8, .\\print\\:mt-6, .\\print\\:mt-4 { margin-top: 6px !important; }
              .\\print\\:mt-2, .\\print\\:mt-3 { margin-top: 4px !important; }
              .\\print\\:mt-1 { margin-top: 2px !important; }
              .\\print\\:gap-8, .\\print\\:gap-6 { gap: 8px !important; }
              .\\print\\:gap-4 { gap: 6px !important; }
              .\\print\\:gap-2 { gap: 4px !important; }
              .\\print\\:text-lg, .\\print\\:text-xl { font-size: 14px !important; }
              .\\print\\:text-base { font-size: 12px !important; }
              .\\print\\:text-sm { font-size: 10px !important; }
              .\\print\\:text-xs { font-size: 9px !important; }
              .\\print\\:text-\\[11px\\], .\\print\\:text-\\[12px\\] { font-size: 9px !important; }
              .\\print\\:text-\\[10px\\] { font-size: 8px !important; }
              .\\print\\:text-\\[9px\\] { font-size: 8px !important; }
              .\\print\\:text-\\[8px\\] { font-size: 7px !important; }
              .border, .border-b, .border-t, .border-l, .border-r, .print\\:break-inside-avoid { break-inside: avoid !important; page-break-inside: avoid !important; }
            `;
            clonedDoc.head.appendChild(compactPrintStyle);

            // Force visibility and font-family for all elements in Arabic contexts with robust connected text fallbacks
            const allNodesInClone = clonedDoc.querySelectorAll("*");
            allNodesInClone.forEach((node) => {
              const htmlEl = node as HTMLElement;

              const isRtl =
                htmlEl.getAttribute("dir") === "rtl" ||
                htmlEl.getAttribute("dir") === "auto" ||
                htmlEl.classList.contains("font-arabic") ||
                htmlEl.classList.contains("font-signature") ||
                htmlEl.style.direction === "rtl";

              // Fix html2canvas letter splitting by strictly resetting letter-spacing and forcing ligatures
              if (isRtl) {
                htmlEl.style.letterSpacing = "0px";
                htmlEl.style.wordSpacing = "normal";
              }

              // Force remove background highlights for clean PDF prints
              htmlEl.style.backgroundColor = "transparent";

              // Apply explicit connected font-families with local defaults to bypass cross-origin browser sandbox blocks
              if (htmlEl.classList.contains("font-signature")) {
                htmlEl.style.fontFamily =
                  "'Amiri', 'Times New Roman', 'Traditional Arabic', 'Sakkal Majalla', Georgia, serif";
                htmlEl.style.letterSpacing = "0px";
              } else if (
                htmlEl.classList.contains("font-arabic") ||
                htmlEl.closest(".font-arabic") ||
                htmlEl.getAttribute("dir") === "rtl" ||
                htmlEl.getAttribute("dir") === "auto"
              ) {
                htmlEl.style.fontFamily =
                  "'Cairo', 'Segoe UI', Tahoma, 'Geeza Pro', 'Noto Sans Arabic', Arial, sans-serif";
                htmlEl.style.letterSpacing = "0px";
              }

              // Fix html2canvas textarea alignment and wrapping bugs
              if (
                htmlEl.tagName.toLowerCase() === "textarea" ||
                htmlEl.tagName.toLowerCase() === "select" ||
                (htmlEl.tagName.toLowerCase() === "input" &&
                  (htmlEl as HTMLInputElement).type === "text")
              ) {
                const el = htmlEl as
                  | HTMLTextAreaElement
                  | HTMLInputElement
                  | HTMLSelectElement;
                const div = clonedDoc.createElement("div");

                let visibleText = el.value || "";
                if (htmlEl.tagName.toLowerCase() === "select") {
                  const selectEl = el as HTMLSelectElement;
                  if (
                    selectEl.selectedIndex >= 0 &&
                    selectEl.options[selectEl.selectedIndex]
                  ) {
                    visibleText = selectEl.options[selectEl.selectedIndex].text;
                  }
                }

                div.innerHTML = visibleText.replace(/\n/g, "<br/>");
                div.className = el.className;

                div.style.whiteSpace = "pre-wrap";
                div.style.wordWrap = "break-word";
                div.style.textAlign =
                  el.style.textAlign || (isRtl ? "right" : "left");
                div.style.direction = isRtl ? "rtl" : "ltr";
                div.style.cssText += el.style.cssText;

                el.parentNode?.replaceChild(div, el);
              }
            });

            // Force visibility for signature images to ensure they are captured by html2canvas
            const signatureImages = clonedDoc.querySelectorAll(
              'img[alt*="Signature"]',
            );
            signatureImages.forEach((img) => {
              (img as HTMLElement).style.visibility = "visible";
              (img as HTMLElement).style.display = "block";
              (img as HTMLElement).style.opacity = "1";
            });

            // Clean inline style attribute oklch, oklab and color-mix definitions
            const allElements = clonedDoc.getElementsByTagName("*");
            for (let i = 0; i < allElements.length; i++) {
              const el = allElements[i] as HTMLElement;
              if (el && typeof el.getAttribute === "function") {
                const inlineStyle = el.getAttribute("style");
                if (
                  inlineStyle &&
                  (inlineStyle.toLowerCase().includes("oklch") ||
                    inlineStyle.toLowerCase().includes("oklab") ||
                    inlineStyle.toLowerCase().includes("color-mix"))
                ) {
                  el.setAttribute(
                    "style",
                    cleanStringOfModernColors(inlineStyle),
                  );
                }
              }
            }
          } catch (cleanError) {
            console.error(
              "Clinical style sheet sanitization routine failed:",
              cleanError,
            );
          }
        },
      });

      const imgData = canvas.toDataURL("image/jpeg", 0.80);

      // Setup portrait standard A4 page dimensions
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      const imgWidth = 210; // A4 width in mm
      const pageHeight = 297; // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, "JPEG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      // Handle multi-page documents (e.g. comprehensive logs, multiple visits, leaflets)
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "JPEG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      // Clean file slugging matching patient name and docType
      const docSlug = docType
        ? docType.toLowerCase().replace(/_/g, "-")
        : "document";
      const cleanedPatientName = displayPatient.id
        ? `Patient_${displayPatient.id}`
        : "Patient";
      const filename = `${cleanedPatientName}_${docSlug}_${new Date().toISOString().split("T")[0]}.pdf`;

      // Render crisp Blob for inline preview and direct download in PDF format
      const pdfBlob = pdf.output("blob");
      const pdfBlobUrl = URL.createObjectURL(pdfBlob);

      let docTitle = "";
      switch (docType) {
        case "REFERRAL":
          docTitle = "إحالة طبية رسمية - Formal Medical Referral";
          break;
        case "CONSENT":
          docTitle = "نموذج موافقة المريض المستنيرة - Patient Informed Consent";
          break;
        case "CONSENT_PRIVATE_EXAM":
          docTitle = "موافقة فحص المناطق الخاصة - Private Area Exam Consent";
          break;
        case "VISIT_ORDERS":
          docTitle = "الطلبات والوصفات الطبية للزيارة - Visit Orders & Rx";
          break;
        case "PRESCRIPTION":
          docTitle = "روشتة طبية إلكترونية - Electronic Prescription (e-Rx)";
          break;
        case "INVESTIGATION":
          docTitle = "طلب فحوصات وتحاليل طبية - Investigation Request";
          break;
        case "MEDICATION_PROFILE":
          docTitle = "الملف الدوائي الشامل للمريض - Medication Profile";
          break;
        case "PATIENT_REPORT":
          docTitle = "تقرير طبي شخصي - Personal Medical Report";
          break;
        case "RECORD":
          docTitle =
            "تقرير طبي سريري رسمي لمراجع رسمية - Official Clinical Medical Report";
          break;
        default:
          if (docType.startsWith("LEAFLET_")) {
            docTitle = "منشور طبي تثقيفي للمريض - Patient Leaflet";
          } else {
            docTitle = "سجل طبي سريري للمريض - Clinical Health Record";
          }
      }

      // Build Telegram dispatch text details
      let text = `*الدكتور واثق الجلبي (Dr. Watheq Al-Chalabi Clinic)*\n`;
      text += `📄 *تم إنشاء ملف الـ PDF الطبي بنجاح ويتم تحميله الآن على جهازك!*\n\n`;
      text += `📢 *المستند:* ${docTitle}\n`;
      text += `👤 *المريض:* ${displayPatient.name}\n`;
      text += `🆔 *رقم الملف:* ${displayPatient.id}\n`;
      text += `📅 *التاريخ:* ${printDate.split(",")[0]}\n`;
      text += `\n📌 *يرجى سحب وإرفاق ملف الـ PDF المحمل (${filename}) في هذه الدردشة لإرسال التقرير الطبي الرسمي والموثق بالكامل.*\n`;
      text += `---------------------------------\n\n`;

      if (vitals) {
        text += `🩺 *موجز المؤشرات الحيوية للزيارة (Vitals):*\n`;
        if (vitals.bloodPressure)
          text += `• ضغط الدم: ${vitals.bloodPressure.trim()} mmHg\n`;
        if (vitals.temp) text += `• الحرارة: ${vitals.temp} °C\n`;
        if (vitals.chiefComplaint)
          text += `• الأعراض والشكوى: ${vitals.chiefComplaint.trim()}\n`;
        text += `\n`;
      }

      if (activeSession?.gpNotes) {
        text += `📋 *تعليمات الطبيب وتوصيات الخطة الطبية:*\n`;
        text += `_${activeSession.gpNotes.trim()}_\n\n`;
      }

      text += `🏥 *الدكتور واثق الجلبي (Dr. Watheq Al-Chalabi Clinic)*\n`;
      text += `📍 العراق - بغداد - السيدية\n`;
      text += `🔒 نظام السجلات السريرية الآمن والموثق`;

      const cleanPhone = displayPatient.phone ? displayPatient.phone.replace(/\D/g, "") : "";
      const fullPhone = cleanPhone.startsWith("07")
        ? "964" + cleanPhone.slice(1)
        : cleanPhone.startsWith("7")
          ? "964" + cleanPhone
          : cleanPhone;

      // Redirect URL builder (direct chat link)
      const formattedUrl =
        platform === "telegram"
          ? `https://t.me/+${fullPhone}`
          : `https://wa.me/${fullPhone}?text=${encodeURIComponent(text)}`;

      setSharePlatform(platform);
      setShareMessageText(text);
      setSharePdfData({
        filename,
        pdfBlobUrl,
        shareUrl: formattedUrl,
        blob: pdfBlob,
      });
      setIsGeneratingPdf(false);
    } catch (error) {
      console.error("Clinical dispatch to PDF failed:", error);
      alert("فشلت عملية مشاركة مستند الـ PDF الطبي.");
      setIsGeneratingPdf(false);
    }
  };

  // Attachment conversion handlings
  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleUploadAndConvertAttachment(e.dataTransfer.files[0]);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      handleUploadAndConvertAttachment(e.target.files[0]);
    }
  };

  const handleUploadAndConvertAttachment = async (file: File) => {
    setIsConvertingAttachment(true);
    setAttachmentError(null);
    try {
      let pdfBlobUrl = "";
      let rawBlob: Blob | undefined;
      let filename = file.name;

      if (file.type === "application/pdf") {
        // If it's already a PDF, use directly! No conversion needed!
        pdfBlobUrl = URL.createObjectURL(file);
        rawBlob = file;
      } else if (file.type.startsWith("image/")) {
        // Standard high-resolution snapshot loader & converter
        const reader = new FileReader();
        const imageDataUrl = await new Promise<string>((resolve, reject) => {
          reader.onload = (e) => resolve(e.target?.result as string);
          reader.onerror = (e) =>
            reject(new Error("Failed to read image file"));
          reader.readAsDataURL(file);
        });

        const pdf = new jsPDF({
          orientation: "portrait",
          unit: "mm",
          format: "a4",
        });

        // Draw elegant Clinical Letterhead Header
        pdf.setFillColor(248, 250, 252);
        pdf.rect(10, 10, 190, 30, "F");
        pdf.setDrawColor(226, 232, 240);
        pdf.rect(10, 10, 190, 30, "S");

        pdf.setTextColor(15, 23, 42);
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(11);
        pdf.text("IQ-DOCTOR DIGITAL MEDICAL SYSTEM", 15, 18);
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(8);
        pdf.setTextColor(100, 116, 139);
        pdf.text("Clinical Node Attachment Registry", 15, 23);
        pdf.text("Security Grounding: Verified Client Upload", 15, 27);
        pdf.text("Baghdad, Iraq", 15, 31);

        pdf.setTextColor(15, 23, 42);
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(9);
        pdf.text(`PATIENT: ${displayPatient.name || "N/A"}`, 130, 18);
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(8);
        pdf.text(`Patient ID: ${displayPatient.id || "N/A"}`, 130, 23);
        pdf.text(
          `Uploaded File: ${file.name.slice(0, 30)}${file.name.length > 30 ? "..." : ""}`,
          130,
          27,
        );
        pdf.text(`Created Date: ${new Date().toLocaleDateString("en-GB")}`, 130, 31);

        pdf.setDrawColor(79, 70, 229);
        pdf.setLineWidth(0.5);
        pdf.line(10, 45, 200, 45);

        pdf.setTextColor(79, 70, 229);
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(10);
        pdf.text(
          "OFFICIAL MEDICAL DOCUMENT ATTACHMENT / مرفق المستند السريري",
          15,
          52,
        );

        const img = new Image();
        img.src = imageDataUrl;
        await new Promise((resolve) => {
          img.onload = resolve;
        });

        const maxWidth = 180;
        const maxHeight = 210;
        let imgWidth = maxWidth;
        let imgHeight = (img.height * imgWidth) / img.width;

        if (imgHeight > maxHeight) {
          imgHeight = maxHeight;
          imgWidth = (img.width * imgHeight) / img.height;
        }

        const posX = 10 + (maxWidth - imgWidth) / 2;
        const posY = 58 + (maxHeight - imgHeight) / 2;

        pdf.addImage(imageDataUrl, "JPEG", posX, posY, imgWidth, imgHeight);

        pdf.setDrawColor(226, 232, 240);
        pdf.setLineWidth(0.2);
        pdf.line(10, 280, 200, 280);
        pdf.setFont("helvetica", "italic");
        pdf.setFontSize(7);
        pdf.setTextColor(148, 163, 184);
        pdf.text(
          "This attachment was automatically converted to a secure, standardized PDF format for clinical compliance.",
          15,
          285,
        );

        const pdfBlob = pdf.output("blob");
        rawBlob = pdfBlob;
        pdfBlobUrl = URL.createObjectURL(pdfBlob);

        filename = `${file.name.substring(0, file.name.lastIndexOf(".")) || file.name}_converted.pdf`;
      } else if (file.type.startsWith("text/")) {
        const reader = new FileReader();
        const textContent = await new Promise<string>((resolve, reject) => {
          reader.onload = (e) => resolve(e.target?.result as string);
          reader.onerror = (e) => reject(new Error("Failed to read text file"));
          reader.readAsText(file);
        });

        const pdf = new jsPDF({
          orientation: "portrait",
          unit: "mm",
          format: "a4",
        });

        pdf.setFillColor(248, 250, 252);
        pdf.rect(10, 10, 190, 30, "F");
        pdf.setDrawColor(226, 232, 240);
        pdf.rect(10, 10, 190, 30, "S");

        pdf.setTextColor(15, 23, 42);
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(11);
        pdf.text("IQ-DOCTOR DIGITAL MEDICAL SYSTEM", 15, 18);
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(8);
        pdf.setTextColor(100, 116, 139);
        pdf.text("Clinical Text Document Registry", 15, 23);
        pdf.text("Security Grounding: Verified Client Upload", 15, 27);
        pdf.text("Baghdad, Iraq", 15, 31);

        pdf.setTextColor(15, 23, 42);
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(9);
        pdf.text(`PATIENT: ${displayPatient.name || "N/A"}`, 130, 18);
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(8);
        pdf.text(`Patient ID: ${displayPatient.id || "N/A"}`, 130, 23);
        pdf.text(`Uploaded File: ${file.name}`, 130, 27);
        pdf.text(`Created Date: ${new Date().toLocaleDateString("en-GB")}`, 130, 31);

        pdf.setDrawColor(79, 70, 229);
        pdf.setLineWidth(0.5);
        pdf.line(10, 45, 200, 45);

        pdf.setTextColor(79, 70, 229);
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(10);
        pdf.text("CLINICAL LABORATORY / CLINICIAN TRANSCRIPT", 15, 52);

        pdf.setTextColor(51, 65, 85);
        pdf.setFont("courier", "normal");
        pdf.setFontSize(9);

        const lines = pdf.splitTextToSize(textContent, 180);
        let currentY = 62;
        const pageHeight = 270;

        lines.forEach((line: string) => {
          if (currentY > pageHeight) {
            pdf.addPage();
            currentY = 20;
          }
          pdf.text(line, 15, currentY);
          currentY += 5;
        });

        pdf.setDrawColor(226, 232, 240);
        pdf.setLineWidth(0.2);
        pdf.line(10, 280, 200, 280);
        pdf.setFont("helvetica", "italic");
        pdf.setFontSize(7);
        pdf.setTextColor(148, 163, 184);
        pdf.text(
          "This text attachment was automatically converted to a secure, standardized PDF format for clinical compliance.",
          15,
          285,
        );

        const pdfBlob = pdf.output("blob");
        rawBlob = pdfBlob;
        pdfBlobUrl = URL.createObjectURL(pdfBlob);

        filename = `${file.name.substring(0, file.name.lastIndexOf(".")) || file.name}_converted.pdf`;
      } else {
        throw new Error(
          "Unsupported file type. Please upload a PDF, PNG, JPG, or TXT document.",
        );
      }

      let text = `*الدكتور واثق الجلبي (Dr. Watheq Al-Chalabi Clinic)*\n`;
      text += `📄 *تم تحويل وإعداد المرفق الطبي بصيغة PDF بنجاح!*\n\n`;
      text += `📢 *مرفق إضافي للزيارة:* ${filename}\n`;
      text += `👤 *المريض:* ${displayPatient.name}\n`;
      text += `🆔 *رقم الملف:* ${displayPatient.id}\n`;
      text += `📅 *التاريخ:* ${new Date().toLocaleDateString("en-GB")}\n`;
      text += `\n📌 *يرجى إرفاق وإرسال ملف الـ PDF المحمل (${filename}) في هذه الدردشة لإكمال التوثيق السريري.*\n`;
      text += `---------------------------------\n\n`;
      text += `🏥 *الدكتور واثق الجلبي (Dr. Watheq Al-Chalabi Clinic)*\n`;
      text += `🔒 نظام السجلات السريرية الآمن والموثق`;

      const cleanPhone = displayPatient.phone ? displayPatient.phone.replace(/\D/g, "") : "";
      const fullPhone = cleanPhone.startsWith("07")
        ? "964" + cleanPhone.slice(1)
        : cleanPhone.startsWith("7")
          ? "964" + cleanPhone
          : cleanPhone;

      const formattedUrl =
        sharePlatform === "telegram"
          ? `https://t.me/+${fullPhone}`
          : `https://wa.me/${fullPhone}?text=${encodeURIComponent(text)}`;

      if (
        sharePdfData?.pdfBlobUrl &&
        sharePdfData.pdfBlobUrl.startsWith("blob:")
      ) {
        try {
          URL.revokeObjectURL(sharePdfData.pdfBlobUrl);
        } catch (_) {}
      }

      setShareMessageText(text);
      setSharePdfData({
        filename,
        pdfBlobUrl,
        shareUrl: formattedUrl,
        blob: rawBlob,
      });
    } catch (err: any) {
      console.error("Custom document attachment conversion failed:", err);
      setAttachmentError(err.message || "فشلت عملية معالجة وتحويل الملف.");
    } finally {
      setIsConvertingAttachment(false);
    }
  };

  return createPortal(
    <div className="clinical-print-portal-wrapper">
      {/* Dynamic override style tag to restrict layout printing onto ONLY this designated workspace container */}
      <style>{`
        @media print {
          /* Hide standard screen elements completely from physical print */
          .no-print, #root {
            display: none !important;
          }
          
          /* Hide all sibling elements under document body to prevent phantom heights */
          body > :not(.clinical-print-portal-wrapper) {
            display: none !important;
          }
          
          /* Setup and fit the main portal container */
          .clinical-print-portal-wrapper {
            display: block !important;
            position: absolute !important;
            left: 0 !important;
            right: 0 !important;
            top: 0 !important;
            width: 100% !important;
            height: auto !important;
            margin: 0 !important;
            padding: 0 !important;
            background: white !important;
            overflow: visible !important;
            visibility: visible !important;
          }
          
          /* Outer container holding the paper content */
          .clinical-print-area-wrapper {
            display: block !important;
            width: 210mm !important;
            max-width: 210mm !important;
            margin: 0 auto !important;
            padding: 0 !important;
            background: white !important;
          }

          /* Clinical A4 page configuration and centered print boundaries layout */
          @page {
            size: A4;
            margin: 0mm !important; /* Removes browser-generated headers/footers (URL) to hide electronic address */
          }
          html, body {
            background: white !important;
            margin: 0 !important;
            padding: 0 !important;
            width: 210mm !important; /* Standard A4 paper width */
            max-width: 210mm !important;
            height: auto !important;
            overflow: visible !important;
          }
          
          /* Enforce high-contrast pure black text for clinical decision accuracy */
          #patient-ehr-print-area, #patient-ehr-print-area * {
            box-sizing: border-box !important;
            visibility: visible !important;
            color: #000000 !important;
            text-shadow: none !important;
            box-shadow: none !important;
          }
          
          #patient-ehr-print-area {
            position: static !important;
            margin: 0 auto !important;
            width: 210mm !important;
            max-width: 210mm !important;
            min-height: 100vh !important; /* Force to paper height */ and multi-page flows */
            padding: 0mm ${simulatedMargin} ${simulatedMargin} ${simulatedMargin} !important; 
            box-sizing: border-box !important;
            background: transparent !important;
            box-shadow: none !important;
            border: none !important;
            display: block !important;
            float: none !important;
            page-break-after: auto !important;
            page-break-inside: auto !important;
            transform: none !important;
          }
          
          /* Clinical standard pagination: avoid breaking single-record containers or rows inside pages */
          .border,
          .rounded-lg,
          .rounded-xl,
          .shadow-xs,
          tr,
          tbody,
          .grid,
          .print-break-inside-avoid {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
            break-inside: avoid-page !important;
          }

          /* Ensure page breaks do not happen immediately after titles and headings */
          h1, h2, h3, h4, h5, h6, strong {
            page-break-after: avoid !important;
            break-after: avoid !important;
          }
          
          .no-print {
            display: none !important;
            height: 0 !important;
            width: 0 !important;
            overflow: hidden !important;
          }
        }
      `}</style>

      <div className="clinical-print-area-wrapper fixed inset-0 z-[100] flex items-center justify-center p-4 print:p-2 bg-slate-900/80 backdrop-blur-sm print:static print:bg-white print:p-0">
        <style>
          {`
            @media print {
              body * {
                visibility: hidden;
              }
              .clinical-print-area-wrapper, .clinical-print-area-wrapper * {
                visibility: visible;
              }
              .clinical-print-area-wrapper {
                position: absolute;
                left: 0;
                top: 0;
                width: 100%;
                margin: 0;
                padding: 0;
              }
            }
          `}
        </style>
        <div
          className={`bg-white w-full transition-all duration-300 rounded-xl shadow-2xl flex flex-col overflow-hidden max-h-[90vh] print:max-h-none print:overflow-visible print:h-auto print:rounded-none print:shadow-none print:w-full print:border-none print:ring-0 ${isPreviewMode ? "max-w-4xl" : "max-w-3xl"}`}
        >
          {/* Modal Control Panel - Intercepted during physical page printing */}
          <div className="no-print bg-slate-900 px-2 py-1 print:py-1 flex items-center justify-between text-white border-b border-slate-800 border-t-0 border-l-0 border-r-0">
            <div className="flex items-center gap-2 print:gap-1">
              <Shield className="w-5 h-5 text-emerald-400" />
              <div>
                <h2 className="text-sm print:text-sm print:text-sm font-bold tracking-tight">
                  EHR Export & Printed Documentation
                </h2>
                <p className="text-[10px] print:text-[11px] text-slate-400">
                  IQ-doctor clinic node sync
                </p>
              </div>
            </div>
            <div className="flex items-center flex-wrap gap-2 print:p-1">
              <button
                onClick={handleShareWhatsApp}
                className="bg-[#25D366] hover:bg-[#20ba5a] text-white rounded-md flex items-center justify-center cursor-pointer shadow-sm transition active:scale-95 animate-in fade-in w-9 h-9 shrink-0"
                title="مشاركة واتساب / Share"
              >
                <Share2 className="w-4 h-4" />
              </button>
              <button
                onClick={handleShareTelegram}
                className="bg-[#0088cc] hover:bg-[#0077b5] text-white rounded-md flex items-center justify-center cursor-pointer shadow-sm transition active:scale-95 animate-in fade-in w-9 h-9 shrink-0"
                title="مشاركة تليجرام / Share"
              >
                <Share2 className="w-4 h-4" />
              </button>
              <button
                onClick={() => {
                  setGmailRecipient(patient.email || "");
                  setShowGmailModal(true);
                }}
                className="bg-rose-600 hover:bg-rose-500 text-white rounded-md flex items-center justify-center cursor-pointer shadow-sm transition active:scale-95 w-9 h-9 shrink-0"
                title="إرسال Gmail / Send Email"
              >
                <Mail className="w-4 h-4" />
              </button>

              <button
                onClick={handleExplicitSave}
                disabled={isExplicitSaving}
                className="bg-indigo-600 hover:bg-indigo-500 text-white rounded-md flex items-center justify-center cursor-pointer shadow-sm transition disabled:opacity-50 w-9 h-9 shrink-0"
                title={
                  isExplicitSaving
                    ? "Saving..."
                    : isExplicitSaved || isDocAlreadyCompleted
                      ? "Saved ✓"
                      : "Save Document"
                }
              >
                {isExplicitSaved || isDocAlreadyCompleted ? (
                  <CheckCircle className="w-4 h-4 text-emerald-400" />
                ) : (
                  <Save className={`w-4 h-4 ${isExplicitSaving ? "animate-pulse" : ""}`} />
                )}
              </button>

              <button
                onClick={handleTriggerPrint}
                className="bg-emerald-600 hover:bg-emerald-500 text-white rounded-md flex items-center justify-center cursor-pointer shadow-sm transition w-9 h-9 shrink-0"
                title="Print / Save PDF"
              >
                <Printer className="w-4 h-4" />
              </button>
              <button
                onClick={handleSaveToAttachments}
                disabled={isSavingToAttachments}
                className="bg-slate-700 hover:bg-slate-600 text-white rounded-md flex items-center justify-center cursor-pointer shadow-sm transition disabled:opacity-50 w-9 h-9 shrink-0"
                title={isSavingToAttachments ? "جاري الحفظ..." : "حفظ في المرفقات"}
              >
                <FolderOpen className={`w-4 h-4 ${isSavingToAttachments ? "animate-pulse" : ""}`} />
              </button>
              <button
                onClick={onClose}
                className="bg-red-600 hover:bg-red-700 text-white rounded-md flex items-center justify-center cursor-pointer shadow-sm transition active:scale-95 shrink-0 w-9 h-9"
                title="العودة لواجهة الطبيب / Back"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Print Layout Controls Sub-Bar (no-print) */}
          <div className="no-print bg-[#F8FAFC] border-b border-slate-200 px-2 py-1 flex flex-nowrap items-center gap-3 overflow-x-auto text-[10px] font-sans text-slate-700 hide-scrollbar">
            <button
              onClick={() => setIsPreviewMode(!isPreviewMode)}
              className={`flex items-center gap-1 px-2 py-1 rounded border font-bold transition shadow-sm cursor-pointer select-none whitespace-nowrap shrink-0 ${
                isPreviewMode
                  ? "bg-indigo-50 border-indigo-200 text-indigo-700"
                  : "bg-white border-slate-300 text-slate-700 hover:bg-slate-50"
              }`}
            >
              <Layers className="w-3 h-3" />
              طريقة العرض القياسية (Form Mode)
            </button>

            <div className="flex items-center gap-1 shrink-0">
              <span className="font-bold text-slate-600 whitespace-nowrap">
                هوامش الصفحة Margins:
              </span>
              <select
                value={simulatedMargin}
                onChange={(e) => setSimulatedMargin(e.target.value)}
                className="bg-white border border-slate-300 rounded px-1.5 py-0.5 text-[10px] font-semibold cursor-pointer outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
              >
                <option value="5mm">ضيق جداً (5mm)</option>
                <option value="10mm">مضغوط (10mm)</option>
                <option value="15mm">قياسي (15mm)</option>
                <option value="20mm">عريض (20mm)</option>
                <option value="25mm">واسع جداً (25mm)</option>
              </select>
            </div>

            <label className="flex items-center gap-1 cursor-pointer font-semibold select-none shrink-0">
              <input
                type="checkbox"
                checked={highContrastMode}
                onChange={(e) => setHighContrastMode(e.target.checked)}
                className="w-3 h-3 text-indigo-600 border-slate-300 rounded focus:ring-indigo-500 cursor-pointer"
              />
              <span className="whitespace-nowrap">
                خط أسود فائق التباين (High Contrast Black)
              </span>
            </label>

            <label className="flex items-center gap-1 cursor-pointer font-semibold select-none shrink-0">
              <input
                type="checkbox"
                checked={showPageBreakGuides}
                onChange={(e) => setShowPageBreakGuides(e.target.checked)}
                className="w-3 h-3 text-indigo-600 border-slate-300 rounded focus:ring-indigo-500 cursor-pointer"
              />
              <span className="whitespace-nowrap">
                خطوط إرشادية لصفحة A4 (A4 Grid Line)
              </span>
            </label>
          </div>

          {/* PRINT CANVAS CONTAINER WORKSPACE (Adds smooth scrolling overlay with shadow A4 clipping) */}
          <div
            className={`flex-1 overflow-auto print:block print:overflow-visible print:h-auto print:p-0 w-full ${isPreviewMode ? "bg-slate-700 p-4 print:p-2 md:p-8 print:p-2 flex flex-col items-start md:items-center min-h-0" : "bg-white"}`}
          >
            <div
              id="patient-ehr-print-area"
              style={{
                padding: simulatedMargin,
                color: highContrastMode ? "#000000" : "#1E293B",
                width: isPreviewMode ? "794px" : "100%",
                minHeight: isPreviewMode ? "1123px" : "auto",
              }}
              className={`flex flex-col transition-all duration-300 relative bg-white shrink-0 ${
                highContrastMode ? "text-black" : "text-slate-800"
              } ${
                isPreviewMode
                  ? "shadow-2xl border border-slate-300 ring-4 ring-black/5 mx-auto rounded-xs"
                  : "p-8 print:p-2"
              }`}
            >
              {/* A4 Page break guides inside print preview workspace */}
              {isPreviewMode && showPageBreakGuides && (
                <div className="absolute inset-x-0 top-[297mm] border-t-2 border-dashed border-rose-400 opacity-60 pointer-events-none select-none no-print z-10">
                  <div className="absolute right-4 -top-3 print:p-1 bg-rose-100 border border-rose-200 text-rose-700 text-[8px] font-bold px-1.5 py-0.5 rounded shadow-sm">
                    حد الصفحة الأولى التقريبي A4 (Approx. Page 1 Boundary)
                  </div>
                </div>
              )}
              {isPreviewMode && showPageBreakGuides && (
                <div className="absolute inset-x-0 top-[594mm] border-t-2 border-dashed border-rose-400 opacity-60 pointer-events-none select-none no-print z-10">
                  <div className="absolute right-4 -top-3 print:p-1 bg-rose-100 border border-rose-200 text-rose-700 text-[8px] font-bold px-1.5 py-0.5 rounded shadow-sm">
                    حد الصفحة الثانية التقريبي A4 (Approx. Page 2 Boundary)
                  </div>
                </div>
              )}

              {/* Document Header Section */}
              <div
                className={`border-b-4 border-black ${docType === "PRESCRIPTION" || docType === "INVESTIGATION" || docType === "SICK_LEAVE" ? "pb-2 mb-2" : "pb-4 print:pb-1 mb-4 print:mb-1"} print:pb-1 print:mb-2 flex justify-between items-start relative`}
              >
                <div className="w-full flex flex-col items-center justify-center text-center">
                  {docType !== "PRESCRIPTION" && (
                    <div
                      className={`${docType === "INVESTIGATION" || docType === "SICK_LEAVE" ? "w-12 h-12 mb-1" : "w-16 print:w-10 h-16 print:h-10 mb-2"} rounded-full overflow-hidden flex items-center justify-center`}
                    >
                      <IQDoctorLogo className="w-full h-full" />
                    </div>
                  )}
                  <div>
                    <div
                      className="flex flex-col items-center justify-center font-arabic text-center mt-0.5"
                      dir="rtl"
                    >
                      <div className="text-[17px] print:text-[16px] font-bold text-slate-800 font-signature">
                        {currentPhysicianAr}
                      </div>
                      <div className="text-[9.5px] font-bold text-slate-700">
                        {currentPhysicianTitleAr}
                      </div>
                      <div className="text-[8.5px] text-slate-500 font-mono mt-0.5">
                        رقم التسجيل: {currentRegNo}
                      </div>
                    </div>
                  </div>
                  <p className="text-[10px] print:text-[11px] text-[#475569] font-medium uppercase tracking-widest mt-0.5">
                    Integrated Medical Systems • Baghdad, Iraq • Clinic Phone:
                    07893804884
                  </p>
                  <div
                    className={`print:mt-0 ${docType === "PRESCRIPTION" || docType === "INVESTIGATION" || docType === "SICK_LEAVE" ? "mt-1" : "mt-3"}`}
                  >
                    <span
                      className={`text-[10px] print:text-[11px] px-2 py-0.5 rounded font-bold uppercase tracking-widest font-mono inline-block bg-white text-slate-900 border border-slate-900`}
                    >
                      {docType === "REFERRAL"
                        ? "Formal Medical Referral"
                        : docType === "CONSENT"
                          ? "Patient Informed Consent"
                          : docType === "CONSENT_PRIVATE_EXAM"
                            ? "Private Area Exam Consent"
                            : docType === "VISIT_ORDERS"
                              ? "Visit Orders & Prescriptions"
                              : docType === "PRESCRIPTION"
                                ? "Medical Prescription (e-Rx)"
                                : docType === "INVESTIGATION"
                                  ? "Investigation Request Form"
                                  : docType === "SICK_LEAVE"
                                    ? "Official Sick Leave (إجازة مرضية)"
                                    : docType === "MEDICATION_PROFILE"
                                      ? "Comprehensive Medication Profile"
                                      : docType === "BILL_10000"
                                        ? "Patient Receipt / وصل استلام نقدية"
                                        : docType.startsWith("LEAFLET")
                                          ? "Patient Information Leaflet (BAD.org.uk)"
                                          : docType === "RECORD"
                                            ? "تقرير طبي"
                                            : "Official Clinical Health Record"}
                    </span>
                  </div>
                </div>
                <div className="absolute right-0 top-0 flex flex-col items-end gap-1">
                  <div className="border border-slate-200 p-0.5 rounded bg-white">
                    <QRCodeSVG
                      value={
                        typeof window !== "undefined"
                          ? window.location.origin + "/#"
                          : "https://iq-dr.com/#"
                      }
                      size={
                        docType === "PRESCRIPTION" ||
                        docType === "INVESTIGATION" ||
                        docType === "SICK_LEAVE"
                          ? 36
                          : 48
                      }
                      level="H"
                    />
                  </div>
                  <div className="text-right text-xs print:text-sm">
                    <span className="text-[#64748B] block font-mono text-[8px] uppercase tracking-wider font-semibold">
                      Report Timestamp
                    </span>
                    <strong className="text-[#0F172A] font-bold font-mono text-[10px] print:text-[11px]">
                      {printDate}
                    </strong>
                  </div>
                </div>
              </div>

              {/* Unified Encounter & Patient Information Grid - Connects Print with Open Visit */}
              <div className="border border-black p-1.5 mb-2 text-xs print:text-sm font-sans bg-white shadow-none">
                <div className="border-b border-black pb-1 mb-1 flex items-center justify-between">
                  <span className="font-extrabold uppercase tracking-widest text-black text-[9px]">
                    I. Patient Profile
                  </span>
                  <span className="text-[9px] text-black font-bold uppercase">
                    Date: {printDate.split(",")[0]}
                  </span>
                </div>
                <div className="grid grid-cols-4 gap-2 print:gap-1 text-[10px] print:text-[11px]">
                  <div className="col-span-2">
                    <span className="text-black font-bold text-[8px] uppercase tracking-wider block">
                      Patient Name
                    </span>
                    <span className="font-extrabold text-sm print:text-sm print:text-sm text-black uppercase">
                      {displayPatient.name}
                    </span>
                  </div>
                  <div>
                    <span className="text-black font-bold text-[8px] uppercase tracking-wider block">
                      Age
                    </span>
                    <span className="font-semibold text-black">
                      {new Date().getFullYear() -
                        new Date(displayPatient.dob).getFullYear()}{" "}
                      Years
                    </span>
                  </div>
                  <div>
                    <span className="text-black font-bold text-[8px] uppercase tracking-wider block">
                      Gender
                    </span>
                    <span className="font-semibold text-black uppercase">
                      {displayPatient.gender || "—"}
                    </span>
                  </div>
                  <div className="col-span-4 mt-0.5">
                    <span className="text-black font-bold text-[8px] uppercase tracking-wider inline-block mr-2">
                      Diagnosis:
                    </span>
                    <span className="italic text-black font-semibold text-[10px] print:text-[11px]">
                      {activeSession?.soapAssessment ||
                        vitals?.chiefComplaint ||
                        "Consultation context"}
                    </span>
                  </div>
                </div>
              </div>

              {docType === "REFERRAL" && (
                <div className="flex-1 flex flex-col space-y-6 print:space-y-2 print:space-y-0.5">
                  <h2 className="text-xl print:text-sm font-bold uppercase tracking-tight border-b pb-2">
                    Medical Referral Letter
                  </h2>
                  <div className="border border-slate-300 p-3 print:p-1 rounded-md text-xs print:text-sm font-mono mb-4 print:mb-1 bg-slate-50">
                    <p>
                      <strong>Recipient:</strong> Relevant Specialist Consultant
                    </p>
                    <p>
                      <strong>Facility Referral From:</strong>{" "}
                      {currentPhysicianEn}, Dr. Watheq Al-Chalabi Clinic (Baghdad, Iraq)
                    </p>
                    <p>
                      <strong>Registered Physician Number:</strong> 17582
                    </p>
                  </div>
                  <div className="space-y-4 print:space-y-0.5 pt-4 print:pt-1 text-sm print:text-sm print:text-sm leading-relaxed print:leading-tight">
                    <p>Dear Colleague,</p>
                    <p>
                      Thank you for seeing <strong>{displayPatient.name}</strong>, a{" "}
                      {new Date().getFullYear() -
                        new Date(displayPatient.dob).getFullYear()}
                      -year-old patient who presented at our clinic.
                    </p>

                    <div
                      className={`mt-2 ${!referralAdditionalNotes && "print:hidden"}`}
                    >
                      <textarea
                        rows={2}
                        placeholder="Doctor's additional notes / ملاحظات إضافية للطبيب..."
                        value={referralAdditionalNotes}
                        onChange={(e) => {
                          const val = e.target.value;
                          setReferralAdditionalNotes(val);
                          localStorage.setItem(
                            `referral_add_notes_${recordKey}`,
                            val,
                          );
                        }}
                        dir="auto"
                        className="w-full whitespace-pre-wrap text-sm print:text-sm bg-transparent border border-dashed border-slate-300 hover:border-indigo-400 focus:border-indigo-500 focus:outline-none p-1 rounded resize-y print:resize-none print:border-none print:p-0"
                      />
                    </div>

                    {vitals && vitals.chiefComplaint && (
                      <p>
                        <strong>Chief Complaint:</strong>{" "}
                        {vitals.chiefComplaint}
                      </p>
                    )}

                    {activeSession &&
                      (referralFindings || activeSession.gpNotes) && (
                        <div>
                          <input
                            type="text"
                            value={referralFindingsHeader}
                            onChange={(e) => {
                              const val = e.target.value;
                              setReferralFindingsHeader(val);
                              localStorage.setItem(
                                `referral_findings_hdr_${recordKey}`,
                                val,
                              );
                            }}
                            className="font-bold bg-transparent border-b border-dashed border-slate-300 focus:outline-none focus:border-indigo-500 w-full max-w-sm print:border-none inline-block print:p-0"
                          />
                          <div className="mt-1">
                            <textarea
                              rows={6}
                              value={referralFindings}
                              onChange={(e) => {
                                const val = e.target.value;
                                setReferralFindings(val);
                                localStorage.setItem(
                                  `referral_findings_${recordKey}`,
                                  val,
                                );
                              }}
                              dir="auto"
                              className="w-full whitespace-pre-wrap mt-1 text-sm print:text-sm bg-transparent border border-dashed border-slate-300 hover:border-indigo-400 focus:border-indigo-500 focus:outline-none p-1 rounded resize-y print:resize-none print:border-none print:p-0"
                            />
                          </div>
                        </div>
                      )}

                    {medicalHistory &&
                      medicalHistory.chronicConditions?.length > 0 && (
                        <div>
                          <strong>Relevant Chronic Conditions:</strong>
                          <ul className="list-disc pl-5 mt-1">
                            {medicalHistory.chronicConditions?.map((c) => (
                              <li key={c.id}>
                                {c.condition} ({c.status})
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                    <p className="pt-4 print:pt-1">
                      I would greatly appreciate your specialist assessment,
                      diagnostic opinion, and further management of this
                      patient.
                    </p>
                    <p>Sincerely,</p>
                    <div className="mt-auto grid grid-cols-2 gap-8 print:p-2 pt-4 print:pt-1 border-t border-slate-300 items-end print:break-inside-avoid">
                      <div>
                        <div className="flex flex-col items-start">
                          <p className="text-[10px] print:text-[11px] text-slate-500 uppercase font-extrabold tracking-widest mb-1">
                            Physician Signature / التوقيع والختم
                          </p>
                          {physicianSignature ? (
                            <img
                              src={physicianSignature}
                              alt="Physician Signature"
                              className="h-[64px] print:h-[54px] w-auto max-w-[150px] object-contain border-b border-black mb-1 mix-blend-multiply opacity-95"
                            />
                          ) : (
                            <div className="w-48 border-b border-black mb-1 h-10">
                              <SignaturePad
                                label="Sign Below"
                                onSave={setPhysicianSignature}
                                onClear={() => setPhysicianSignature(null)}
                              />
                            </div>
                          )}
                          <p className="font-bold text-[11px] print:text-[14px] text-slate-600 mt-1">
                            {currentPhysicianEn}
                          </p>
                          <p className="text-[8.5px] text-slate-400">
                            Dr. Watheq Al-Chalabi Clinic • Reg: {currentRegNo}
                          </p>
                        </div>
                      </div>
                      <div
                        className="text-right flex flex-col justify-end items-end h-full font-arabic pr-2"
                        dir="rtl"
                        style={{ direction: "rtl" }}
                      >
                        <div className="font-extrabold text-[#0F172A] text-[15px] print:text-[16px] font-signature leading-snug">
                          {currentPhysicianAr}
                        </div>
                        <div className="text-[9.5px] font-bold text-slate-700 mt-1">
                          {currentPhysicianTitleAr}
                        </div>
                        <div className="text-[8.5px] text-slate-500 font-mono mt-0.5">
                          رقم التسجيل: {currentRegNo}
                        </div>
                        <p className="text-[10px] print:text-[11px] text-slate-400 mt-2 font-sans">
                          الدكتور واثق الجلبي • بغداد
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {docType === "CONSENT" && (
                <div className="flex-1 flex flex-col space-y-6 print:space-y-2 print:space-y-0.5" dir="rtl">
                  <h2 className="text-xl print:text-sm font-bold uppercase tracking-tight border-b pb-2">
                    نموذج الإقرار والموافقة الطبية المستنيرة
                  </h2>
                  <div className="text-sm print:text-sm print:text-sm space-y-4 print:space-y-0.5 leading-relaxed print:leading-tight">
                    <p>
                      أنا، <strong>{displayPatient.name}</strong> (رقم الملف: {displayPatient.id})،
                      أفوض الطبيب المعالج في <strong>الدكتور واثق الجلبي</strong> وأي مساعدين 
                      يعينهم للقيام بالإجراءات السريرية المحددة في خطتي العلاجية.
                    </p>

                    <h3 className="font-bold mt-4 print:mt-1">
                      1. شرح الإجراء الطبي
                    </h3>
                    <p>
                      لقد تم إعلامي بطبيعة والغرض من الإجراءات المقترحة، والخيارات العلاجية البديلة،
                      والمخاطر والمضاعفات والآثار الجانبية المحتملة.
                    </p>

                    <h3 className="font-bold mt-4 print:mt-1">
                      2. التخدير والأدوية
                    </h3>
                    <p>
                      أوافق على إعطاء أي تخدير أو أدوية يعتبرها الفريق الطبي ضرورية. لقد 
                      أفصحت عن جميع أنواع الحساسية التي أعاني منها والأدوية التي أتناولها حالياً.
                    </p>

                    {medicalHistory &&
                      medicalHistory.allergies &&
                      medicalHistory.allergies.length > 0 && (
                        <div className="bg-rose-50 p-4 print:p-2 rounded-md border border-rose-200">
                          <strong className="text-rose-700">
                            الحساسية المسجلة للمريض:
                          </strong>
                          <ul className="list-disc pr-5 text-rose-700 mt-1">
                            {medicalHistory.allergies.map((a) => (
                              <li key={a.id}>
                                {a.allergen} ({a.severity})
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                    <h3 className="font-bold mt-4 print:mt-1">
                      3. إقرار وفهم
                    </h3>
                    <p>
                      أقر بأنه لم تقدم لي أي ضمانات بخصوص نتيجة الإجراء الطبي. وأؤكد 
                      أنني قرأت وفهمت تماماً نموذج الموافقة هذا، وأنه قد تم الإجابة على جميع أسئلتي 
                      بشكلٍ مرضٍ.
                    </p>

                    <div className="mt-auto pt-8 grid grid-cols-2 gap-8 print:p-2 text-xs print:text-sm font-mono">
                      <div className="flex flex-col">
                        {patientSignature ? (
                          <div className="h-32 flex flex-col justify-end">
                            <img
                              src={patientSignature}
                              alt="Patient Signature"
                              className="h-[120px] object-contain border-b border-black mb-2 px-4 w-full"
                            />
                          </div>
                        ) : (
                          <div className="mb-4 print:mb-1">
                            <SignaturePad
                              label="التوقيع الرقمي للمريض"
                              onSave={setPatientSignature}
                              onClear={() => setPatientSignature(null)}
                            />
                            <div className="border-b border-black h-8 mb-2"></div>
                          </div>
                        )}
                        <p>توقيع المريض (أو ولي الأمر/الوصي)</p>
                        <p className="mt-2 text-slate-500">التاريخ: {printDate}</p>
                        {patientSignature && (
                          <p className="text-[9px] text-emerald-600 font-bold mt-1 uppercase">
                            ✓ تم التوقيع رقمياً
                          </p>
                        )}
                      </div>

                      <div className="flex flex-col justify-between">
                        <div>
                          <p className="text-[11px] print:text-sm font-bold text-slate-500 uppercase tracking-wider mb-2">
                            Signature & Credentials / توقيع واعتماد الطبيب
                          </p>

                          <div className="grid grid-cols-12 gap-4 print:p-2 items-end bg-slate-50 p-3 print:p-1 rounded-lg border border-slate-200">
                            {/* Signature block */}
                            <div className="col-span-7 flex flex-col items-start border-r border-slate-200 pr-3">
                              {physicianSignature ? (
                                <div className="flex flex-col justify-end">
                                  <img
                                    src={physicianSignature}
                                    alt="Physician Signature"
                                    className="h-[64px] print:h-[54px] w-auto max-w-[150px] object-contain border-b border-black mb-1 mix-blend-multiply opacity-95"
                                  />
                                </div>
                              ) : (
                                <div className="w-full mb-1">
                                  <div className="h-[56px] print:h-[28px] w-full flex items-center justify-center border-b border-slate-400 border-dashed">
                                    <SignaturePad
                                      label="Draw Signature"
                                      onSave={setPhysicianSignature}
                                      onClear={() =>
                                        setPhysicianSignature(null)
                                      }
                                    />
                                  </div>
                                </div>
                              )}
                              <p className="font-extrabold text-[9.5px] print:text-[14px] text-slate-800">
                                {currentPhysicianEn}
                              </p>
                              <p className="text-[8.5px] font-medium text-slate-500 mt-0.5">
                                Attending Physician • Reg: {currentRegNo}
                              </p>
                            </div>

                            {/* Arabic text block (adjacent and perfectly aligned RTL) */}
                            <div
                              className="col-span-5 text-right flex flex-col justify-end items-end font-arabic"
                              dir="rtl"
                              style={{ direction: "rtl" }}
                            >
                              <div className="font-extrabold text-[#0F172A] text-[15px] print:text-[16px] font-signature leading-tight">
                                {currentPhysicianAr}
                              </div>
                              <div className="text-[9.5px] font-bold text-slate-700 mt-1 leading-tight">
                                {currentPhysicianTitleAr}
                              </div>
                              <div className="text-[8.5px] text-slate-500 font-mono mt-0.5">
                                رقم التسجيل: {currentRegNo}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="mt-2 flex justify-between items-center text-[10px] print:text-[11px] text-slate-500 px-1 font-sans">
                          <p>التاريخ: {printDate}</p>
                          {physicianSignature && (
                            <p className="text-[9px] text-emerald-600 font-bold uppercase">
                              ✓ تم التوقيع رقمياً
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {docType === "CONSENT_PRIVATE_EXAM" && (
                <div className="space-y-6 print:space-y-2 print:space-y-0.5" dir="rtl">
                  <h2 className="text-xl print:text-sm font-bold uppercase tracking-tight border-b pb-2">
                    نموذج الموافقة على فحص المناطق الخاصة
                  </h2>
                  <div className="text-sm print:text-sm print:text-sm space-y-4 print:space-y-0.5 leading-relaxed print:leading-tight">
                    <p>
                      أنا، <strong>{displayPatient.name}</strong> (رقم الملف: {displayPatient.id})،
                      أفوض الطبيب المعالج في <strong>الدكتور واثق الجلبي</strong> لإجراء فحص
                      للمناطق الخاصة/الحساسة حسب الضرورة الطبية وما تتطلبه الحالة من أجل التشخيص والعلاج.
                    </p>

                    <h3 className="font-bold mt-4 print:mt-1">
                      1. شرح الفحص الطبي
                    </h3>
                    <p>
                      لقد تم إعلامي بطبيعة والغرض ونطاق الفحص. وقد شرح الطبيب 
                      سبب ضرورة هذا الفحص لخطتي العلاجية وما يتضمنه الإجراء.
                    </p>

                    <h3 className="font-bold mt-4 print:mt-1">
                      2. حق توفير مرافق (Chaperone)
                    </h3>
                    <p>
                      أقر بأنه قد عُرض عليّ الحق في وجود مرافق (أحد أفراد الكادر الطبي
                      أو شخص محدد من اختياري) ليكون حاضراً أثناء هذا الفحص.
                    </p>
                    <div className="mt-2 pr-4 space-y-2 print:space-y-0.5 border-r-2 border-slate-300 mr-2 py-1">
                      <label className="flex items-center gap-2 print:gap-1 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={chaperoneAgreed}
                          onChange={(e) => {
                            setChaperoneAgreed(e.target.checked);
                            if (e.target.checked) setChaperoneRefused(false);
                            dispatchClinicalEvent("موافقة المرافق", e.target.checked ? "تم تحديد موافقة المرافق" : "تم إلغاء موافقة المرافق");
                          }}
                          className="w-4 h-4 text-indigo-600 border-slate-300 rounded focus:ring-indigo-500"
                        />
                        <span>
                          أنا <strong>أوافق</strong> على وجود مرافق أثناء الفحص السريري.
                        </span>
                      </label>
                      <label className="flex items-center gap-2 print:gap-1 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={chaperoneRefused}
                          onChange={(e) => {
                            setChaperoneRefused(e.target.checked);
                            if (e.target.checked) setChaperoneAgreed(false);
                            dispatchClinicalEvent("رفض المرافق", e.target.checked ? "تم تحديد رفض المرافق" : "تم إلغاء رفض المرافق");
                          }}
                          className="w-4 h-4 text-indigo-600 border-slate-300 rounded focus:ring-indigo-500"
                        />
                        <span>
                          أنا <strong>أرفض</strong> وجود مرافق وأفضل أن أُفحص بمفردي مع الطبيب.
                        </span>
                      </label>
                    </div>

                    <h3 className="font-bold mt-4 print:mt-1">
                      3. الحق في سحب الموافقة
                    </h3>
                    <p>
                      أفهم أن موافقتي هذه طوعية تماماً، وأنه يحق لي سحب الموافقة 
                      وإيقاف الفحص في أي وقت دون أن يكون لذلك أي تأثير سلبي على استمرار رعايتي الطبية.
                    </p>

                    <h3 className="font-bold mt-4 print:mt-1">
                      4. إقرار وفهم
                    </h3>
                    <p>
                      أؤكد أنني قرأت وفهمت تماماً نموذج الموافقة هذا، وأنه سيتم 
                      احترام خصوصيتي طوال الوقت، وأنه قد تمت الإجابة على جميع أسئلتي 
                      بشكلٍ مرضٍ.
                    </p>

                    <div className="mt-auto pt-8 grid grid-cols-2 gap-8 print:p-2 text-xs print:text-sm font-mono">
                      <div className="flex flex-col">
                        {patientSignature ? (
                          <div className="h-32 flex flex-col justify-end">
                            <img
                              src={patientSignature}
                              alt="Patient Signature"
                              className="h-[120px] object-contain border-b border-black mb-2 px-4 w-full"
                            />
                          </div>
                        ) : (
                          <div className="mb-4 print:mb-1">
                            <SignaturePad
                              label="التوقيع الرقمي للمريض"
                              onSave={setPatientSignature}
                              onClear={() => setPatientSignature(null)}
                            />
                            <div className="border-b border-black h-8 mb-2"></div>
                          </div>
                        )}
                        <p>توقيع المريض (أو ولي الأمر/الوصي)</p>
                        <p className="mt-2 text-slate-500">التاريخ: {printDate}</p>
                        {patientSignature && (
                          <p className="text-[9px] text-emerald-600 font-bold mt-1 uppercase">
                            ✓ تم التوقيع رقمياً
                          </p>
                        )}
                      </div>

                      <div className="flex flex-col justify-between">
                        <div>
                          <p className="text-[11px] print:text-sm font-bold text-slate-500 uppercase tracking-wider mb-2">
                            Signature & Credentials / توقيع واعتماد الطبيب
                          </p>

                          <div className="grid grid-cols-12 gap-4 print:p-2 items-end bg-slate-50 p-3 print:p-1 rounded-lg border border-slate-200">
                            {/* Signature block */}
                            <div className="col-span-7 flex flex-col items-start border-r border-slate-200 pr-3">
                              {physicianSignature ? (
                                <div className="flex flex-col justify-end">
                                  <img
                                    src={physicianSignature}
                                    alt="Physician Signature"
                                    className="h-[64px] print:h-[54px] w-auto max-w-[150px] object-contain border-b border-black mb-1 mix-blend-multiply opacity-95"
                                  />
                                </div>
                              ) : (
                                <div className="w-full mb-1">
                                  <div className="h-[56px] print:h-[28px] w-full flex items-center justify-center border-b border-slate-400 border-dashed">
                                    <SignaturePad
                                      label="Draw Signature"
                                      onSave={setPhysicianSignature}
                                      onClear={() =>
                                        setPhysicianSignature(null)
                                      }
                                    />
                                  </div>
                                </div>
                              )}
                              <p className="font-extrabold text-[9.5px] print:text-[14px] text-slate-800">
                                {currentPhysicianEn}
                              </p>
                              <p className="text-[8.5px] font-medium text-slate-500 mt-0.5">
                                Attending Physician • Reg: {currentRegNo}
                              </p>
                            </div>

                            {/* Arabic text block (adjacent and perfectly aligned RTL) */}
                            <div
                              className="col-span-5 text-right flex flex-col justify-end items-end font-arabic"
                              dir="rtl"
                              style={{ direction: "rtl" }}
                            >
                              <div className="font-extrabold text-[#0F172A] text-[15px] print:text-[16px] font-signature leading-tight">
                                {currentPhysicianAr}
                              </div>
                              <div className="text-[9.5px] font-bold text-slate-700 mt-1 leading-tight">
                                {currentPhysicianTitleAr}
                              </div>
                              <div className="text-[8.5px] text-slate-500 font-mono mt-0.5">
                                رقم التسجيل: {currentRegNo}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="mt-2 flex justify-between items-center text-[10px] print:text-[11px] text-slate-500 px-1 font-sans">
                          <p>التاريخ: {printDate}</p>
                          {physicianSignature && (
                            <p className="text-[9px] text-emerald-600 font-bold uppercase">
                              ✓ تم التوقيع رقمياً
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {docType === "VISIT_ORDERS" && (
                <div className="flex-1 flex flex-col space-y-6 print:space-y-2 print:space-y-0.5">
                  <h2 className="text-xl print:text-sm font-bold uppercase tracking-tight border-b pb-2 text-indigo-900 border-indigo-200">
                    Visit Orders & Prescriptions
                  </h2>

                  <div className="grid grid-cols-2 gap-8 print:p-2 text-sm print:text-sm print:text-sm">
                    <div>
                      <p className="text-xs print:text-sm text-slate-500 uppercase font-bold tracking-wider mb-1">
                        Patient Subject
                      </p>
                      <p className="font-semibold text-base print:text-sm print:text-[13px]">
                        {displayPatient.name}
                      </p>
                      <p className="text-slate-600">ID: {displayPatient.id}</p>
                      <p className="text-slate-600">
                        DOB: {displayPatient.dob} (
                        {new Date().getFullYear() -
                          new Date(displayPatient.dob).getFullYear()}{" "}
                        yrs)
                      </p>
                    </div>
                    <div>
                      <p className="text-xs print:text-sm text-slate-500 uppercase font-bold tracking-wider mb-1">
                        Encounter / Prescriber
                      </p>
                      <p className="font-semibold text-base print:text-sm print:text-[13px]">
                        Attending: {currentPhysicianEn}
                      </p>
                      <p className="text-slate-600 font-bold text-xs print:text-sm mt-1">
                        Reg. No: 17582
                      </p>
                      <p className="text-slate-600">
                        Facility: Dr. Watheq Al-Chalabi Clinic
                      </p>
                      <p className="text-slate-600 font-mono text-xs print:text-sm mt-1">
                        Date: {printDate}
                      </p>
                    </div>
                  </div>

                  {/* Lab & X-RAY Orders */}
                  {((activeSession?.draftInvestigations &&
                    activeSession.draftInvestigations.length > 0) ||
                    orders.filter(
                      (o) =>
                        o.patientId === displayPatient.id &&
                        (o.department === "LAB" ||
                          o.department === "X_RAY" ||
                          o.department === "NURSING") &&
                        new Date(o.createdAt).getTime() >
                          Date.now() - 24 * 60 * 60 * 1000,
                    ).length > 0) && (
                    <div className="mt-8 print:mt-2 border-2 border-indigo-900 p-6 print:p-2 shadow-sm rounded-xl bg-indigo-50/30">
                      <h3 className="font-bold text-lg print:text-sm text-indigo-900 mb-4 print:mb-1 border-b border-indigo-200 pb-2 uppercase tracking-wide flex items-center gap-2 print:gap-1">
                        <Activity className="w-5 h-5" /> Investigations &
                        Clinical Requests
                      </h3>
                      <div className="space-y-4 print:space-y-0.5">
                        {/* From draft investigations */}
                        {activeSession?.draftInvestigations?.map(
                          (inv: any, idx: number) => (
                            <div
                              key={"draft-inv-" + idx}
                              className="bg-white p-3 print:p-1 border border-indigo-100 rounded-md shadow-sm flex items-center justify-between gap-3 print:p-1"
                            >
                              <div className="flex items-center gap-2 print:gap-1.5">
                                <div className="w-4 h-4 rounded-full bg-indigo-50 border border-indigo-200 flex items-center justify-center text-[9px] font-black text-indigo-700">
                                  ✓
                                </div>
                                <div className="font-bold text-slate-800 text-xs print:text-sm">
                                  {inv.name}
                                </div>
                              </div>
                              <span
                                className={`font-mono text-[8.5px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded border ${
                                  inv.dept === "LAB"
                                    ? "bg-purple-50 text-purple-700 border-purple-200"
                                    : inv.dept === "X_RAY"
                                      ? "bg-sky-50 text-sky-700 border-sky-200"
                                      : "bg-teal-50 text-teal-700 border-teal-200"
                                }`}
                              >
                                {inv.dept}
                              </span>
                            </div>
                          ),
                        )}

                        {/* From dispatched Orders */}
                        {orders
                          .filter(
                            (o) =>
                              o.patientId === displayPatient.id &&
                              (o.department === "LAB" ||
                                o.department === "X_RAY" ||
                                o.department === "NURSING") &&
                              new Date(o.createdAt).getTime() >
                                Date.now() - 24 * 60 * 60 * 1000,
                          )
                          .flatMap((o) => {
                            const lines = o.instruction
                              .split(/\r?\n/)
                              .map((l) => l.trim())
                              .filter(Boolean);
                            return lines.map((line) => ({
                              dept: o.department,
                              text: line
                                .replace(/^\d+[\.\s\-:]+\s*/, "")
                                .replace(/^\[(LAB|X_RAY)\]\s*/i, ""),
                            }));
                          })
                          .map((item, idx) => (
                            <div
                              key={"disp-inv-" + idx}
                              className="bg-white p-3 print:p-1 border border-indigo-100 rounded-md shadow-sm flex items-center justify-between gap-3 print:p-1"
                            >
                              <div className="flex items-center gap-2 print:gap-1.5">
                                <div className="w-4 h-4 rounded-full bg-indigo-50 border border-indigo-200 flex items-center justify-center text-[9px] font-black text-indigo-700">
                                  ✓
                                </div>
                                <div className="font-bold text-slate-800 text-xs print:text-sm">
                                  {item.text}
                                </div>
                              </div>
                              <span
                                className={`font-mono text-[8.5px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded border ${
                                  item.dept === "LAB"
                                    ? "bg-purple-50 text-purple-700 border-purple-200"
                                    : item.dept === "X_RAY"
                                      ? "bg-sky-50 text-sky-700 border-sky-200"
                                      : "bg-teal-50 text-teal-700 border-teal-200"
                                }`}
                              >
                                {item.dept}
                              </span>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}

                  {/* Prescriptions */}
                  {(activeSession?.prescriptions &&
                    activeSession.prescriptions.length > 0) ||
                  orders.filter(
                    (o) =>
                      o.patientId === displayPatient.id &&
                      o.department === "PHARMACY" &&
                      new Date(o.createdAt).getTime() >
                        Date.now() - 24 * 60 * 60 * 1000,
                  ).length > 0 ? (
                    <div className="mt-4 print:mt-1 border-2 border-slate-900 p-4 print:p-2 shadow-sm rounded-lg bg-white">
                      <div className="flex justify-between items-center mb-3 border-b border-slate-300 pb-2">
                        <h3 className="font-extrabold text-2xl font-serif text-slate-900 italic">
                          Rx
                        </h3>
                        <span className="text-[10px] print:text-[11px] font-mono text-slate-500">
                          Ref: {new Date().getTime().toString(36).toUpperCase()}
                        </span>
                      </div>

                      <div className="space-y-2 print:space-y-0.5 text-xs print:text-[11px] font-semibold text-slate-800">
                        {/* From activeSession */}
                        {activeSession?.prescriptions?.map(
                          (rx: any, idx: number) => (
                            <div
                              key={"active-" + idx}
                              className="p-2 border border-slate-200 rounded shadow-sm bg-slate-50"
                            >
                              <div className="text-xs print:text-[11px] font-extrabold text-slate-900 mb-0.5 uppercase tracking-wider">
                                {rx.name}
                              </div>
                              <div className="flex flex-wrap gap-x-4 gap-y-1 text-[10px] print:text-[13px] text-slate-600">
                                {rx.dose && (
                                  <span>
                                    <strong className="text-slate-800">
                                      Dose:
                                    </strong>{" "}
                                    {rx.dose}
                                  </span>
                                )}
                                {rx.frequency && (
                                  <span>
                                    <strong className="text-slate-800">
                                      Freq:
                                    </strong>{" "}
                                    {rx.frequency}
                                  </span>
                                )}
                                {rx.duration && (
                                  <span>
                                    <strong className="text-slate-800">
                                      Duration:
                                    </strong>{" "}
                                    {rx.duration}
                                  </span>
                                )}
                              </div>
                            </div>
                          ),
                        )}
                        {/* From Dispatched Orders if not in active session (to avoid duplication we just show dispatched pharmacy if activeSession prescriptions are empty or if we want to show historical in last 24h) */}
                        {(!activeSession?.prescriptions ||
                          activeSession.prescriptions.length === 0) &&
                          orders
                            .filter(
                              (o) =>
                                o.patientId === displayPatient.id &&
                                o.department === "PHARMACY" &&
                                new Date(o.createdAt).getTime() >
                                  Date.now() - 24 * 60 * 60 * 1000,
                            )
                            .map((o, idx) => (
                              <div
                                key={"dispatched-" + idx}
                                className="p-2 border border-slate-200 rounded shadow-sm bg-slate-50 text-[11px] print:text-[11px]"
                              >
                                <div className="whitespace-pre-wrap">
                                  {o.instruction}
                                </div>
                              </div>
                            ))}
                      </div>

                      <div className="mt-6 print:mt-2 grid grid-cols-2 gap-8 print:p-2 text-[10px] print:text-[11px] font-mono print:break-inside-avoid">
                        <div className="flex flex-col justify-end w-48">
                          <p className="text-[9px] uppercase font-bold text-slate-500 mb-1">
                            Physician Signature / التوقيع
                          </p>
                          {physicianSignature ? (
                            <div className="flex flex-col justify-end text-left mb-1">
                              <img
                                src={physicianSignature}
                                alt="Physician Signature"
                                className="h-[64px] print:h-[54px] w-auto max-w-[150px] object-contain border-b border-black mb-1 mix-blend-multiply opacity-95"
                              />
                            </div>
                          ) : (
                            <div className="mb-1">
                              <SignaturePad
                                label="Draw Signature"
                                onSave={setPhysicianSignature}
                                onClear={() => setPhysicianSignature(null)}
                              />
                              <div className="h-2 border-b border-black w-full mt-1 print:block"></div>
                            </div>
                          )}
                          <p className="font-bold text-[14px] print:text-[14px] text-slate-755">
                            {currentPhysicianEn}
                          </p>
                          <p className="text-slate-400 font-mono text-[8px] uppercase">
                            Registered: 17582
                          </p>
                        </div>
                        <div
                          className="text-right flex flex-col justify-end items-end h-full font-arabic pr-2"
                          dir="rtl"
                          style={{ direction: "rtl" }}
                        >
                          <div className="font-extrabold text-[#0D1B2A] text-[15px] print:text-[16px] font-signature leading-tight">
                            {currentPhysicianAr}
                          </div>
                          <div className="text-[9.5px] font-bold text-slate-755 mt-1">
                            {currentPhysicianTitleAr}
                          </div>
                          <div className="text-[8.5px] text-slate-500 font-mono mt-0.5">
                            رقم التسجيل: {currentRegNo}
                          </div>
                          <p className="text-[8px] text-slate-400 mt-2 font-sans">
                            تاريخ التوثيق: {printDate.split(",")[0]}
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : null}

                  {/* If empty */}
                  {orders.filter(
                    (o) =>
                      o.patientId === displayPatient.id &&
                      new Date(o.createdAt).getTime() >
                        Date.now() - 24 * 60 * 60 * 1000,
                  ).length === 0 &&
                    (!activeSession?.prescriptions ||
                      activeSession.prescriptions.length === 0) && (
                      <div className="text-center py-10 bg-slate-50 rounded text-slate-500 font-bold">
                        No active prescriptions or requests found for today's
                        visit.
                      </div>
                    )}
                </div>
              )}

              {docType === "PATIENT_REPORT" && (
                <div className="space-y-6 print:space-y-2 print:space-y-0.5">
                  <div className="mb-4 print:mb-1 bg-slate-100 p-3 print:p-1 rounded-md border border-slate-200 print:hidden font-sans">
                    <p className="text-xs print:text-sm font-bold text-indigo-900">
                      ✏️ يمكن تحرير محتويات التقرير بالكامل بالتحرير المباشر
                      بفضل خاصية dir="auto". اكتب التوجيهات باللغة العربية
                      للمريض بحسب رغبته.
                    </p>
                    <p className="text-[10px] print:text-[13px] text-slate-600 mt-1">
                      Clinical Editing Enabled: Feel free to click on any report
                      fields to rewrite them in Arabic or English.
                    </p>
                  </div>

                  <input
                    type="text"
                    value={reportTitle}
                    onChange={(e) => {
                      const val = e.target.value;
                      setReportTitle(val);
                      localStorage.setItem(`report_title_${recordKey}`, val);
                    }}
                    dir="auto"
                    className="w-full text-xl print:text-sm font-bold uppercase tracking-tight border-b pb-2 bg-transparent border-dashed border-slate-300 focus:border-indigo-500 focus:outline-none focus:ring-0 print:border-none print:pb-0"
                  />

                  <div className="text-sm print:text-sm print:text-sm space-y-6 print:space-y-2 print:space-y-0.5 leading-relaxed print:leading-tight">
                    <textarea
                      rows={2}
                      value={reportSub}
                      onChange={(e) => {
                        const val = e.target.value;
                        setReportSub(val);
                        localStorage.setItem(`report_sub_${recordKey}`, val);
                      }}
                      dir="auto"
                      className="w-full text-xs print:text-[11px] text-slate-700 bg-transparent border border-dashed border-slate-300 focus:border-indigo-500 focus:outline-none p-1.5 rounded resize-none print:border-none print:p-0"
                    />

                    <div className="bg-slate-50 p-4 print:p-2 border border-slate-200 rounded-lg">
                      <input
                        type="text"
                        value={reportInstructionsHeader}
                        onChange={(e) => {
                          const val = e.target.value;
                          setReportInstructionsHeader(val);
                          localStorage.setItem(
                            `report_instr_hdr_${recordKey}`,
                            val,
                          );
                        }}
                        dir="auto"
                        className="w-full font-bold uppercase tracking-wider text-xs print:text-sm mb-2 bg-transparent border-b border-dashed border-slate-300 focus:border-indigo-500 focus:outline-none print:border-none print:mb-2"
                      />
                      <textarea
                        rows={6}
                        value={reportInstructions}
                        onChange={(e) => {
                          const val = e.target.value;
                          setReportInstructions(val);
                          localStorage.setItem(
                            `report_instr_${recordKey}`,
                            val,
                          );
                        }}
                        dir="auto"
                        className="w-full text-sm print:text-[12px] font-semibold leading-relaxed print:leading-tight bg-white border border-dashed border-indigo-200 rounded p-2 focus:border-indigo-500 focus:outline-none print:border-none print:p-0 print:bg-transparent"
                        placeholder="اكتب التوجيهات الطبية والخطة العلاجية باللغة العربية هنا..."
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4 print:p-2">
                      <LabTrendChart
                        orders={orders}
                        metricName="HbA1c (%)"
                        regex={/(?:HbA1c|A1C)[^\d]*([\d.]+)/i}
                        color="#f43f5e"
                      />
                      <LabTrendChart
                        orders={orders}
                        metricName="Cholesterol (mg/dL)"
                        regex={/(?:Cholesterol|CHOL)[^\d]*([\d.]+)/i}
                        color="#0ea5e9"
                      />
                    </div>

                    <div className="mt-8 print:mt-2 grid grid-cols-2 gap-8 print:p-2 text-xs print:text-sm font-mono border-t border-slate-200 pt-6 print:break-inside-avoid">
                      <div className="flex flex-col justify-end">
                        <p className="text-[9.5px] text-slate-500 uppercase font-extrabold tracking-widest mb-1.5">
                          Physician Signature / التوقيع والختم
                        </p>
                        {physicianSignature ? (
                          <div className="flex flex-col justify-end">
                            <img
                              src={physicianSignature}
                              alt="Physician Signature"
                              className="h-[64px] print:h-[54px] w-auto max-w-[150px] object-contain border-b border-black mb-1 mix-blend-multiply opacity-95"
                            />
                          </div>
                        ) : (
                          <div className="mb-2 w-full">
                            <SignaturePad
                              label="Physician Signature"
                              onSave={setPhysicianSignature}
                              onClear={() => setPhysicianSignature(null)}
                            />
                            <div className="border-b border-black h-4 mb-1"></div>
                          </div>
                        )}
                        <p className="font-bold text-slate-600">
                          Attending Physician: {currentPhysicianEn}
                        </p>
                        <p className="text-slate-400 mt-0.5 font-mono text-[9px]">
                          IQ-Doctor Secure Clinical Report • Validated
                        </p>
                      </div>
                      <div
                        className="text-right flex flex-col justify-end items-end h-full font-arabic pr-2"
                        dir="rtl"
                        style={{ direction: "rtl" }}
                      >
                        <div className="font-extrabold text-[#0D1B2A] text-[15px] print:text-[16px] font-signature tracking-tight leading-normal print:leading-tight">
                          {currentPhysicianAr}
                        </div>
                        <div className="text-[9.5px] font-semibold text-slate-700 mt-1">
                          {currentPhysicianTitleAr}
                        </div>
                        <div className="text-[8.5px] text-slate-500 font-mono mt-0.5">
                          رقم التسجيل: {currentRegNo}
                        </div>
                        <p className="text-[10px] print:text-[11px] text-slate-400 mt-3 font-sans">
                          تاريخ التحرير: {printDate.split(",")[0]} • بغداد،
                          العراق
                        </p>
                      </div>
                    </div>

                    <div className="mt-12 print:mt-2 text-xs print:text-sm font-mono text-center text-slate-500">
                      <p>*** END OF PATIENT REPORT ***</p>
                      <p>Please bring this report to your next visit.</p>
                    </div>
                  </div>
                </div>
              )}

              {docType === "PRESCRIPTION" && (
                <div className="space-y-4 print:space-y-0.5 font-sans">
                  {/* Prescription Header and Watermarked Rx icon */}
                  <div className="flex justify-between items-center border-b-2 border-slate-900 pb-2">
                    <div className="flex items-center gap-3 print:p-1">
                      <span className="font-serif font-black text-5xl text-slate-900 italic tracking-tighter select-none leading-none">
                        Rₓ
                      </span>
                      <div>
                        <h2 className="font-serif font-extrabold text-xs print:text-sm uppercase tracking-wider text-slate-900 leading-tight">
                          Official Out-Patient Prescription
                        </h2>
                        <p className="text-[9px] text-slate-500 font-bold uppercase tracking-widest mt-0.5">
                          Authorized for Dispensation at Any External Pharmacy
                        </p>
                      </div>
                    </div>
                    <div className="text-right text-[9px] font-mono leading-tight">
                      <p className="font-bold text-slate-900">
                        Rx Serial No:{" "}
                        <span className="text-indigo-700 bg-indigo-50 border border-indigo-200 px-1.5 py-0.5 rounded text-[10px] print:text-[11px] font-black uppercase font-sans">
                          {new Date().getTime().toString(36).toUpperCase()}-
                          {displayPatient.id.substring(0, 4).toUpperCase()}
                        </span>
                      </p>
                      <p className="text-slate-500 mt-1">
                        Status: Certified Electronic-Rx
                      </p>
                    </div>
                  </div>

                  {/* Prescription Body Table */}
                  <div className="border border-black overflow-hidden mt-1 bg-white">
                    <table className="w-full text-left text-[10px] print:text-[11px] border-collapse font-sans">
                      <thead className="border-b-2 border-black text-black uppercase tracking-wider text-[8px] font-bold">
                        <tr>
                          <th className="py-2 px-2 text-center w-8 border-r border-black">
                            Item
                          </th>
                          <th className="py-2 px-2 border-r border-black">
                            Therapeutic Formulation & Strength
                          </th>
                          <th className="py-2 px-2 text-center w-24 border-r border-black">
                            Dosage Regimen
                          </th>
                          <th className="py-2 px-2 text-center w-32 border-r border-black">
                            Frequency / Intake timing
                          </th>
                          <th className="py-2 px-2 text-center w-20">
                            Duration
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-black font-medium text-black">
                        {(() => {
                          let parsedRxList: Array<{
                            name: string;
                            dose?: string;
                            frequency?: string;
                            duration?: string;
                          }> = [];
                          
                          if (selectedOrderToPrint && docType === "PRESCRIPTION") {
                            const lines = selectedOrderToPrint.instruction.split("\n");
                            parsedRxList = lines.map(line => {
                               let namePart = line;
                               let dosePart = "As directed";
                               let freqPart = "As directed";
                               let durPart = "As directed";
                               
                               const parts = line.split("|");
                               if (parts.length > 0) {
                                  namePart = parts[0].trim().replace(/^\d+\.\s*/, '');
                               }
                               if (parts.length > 1) dosePart = parts[1].replace("Dose:", "").trim();
                               if (parts.length > 2) freqPart = parts[2].replace("Freq:", "").trim();
                               if (parts.length > 3) durPart = parts[3].replace("Dur:", "").trim();
                               
                               return {
                                   name: namePart,
                                   dose: dosePart,
                                   frequency: freqPart,
                                   duration: durPart
                               };
                            }).filter(rx => rx.name.length > 0);
                          } else if (
                            activeSession &&
                            activeSession.prescriptions &&
                            activeSession.prescriptions.length > 0
                          ) {
                            parsedRxList = activeSession.prescriptions;
                          }

                          // Smart Fallback: If the doctor just saved the record (which clears active session) and then clicked Print,
                          // we should automatically pull the latest saved prescription from the clinical records history.
                          if (parsedRxList.length === 0 && clinicalRecords && clinicalRecords.length > 0) {
                            const lastRecordWithRx = clinicalRecords.find((r: any) => r.prescriptions && typeof r.prescriptions === 'string' && r.prescriptions.length > 5);
                            if (lastRecordWithRx) {
                              try {
                                parsedRxList = JSON.parse(lastRecordWithRx.prescriptions);
                              } catch (e) {
                                console.error("Failed to parse historical prescriptions for print fallback", e);
                              }
                            }
                          }

                          if (parsedRxList.length === 0) {
                            return (
                              <tr>
                                <td
                                  colSpan={5}
                                  className="py-4 print:py-1 text-center text-black italic"
                                >
                                  No prescription drug specifications logged.
                                </td>
                              </tr>
                            );
                          }

                          return parsedRxList.map((rx, idx) => (
                            <tr key={idx} className="bg-white">
                              <td className="py-2 px-2 text-center font-bold text-black border-r border-black align-top text-[10px] print:text-[13px]">
                                {idx + 1}
                              </td>
                              <td className="py-2 px-2 border-r border-black align-top">
                                <span className="font-extrabold text-black block text-[10px] print:text-[11px] uppercase tracking-wider">
                                  {rx.name}
                                </span>
                                <span className="text-[8px] text-black font-semibold uppercase tracking-wider block mt-0.5">
                                  Rₓ Dispensation Class A
                                </span>
                              </td>
                              <td className="py-2 px-2 text-center font-semibold text-black border-r border-black align-top text-[11px] print:text-[9.5px]">
                                {rx.dose || "—"}
                              </td>
                              <td className="py-2 px-2 text-center font-semibold text-black border-r border-black align-top text-[11px] print:text-[9.5px]">
                                {rx.frequency || "—"}
                              </td>
                              <td className="py-2 px-2 text-center font-semibold text-black font-mono text-[11px] print:text-[9.5px] align-top">
                                {rx.duration || "—"}
                              </td>
                            </tr>
                          ));
                        })()}
                      </tbody>
                    </table>
                  </div>

                  {/* Special Pharmacist Dispensing Directives */}
                  <div className="border border-slate-200 rounded p-2 bg-slate-50/50 text-[9px] leading-relaxed print:leading-tight text-slate-700 mt-2">
                    <span className="font-extrabold uppercase tracking-wider text-slate-900 text-[8px] block mb-0.5">
                      Pharmacist Dispensing & Substitution Directives:
                    </span>
                    <p>
                      1. Generic substitution is permitted unless explicitly
                      denoted "Dispense as Written" by the prescribing
                      physician.
                    </p>
                    <p>
                      2. Verify patient credentials via the QR code printed in
                      the document head prior to dispensing.
                    </p>
                    <p>
                      3. Return verification callback records to IQ-Doctor
                      Clinical Core terminal if discrepancy occurs.
                    </p>
                  </div>

                  {/* Legal Validation Stamp and Authorized Signatures */}
                  <div className="mt-4 print:mt-1 pt-4 print:pt-1 border-t border-slate-300 grid grid-cols-3 gap-6 print:p-2 items-end text-xs print:break-inside-avoid">
                    <div className="flex flex-col justify-end h-full">
                    </div>

                    <div className="flex flex-col items-center justify-end h-full text-center">
                      <p className="text-[8.5px] text-slate-500 uppercase font-extrabold tracking-widest mb-1.5">
                        Physician Signature
                      </p>
                      {physicianSignature ? (
                        <div className="flex flex-col justify-end text-center w-full mb-1">
                          <img
                            src={physicianSignature}
                            alt="Physician Signature"
                            className="h-[64px] print:h-[54px] w-auto max-w-[150px] object-contain border-b border-black mb-1 mix-blend-multiply opacity-95"
                          />
                        </div>
                      ) : (
                        <div className="mb-1 text-center w-full">
                          <SignaturePad
                            label="Draw Signature"
                            onSave={setPhysicianSignature}
                            onClear={() => setPhysicianSignature(null)}
                          />
                          <div className="h-4 border-b border-slate-800 w-full mt-1 print:block"></div>
                        </div>
                      )}
                      <p className="text-[8.5px] text-slate-500 font-mono uppercase mt-1">
                        Digitally Witnessed & Locked
                      </p>
                    </div>

                    <div
                      className="text-right flex flex-col justify-end items-end h-full font-arabic pr-2"
                      dir="rtl"
                      style={{ direction: "rtl" }}
                    >
                      <div className="font-extrabold text-[#0F172A] text-[15px] print:text-[16px] font-signature tracking-tight leading-normal print:leading-tight">
                        {currentPhysicianAr}
                      </div>
                      <div className="text-[9.5px] font-bold text-slate-705 mt-1">
                        {currentPhysicianTitleAr}
                      </div>
                      <div className="text-[8.5px] text-slate-500 font-mono mt-0.5">
                        رقم التسجيل: {currentRegNo}
                      </div>
                      <div className="clear-both space-y-0 text-[8.5px] text-slate-500 uppercase font-semibold mt-2 font-mono text-right">
                        <p className="text-[#0f172a] font-extrabold text-[9px]">
                          Reg Clinician: {currentRegNo}
                        </p>
                        <p>Baghdad Clinical Node</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {docType === "SICK_LEAVE" && (
                <div className="space-y-4 print:space-y-2 font-sans mb-12">
                  <div className="flex justify-between items-center border-b-2 border-slate-900 pb-2">
                    <div className="flex items-center gap-3 print:p-1">
                      <span className="font-serif font-black text-4xl text-slate-900 italic tracking-tighter select-none leading-none">
                        SL
                      </span>
                      <div>
                        <h2 className="font-serif font-extrabold text-lg print:text-xl tracking-wider text-slate-900 leading-tight">
                          إجازة مرضية رسمية
                        </h2>
                        <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest mt-0.5">
                          Official Sick Leave Certificate
                        </p>
                      </div>
                    </div>
                    <div className="text-right text-[10px] font-mono leading-tight">
                      <p className="font-bold text-slate-900">
                        Cert No:{" "}
                        <span className="text-indigo-700 bg-indigo-50 border border-indigo-200 px-1.5 py-0.5 rounded text-[10px] print:text-[11px] font-black uppercase font-sans">
                          {new Date().getTime().toString(36).toUpperCase()}-
                          {displayPatient.id.substring(0, 4).toUpperCase()}
                        </span>
                      </p>
                    </div>
                  </div>

                  <div
                    className="my-6 border border-slate-300 p-6 rounded-lg text-right font-arabic"
                    dir="rtl"
                  >
                    <p className="text-lg print:text-sm mb-4 text-slate-800 leading-loose print:leading-normal">
                      إلى من يهمه الأمر،
                    </p>
                    <p className="text-xl print:text-base leading-loose print:leading-relaxed font-medium text-slate-800">
                      نؤيد بأن المريض/ـة{" "}
                      <strong className="font-extrabold pb-1 border-b-2 border-slate-400">
                        {displayPatient.name}
                      </strong>
                      البالغ من العمر{" "}
                      <strong className="font-extrabold">
                        {displayPatient.age} سنة
                      </strong>{" "}
                      قد راجع عيادتنا بتاريخ{" "}
                      <strong>{printDate.split(",")[0]}</strong>.
                    </p>
                    <div className="mt-4 flex flex-col gap-2">
                      <p className="text-lg print:text-sm text-slate-800">
                        التشخيص (Diagnosis):{" "}
                        <strong className="font-bold">
                          {activeSession?.soapAssessment || "ــــــــــ"}
                        </strong>
                      </p>
                    </div>
                    <div className="mt-6 p-4 bg-slate-50 border border-slate-200 rounded-md">
                      <p className="text-lg print:text-sm text-slate-800 font-bold flex items-center justify-start gap-2">
                        يحتاج المريض إلى فترة راحة وعلاج (إجازة مرضية) لمدة:
                        <input
                          type="number"
                          min="1"
                          max="30"
                          value={sickLeaveDays}
                          onChange={(e) =>
                            setSickLeaveDays(parseInt(e.target.value) || 1)
                          }
                          className="w-16 border border-slate-300 rounded px-2 py-1 text-center font-mono text-lg mx-2 text-indigo-700 font-black no-print"
                        />
                        <span className="hidden print:inline px-4 border-b-2 border-dashed border-slate-800 font-black font-mono text-xl print:text-lg">
                          {sickLeaveDays}
                        </span>
                        يوم / أيام.
                      </p>
                      <p className="text-sm print:text-xs text-slate-500 mt-2">
                        ابتداءً من تاريخ التحرير أعلاه.
                      </p>
                    </div>
                  </div>

                  {/* Legal Validation Stamp and Authorized Signatures */}
                  <div className="mt-8 print:mt-4 pt-4 print:pt-1 border-t border-slate-300 grid grid-cols-3 gap-6 print:p-2 items-end text-xs print:break-inside-avoid">
                    <div className="flex flex-col justify-end h-full">
                    </div>

                    <div className="flex flex-col items-center justify-end h-full text-center">
                      <p className="text-[8.5px] text-slate-500 uppercase font-extrabold tracking-widest mb-1.5">
                        Physician Signature
                      </p>
                      {physicianSignature ? (
                        <div className="flex flex-col justify-end text-center w-full mb-1">
                          <img
                            src={physicianSignature}
                            alt="Physician Signature"
                            className="h-[64px] print:h-[54px] w-auto max-w-[150px] object-contain border-b border-black mb-1 mix-blend-multiply opacity-95"
                          />
                        </div>
                      ) : (
                        <div className="mb-1 text-center w-full">
                          <SignaturePad
                            label="Draw Signature"
                            onSave={setPhysicianSignature}
                            onClear={() => setPhysicianSignature(null)}
                          />
                          <div className="h-4 border-b border-slate-800 w-full mt-1 print:block"></div>
                        </div>
                      )}
                      <p className="text-[8.5px] text-slate-500 font-mono uppercase mt-1">
                        Digitally Witnessed & Locked
                      </p>
                    </div>

                    <div
                      className="text-right flex flex-col justify-end items-end h-full font-arabic pr-2"
                      dir="rtl"
                      style={{ direction: "rtl" }}
                    >
                      <div className="font-extrabold text-[#0F172A] text-[15px] print:text-[16px] font-signature tracking-tight leading-normal print:leading-tight">
                        {currentPhysicianAr}
                      </div>
                      <div className="text-[9.5px] font-bold text-slate-705 mt-1">
                        {currentPhysicianTitleAr}
                      </div>
                      <div className="text-[8.5px] text-slate-500 font-mono mt-0.5">
                        رقم التسجيل: {currentRegNo}
                      </div>
                      <div className="clear-both space-y-0 text-[8.5px] text-slate-500 uppercase font-semibold mt-2 font-mono text-right">
                        <p className="text-[#0f172a] font-extrabold text-[9px]">
                          Reg Clinician: {currentRegNo}
                        </p>
                        <p>Baghdad Clinical Node</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {docType === "INVESTIGATION" &&
                (() => {
                  // Clinical Security & Fallback Logic: Determine values to print securely
                  let department = "LAB/X-RAY";
                  let printId =
                    "REQ-" + new Date().getTime().toString(36).toUpperCase();
                  let specimenType = "Blood Sample / Imaging Scan";
                  let instructions = "";
                  let timeString = new Date().toLocaleTimeString();

                  if (selectedOrderToPrint) {
                    department = selectedOrderToPrint.department;
                    printId = selectedOrderToPrint.id
                      .substring(0, 10)
                      .toUpperCase();
                    specimenType =
                      selectedOrderToPrint.department === "LAB"
                        ? "Venous Blood / Urine Specimen"
                        : "General Radiology Imaging";
                    instructions = selectedOrderToPrint.instruction;
                    timeString = new Date(
                      selectedOrderToPrint.createdAt,
                    ).toLocaleTimeString();
                  } else {
                    // Compile clinical inputs from active session details or previous 24h orders safely to avoid gaps
                    const draftAndDispatchedList: string[] = [];
                    if (
                      activeSession &&
                      activeSession.draftInvestigations &&
                      activeSession.draftInvestigations.length > 0
                    ) {
                      activeSession.draftInvestigations.forEach((inv: any) => {
                        draftAndDispatchedList.push(
                          `[${inv.dept}] ${inv.name}`,
                        );
                      });
                    } else if (orders) {
                      const todayOrders = orders.filter(
                        (o) =>
                          o.patientId === displayPatient.id &&
                          (o.department === "LAB" ||
                            o.department === "X_RAY" ||
                            o.department === "NURSING") &&
                          new Date(o.createdAt).getTime() >
                            Date.now() - 24 * 60 * 60 * 1000,
                      );
                      todayOrders.forEach((o) => {
                        draftAndDispatchedList.push(
                          `[${o.department}] ${o.instruction}`,
                        );
                      });
                    }

                    if (draftAndDispatchedList.length > 0) {
                      instructions = draftAndDispatchedList.join("\n");
                    } else {
                      instructions =
                        "No investigation orders drafted or dispatched for this session.";
                    }
                  }

                  return (
                    <div className="space-y-6 print:space-y-2 print:space-y-0.5 font-sans">
                      {/* Investigation Header */}
                      <div className="flex justify-between items-center border-b-2 border-slate-900 pb-3">
                        <div className="flex items-center gap-3 print:p-1">
                          <div className="p-2.5 bg-white text-slate-900 border border-slate-900 rounded-lg shadow-sm">
                            <svg
                              className="w-6 h-6"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"
                              />
                            </svg>
                          </div>
                          <div>
                            <h2 className="font-serif font-extrabold text-sm print:text-sm print:text-sm uppercase tracking-wider text-slate-900 leading-tight">
                              Diagnostic Investigation Requisition
                            </h2>
                            <p className="text-[9.5px] text-slate-500 font-bold uppercase tracking-widest mt-0.5">
                              Clinical Referrals, Laboratories, & Radiographic
                              Center Requisitions
                            </p>
                          </div>
                        </div>
                        <div className="text-right text-[10px] print:text-[11px] font-mono leading-tight">
                          <p className="font-bold text-slate-900">
                            Requisition ID:{" "}
                            <span className="text-indigo-700 bg-indigo-50 border border-indigo-200 px-1.5 py-0.5 rounded text-[11px] print:text-sm font-black uppercase font-sans">
                              {printId}
                            </span>
                          </p>
                        </div>
                      </div>

                      {/* Ordered Analytical Panels / Tests Request Card */}
                      <div className="border border-black mt-2 bg-white flex-1 min-h-[400px]">
                        <div className="flex items-center justify-between border-b border-black p-3 print:p-1 bg-white">
                          <h4 className="text-[10px] print:text-[11px] uppercase text-black font-extrabold tracking-widest">
                            Requested Analytical & Examination Panels
                          </h4>
                          <span className="text-[10px] print:text-[11px] text-black font-mono">
                            Clinical Order Line
                          </span>
                        </div>

                        <div className="bg-white divide-y divide-black divide-dashed">
                          {(() => {
                            const lines = instructions
                              .split(/\r?\n/)
                              .map((line) => line.trim())
                              .filter(Boolean);

                            if (lines.length === 0) {
                              return (
                                <div className="p-4 print:p-2 text-black italic text-center text-xs print:text-sm">
                                  No active investigations found.
                                </div>
                              );
                            }

                            return lines.map((line, idx) => {
                              const cleanedText = line
                                .replace(/^\d+[\.\s\-:]+\s*/, "")
                                .replace(/^\[(LAB|X_RAY)\]\s*/i, "");

                              let lineDept = department;
                              if (
                                line.toUpperCase().includes("[LAB]") ||
                                line.toUpperCase().startsWith("LAB/")
                              ) {
                                lineDept = "LAB";
                              } else if (
                                line.toUpperCase().includes("[X_RAY]") ||
                                line.toUpperCase().startsWith("X_RAY/")
                              ) {
                                lineDept = "X_RAY";
                              }

                              return (
                                <div
                                  key={idx}
                                  className="p-3 print:p-1 flex items-center justify-between gap-4 print:p-2"
                                >
                                  <div className="flex items-center gap-3 print:p-1">
                                    <div className="w-4 h-4 rounded border border-black flex items-center justify-center font-bold text-[9px] text-black shrink-0 select-none">
                                      {idx + 1}
                                    </div>
                                    <span className="text-[11.5px] font-extrabold text-black uppercase tracking-wider">
                                      {cleanedText}
                                    </span>
                                  </div>
                                </div>
                              );
                            });
                          })()}
                        </div>
                      </div>

                      {/* Footnotes and Barcode */}
                      <div className="mt-8 print:mt-2 pt-6 print:pt-2 border-t border-black grid grid-cols-2 gap-8 print:p-2 items-start print:break-inside-avoid">
                        <div className="flex flex-col items-start text-xs print:text-sm text-black">
                          <p className="text-[9px] uppercase font-bold text-black mb-1.5">
                            Authorized Signature
                          </p>
                          {physicianSignature ? (
                            <div className="flex flex-col justify-end text-left w-48 mb-2">
                              <img
                                src={physicianSignature}
                                alt="Physician Signature"
                                className="h-[64px] print:h-[54px] w-auto max-w-[150px] object-contain border-b border-black mb-1 mix-blend-multiply opacity-95"
                              />
                            </div>
                          ) : (
                            <div className="mb-2 w-48">
                              <SignaturePad
                                label="Draw Signature"
                                onSave={setPhysicianSignature}
                                onClear={() => setPhysicianSignature(null)}
                              />
                              <div className="h-6 border-b border-black w-full mt-2 print:block"></div>
                            </div>
                          )}
                          <p className="font-extrabold text-black text-[10px] print:text-[11px] uppercase mt-1 print:block">
                            Attending Clinician: {currentPhysicianEn}
                          </p>
                          <p className="print:block">
                            Registered No: <strong>{currentRegNo}</strong>
                          </p>
                          <p className="text-[9.5px] text-slate-700 print:block">
                            Recorded: {timeString}
                          </p>
                        </div>

                        <div className="flex flex-col items-end justify-between h-full text-right">
                          <div
                            className="text-right flex flex-col justify-end items-end font-arabic"
                            dir="rtl"
                            style={{ direction: "rtl" }}
                          >
                            <div className="font-extrabold text-black text-[15px] print:text-[16px] font-signature tracking-tight leading-normal print:leading-tight">
                              {currentPhysicianAr}
                            </div>
                            <div className="text-[9.5px] font-bold text-slate-800 mt-1">
                              {currentPhysicianTitleAr}
                            </div>
                            <div className="text-[8.5px] text-slate-500 font-mono mt-0.5">
                              رقم التسجيل: {currentRegNo}
                            </div>
                            <p className="text-[9.5px] text-slate-500 mt-2 font-sans">
                              رمز التدقيق والتحقق للأشعة والتحاليل الطبية
                            </p>
                          </div>

                          <div className="text-right border border-black bg-white p-2 flex flex-col items-end mt-4 print:mt-1 w-44">
                            <p className="text-[8px] uppercase font-bold text-black border-b border-black pb-0.5 mb-1 tracking-widest">
                              IQ-Doctor Secure Token
                            </p>
                            <div className="font-mono text-[11px] print:text-sm font-black text-black tracking-wider">
                              *{printId}*
                            </div>
                            <span className="text-[7.5px] font-mono text-black mt-0.5">
                              EHR INTEGRITY SECURED
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()}

              {docType.startsWith("LEAFLET") && (
                <div className="space-y-6 print:space-y-2 print:space-y-0.5">
                  <h2 className="text-xl print:text-sm font-bold uppercase tracking-tight border-b pb-2">
                    Patient Information Leaflet
                  </h2>

                  {docType === "LEAFLET_CUSTOM" && (
                    <div
                      className="text-sm print:text-sm print:text-sm space-y-4 print:space-y-0.5 leading-relaxed print:leading-tight prose prose-slate max-w-none font-sans"
                      dir="rtl"
                    >
                      <Markdown>
                        {activeSession?.customLeafletResult || ""}
                      </Markdown>
                    </div>
                  )}

                  {docType === "LEAFLET_ACNE" && (
                    <div
                      className="text-sm print:text-sm print:text-sm space-y-4 print:space-y-0.5 leading-relaxed print:leading-tight"
                      dir="rtl"
                    >
                      <h3 className="font-bold text-lg print:text-sm mb-4 print:mb-1 text-emerald-800">
                        حب الشباب (Acne)
                      </h3>
                      <p>
                        هذه المعلومات تقدم لك لمساعدتك على فهم حب الشباب. هذا
                        الكتيب يخبرك ما هو حب الشباب، أسبابه، وماذا يمكنك أن
                        تفعل حياله، وأين يمكنك معرفة المزيد من المعلومات.
                      </p>
                      <h4 className="font-bold mt-4 print:mt-1">
                        ما هو حب الشباب؟
                      </h4>
                      <p>
                        حب الشباب هو سبب شائع جداً لظهور البقع على الجلد. يصيب
                        الغدد الدهنية وبصيلات الشعر. وهو يؤثر غالباً على الوجه
                        والصدر والظهر.
                      </p>
                      <h4 className="font-bold mt-4 print:mt-1">
                        نصائح هامة للعناية اليومية:
                      </h4>
                      <ul className="list-disc pr-6 space-y-2 print:space-y-0.5">
                        <li>
                          تجنب غسل الوجه بالصابون القاسي بكثرة (مرتين يوميا
                          يكفي).
                        </li>
                        <li>
                          لا تعصر أو تقشر البثور، حيث يمكن أن يؤدي ذلك إلى تندب
                          دائم.
                        </li>
                        <li>
                          تأكد من أن جميع مستحضرات التجميل والمرطبات غير مسببة
                          للرؤوس السوداء (Non-comedogenic).
                        </li>
                        <li>
                          قد تستغرق العلاجات الطبية من 6 إلى 8 أسابيع لتظهر
                          فعاليتها التامة، لذا يرجى التحلي بالصبر.
                        </li>
                      </ul>
                      <p
                        className="text-xs print:text-sm text-slate-500 mt-6 print:mt-2 pt-4 print:pt-1 border-t border-slate-200 text-left font-sans"
                        dir="ltr"
                      >
                        Information officially derived from British Association
                        of Dermatologists (BAD) - Clinical Patient Guidelines.
                      </p>
                      <div className="mt-8 print:mt-2 pt-4 print:pt-1 border-t border-slate-100 flex justify-end">
                        <div className="text-right flex flex-col justify-end items-end font-arabic">
                          <p
                            className="text-[10px] print:text-[11px] font-bold text-slate-800 font-signature"
                            dir="rtl"
                          >
                            تمت مراجعته وتدقيقه بواسطة: {currentPhysicianAr}
                          </p>
                          <p className="text-[9px] text-slate-500 font-sans mt-0.5">
                            Clinical Leaflet Validated • Dr. Watheq Al-Chalabi Clinic
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {docType === "LEAFLET_ECZEMA" && (
                    <div
                      className="text-sm print:text-sm print:text-sm space-y-4 print:space-y-0.5 leading-relaxed print:leading-tight"
                      dir="rtl"
                    >
                      <h3 className="font-bold text-lg print:text-sm mb-4 print:mb-1 text-emerald-800">
                        الإكزيما التأتبية (Atopic Eczema)
                      </h3>
                      <p>
                        هذا الكتيب يخبرك ما هي الإكزيما التأتبية، أسبابها، وماذا
                        يمكنك أن تفعل حيالها.
                      </p>
                      <h4 className="font-bold mt-4 print:mt-1">
                        ما هي الإكزيما؟
                      </h4>
                      <p>
                        الإكزيما التأتبية (تسمى أيضاً التهاب الجلد التأتبی) هي
                        حالة جلدية شائعة جداً، تؤدي إلى جفاف الجلد، احمراره،
                        والتهابه وحكة شديدة.
                      </p>
                      <h4 className="font-bold mt-4 print:mt-1">
                        العناية اليومية:
                      </h4>
                      <ul className="list-disc pr-6 space-y-2 print:space-y-0.5">
                        <li>
                          المرطبات (المطريات): هي الأهم! استخدمها بكثرة كل يوم
                          حتى لو كان الجلد يبدو طبيعياً.
                        </li>
                        <li>
                          الاستحمام: استخدم بدائل الصابون (الغسولات المطرية)
                          وتجنب الماء شديد الحرارة.
                        </li>
                        <li>
                          الكورتيكوستيرويدات الموضعية: استخدمها بانتظام حسب
                          إرشادات الطبيب (فقط على المناطق الملتهبة).
                        </li>
                        <li>
                          تجنب المهيجات مثل الملابس الصوفية الخشنة، والعطور
                          القوية.
                        </li>
                      </ul>
                      <p
                        className="text-xs print:text-sm text-slate-500 mt-6 print:mt-2 pt-4 print:pt-1 border-t border-slate-200 text-left font-sans"
                        dir="ltr"
                      >
                        Information officially derived from British Association
                        of Dermatologists (BAD) - Clinical Patient Guidelines.
                      </p>
                      <div className="mt-8 print:mt-2 pt-4 print:pt-1 border-t border-slate-100 flex justify-end">
                        <div className="text-right">
                          <p className="text-[10px] print:text-[11px] font-bold text-slate-800 font-arabic">
                            تمت مراجعته وتدقيقه بواسطة: {currentPhysicianAr}
                          </p>
                          <p className="text-[9px] text-slate-500">
                            Clinical Leaflet Validated • Dr. Watheq Al-Chalabi Clinic
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {docType === "LEAFLET_PSORIASIS" && (
                    <div
                      className="text-sm print:text-sm print:text-sm space-y-4 print:space-y-0.5 leading-relaxed print:leading-tight"
                      dir="rtl"
                    >
                      <h3 className="font-bold text-lg print:text-sm mb-4 print:mb-1 text-emerald-800">
                        الصدفية (Psoriasis)
                      </h3>
                      <p>
                        هذه النشرة للمرضى وأقاربهم للمساعدة على فهم مرض الصدفية.
                      </p>
                      <h4 className="font-bold mt-4 print:mt-1">
                        ما هي الصدفية؟
                      </h4>
                      <p>
                        الصدفية هي حالة شائعة تسبب بقعا (لويحات) حمراء وقشرية
                        على الجلد. إنها ليست معدية. معدل تجدد خلايا الجلد يكون
                        أسرع بكثير من الطبيعي.
                      </p>
                      <h4 className="font-bold mt-4 print:mt-1">
                        إرشادات هامة:
                      </h4>
                      <ul className="list-disc pr-6 space-y-2 print:space-y-0.5">
                        <li>
                          تجنب خدش اللويحات حيث قد يؤدي إلى تفاقم الحالة (ظاهرة
                          كوبنر).
                        </li>
                        <li>
                          تجنب التوتر والإجهاد العاطفي لأنه قد يهيج الحالة.
                        </li>
                        <li>
                          العلاجات الموضعية (مثل نظائر فيتامين د وعلاجات
                          الكورتيزون) تتطلب الالتزام المستمر بها.
                        </li>
                        <li>
                          يُنصح بالتعرض المعتدل لأشعة الشمس المفيدة للصدفية دون
                          التعرض للحروق.
                        </li>
                      </ul>
                      <p
                        className="text-xs print:text-sm text-slate-500 mt-6 print:mt-2 pt-4 print:pt-1 border-t border-slate-200 text-left font-sans"
                        dir="ltr"
                      >
                        Information officially derived from British Association
                        of Dermatologists (BAD) - Clinical Patient Guidelines.
                      </p>
                      <div className="mt-8 print:mt-2 pt-4 print:pt-1 border-t border-slate-100 flex justify-end">
                        <div className="text-right">
                          <p className="text-[10px] print:text-[11px] font-bold text-slate-800 font-arabic">
                            تمت مراجعته وتدقيقه بواسطة: {currentPhysicianAr}
                          </p>
                          <p className="text-[9px] text-slate-500">
                            Clinical Leaflet Validated • Dr. Watheq Al-Chalabi Clinic
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {docType === "MEDICATION_PROFILE" && (
                <div className="flex-1 flex flex-col space-y-6 print:space-y-2 print:space-y-0.5">
                  <h2 className="text-xl print:text-sm font-bold uppercase tracking-tight border-b pb-2 text-indigo-900 border-indigo-200">
                    Historical Medication Profile
                  </h2>

                  <div className="text-sm print:text-sm print:text-sm space-y-4 print:space-y-0.5">
                    <div className="bg-slate-50 p-4 print:p-2 border border-slate-200 rounded-lg">
                      <h3 className="font-bold text-slate-800 border-b border-slate-200 pb-2 mb-3 uppercase tracking-wider text-xs print:text-sm">
                        Patient Details
                      </h3>
                      <div className="grid grid-cols-2 gap-4 print:p-2">
                        <div>
                          <span className="text-slate-500">Name:</span>{" "}
                          {displayPatient.name}
                        </div>
                        <div>
                          <span className="text-slate-500">Patient ID:</span>{" "}
                          {displayPatient.id}
                        </div>
                        <div>
                          <span className="text-slate-500">Date of Birth:</span>{" "}
                          {displayPatient.dob ? new Date(displayPatient.dob).toLocaleDateString("en-GB") : ""}
                        </div>
                      </div>
                    </div>

                    <h3 className="font-bold text-slate-800 uppercase tracking-wider text-xs print:text-sm mt-6 print:mt-2 mb-2">
                      Prescription Ledger
                    </h3>

                    {(() => {
                      const prescriptionsFromRecords = clinicalRecords
                        .filter((r) => r.prescriptions)
                        .map((r) => {
                          try {
                            return {
                              date: r.createdAt,
                              physicianId: r.physicianId,
                              physicianName: r.physicianName,
                              meds: JSON.parse(r.prescriptions as string),
                            };
                          } catch {
                            return {
                              date: r.createdAt,
                              physicianId: r.physicianId,
                              physicianName: r.physicianName,
                              meds: [],
                            };
                          }
                        })
                        .filter((item) => item.meds && item.meds.length > 0)
                        .sort(
                          (a, b) =>
                            new Date(b.date).getTime() -
                            new Date(a.date).getTime(),
                        );

                      if (prescriptionsFromRecords.length === 0) {
                        return (
                          <p className="text-slate-500 italic text-xs print:text-sm">
                            No historical medications found in EHR.
                          </p>
                        );
                      }

                      return (
                        <div className="space-y-4 print:space-y-0.5">
                          {prescriptionsFromRecords.map((record, idx) => (
                            <div
                              key={idx}
                              className="border border-slate-200 rounded-lg overflow-hidden"
                            >
                              <div className="bg-slate-100 px-4 py-2 border-b border-slate-200 flex justify-between items-center text-xs print:text-sm">
                                <span className="font-bold text-slate-800">
                                  Date:{" "}
                                  {new Date(record.date).toLocaleDateString("en-GB")}
                                </span>
                                <span className="text-slate-500">
                                  Prescriber: {record.physicianId}
                                </span>
                              </div>
                              <div className="p-0">
                                <table className="w-full text-left text-xs print:text-sm">
                                  <thead className="bg-white border-b border-slate-100 text-slate-500 uppercase tracking-widest text-[9px]">
                                    <tr>
                                      <th className="px-4 py-2 font-semibold">
                                        Medication Name
                                      </th>
                                      <th className="px-4 py-2 font-semibold">
                                        Dosage
                                      </th>
                                      <th className="px-4 py-2 font-semibold">
                                        Frequency
                                      </th>
                                      <th className="px-4 py-2 font-semibold">
                                        Duration
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-slate-100">
                                    {record.meds.map((med: any, i: number) => (
                                      <tr key={i} className="hover:bg-slate-50">
                                        <td className="px-4 py-2 font-medium text-slate-900">
                                          {med.name}
                                        </td>
                                        <td className="px-4 py-2 text-slate-600">
                                          {med.dose || "-"}
                                        </td>
                                        <td className="px-4 py-2 text-slate-600">
                                          {med.frequency || "-"}
                                        </td>
                                        <td className="px-4 py-2 text-slate-600">
                                          {med.duration || "-"}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </div>

                  <div className="mt-auto pt-8 print:pt-2 border-t border-slate-200">
                    <div className="flex flex-col max-w-lg">
                      <p className="text-[11px] print:text-sm font-bold text-slate-500 uppercase tracking-wider mb-2">
                        Signature & Credentials / توقيع واعتماد الطبيب
                      </p>

                      <div className="grid grid-cols-12 gap-4 print:p-2 items-end bg-slate-50 p-3 print:p-1 rounded-lg border border-slate-200">
                        {/* Signature block */}
                        <div className="col-span-7 flex flex-col items-start border-r border-slate-200 pr-3">
                          {physicianSignature ? (
                            <div className="flex flex-col justify-end">
                              <img
                                src={physicianSignature}
                                alt="Physician Signature"
                                className="h-[64px] print:h-[54px] w-auto max-w-[150px] object-contain border-b border-black mb-1 mix-blend-multiply opacity-95"
                              />
                            </div>
                          ) : (
                            <div className="w-full mb-1">
                              <div className="h-[56px] print:h-[28px] w-full flex items-center justify-center border-b border-slate-400 border-dashed">
                                <SignaturePad
                                  label="Clinician Verification"
                                  onSave={setPhysicianSignature}
                                  onClear={() => setPhysicianSignature(null)}
                                />
                              </div>
                            </div>
                          )}
                          <p className="font-extrabold text-[9.5px] print:text-[14px] text-slate-800">
                            {currentPhysicianEn}
                          </p>
                          <p className="text-[8.5px] font-medium text-slate-500 mt-0.5">
                            Attending Physician • Reg: {currentRegNo}
                          </p>
                        </div>

                        {/* Arabic text block (adjacent and perfectly aligned RTL) */}
                        <div
                          className="col-span-5 text-right flex flex-col justify-end items-end font-arabic"
                          dir="rtl"
                          style={{ direction: "rtl" }}
                        >
                          <div className="font-extrabold text-[#0F172A] text-[15px] print:text-[16px] font-signature leading-tight">
                            {currentPhysicianAr}
                          </div>
                          <div className="text-[9.5px] font-bold text-slate-700 mt-1 leading-tight">
                            {currentPhysicianTitleAr}
                          </div>
                          <div className="text-[8.5px] text-slate-500 font-mono mt-0.5">
                            رقم التسجيل: {currentRegNo}
                          </div>
                        </div>
                      </div>

                      <div className="mt-2 flex justify-between items-center text-[10px] print:text-[11px] text-slate-500 px-1 font-sans">
                        <p>Date: {printDate}</p>
                        {physicianSignature && (
                          <p className="text-[9px] text-emerald-600 font-bold uppercase">
                            ✓ Digitally Signed
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {docType === "BILL_10000" && (
                <div className="mt-6 mb-6 px-4 print:px-2">
                  <div
                    className="mb-4 bg-amber-50 border border-amber-200 text-amber-900 text-xs px-3 py-2 rounded-md font-sans font-bold text-right print:hidden"
                    dir="rtl"
                  >
                    ✏️ يمكن تحرير مبلغ الكشفية والملحوظات الحسابية مباشرة أدناه!
                    ستقوم الأداة بمقترحات لغوية تلقائية عند تعديل الرقم (مثلاً:
                    15,000). تأكد من إكمال البيانات قبل الطباعة.
                  </div>
                  <div className="border-2 border-black rounded-lg p-6 font-arabic bg-white">
                    <div className="text-center mb-6 border-b-2 border-black pb-3">
                      <h2 className="text-xl font-black uppercase text-black font-sans mb-1">
                        عيادة الدكتور واثق عبدالخالق الجلبي
                      </h2>
                      <h3 className="text-lg font-bold font-arabic mb-1 text-slate-800">
                        وصل استلام نقدية طبية / Medical Receipt
                      </h3>
                      <p className="text-[10px] font-mono text-slate-500">
                        Document No. IQ-{displayPatient.id.slice(0, 6).toUpperCase()}-
                        {Math.floor(Math.random() * 100000)}
                      </p>
                      <p className="text-[10px] font-mono text-slate-500 mt-1">
                        Date & Time: {printDate}{" "}
                        {new Date().toLocaleTimeString("en-IQ")}
                      </p>
                    </div>

                    <div className="grid grid-cols-2 gap-6 mb-6 text-sm">
                      <div className="flex flex-col gap-3">
                        <div className="border border-slate-300 rounded p-3 bg-slate-50">
                          <p className="text-[#64748B] text-[9px] uppercase font-bold tracking-widest mb-1 font-sans">
                            Patient Name / اسم المراجع الثلاثي
                          </p>
                          <textarea
                            rows={2}
                            value={receiptName}
                            onChange={(e) => {
                              const val = e.target.value;
                              setReceiptName(val);
                              localStorage.setItem(
                                `receipt_name_${recordKey}`,
                                val,
                              );
                            }}
                            className="font-bold text-sm text-black bg-transparent border-b border-dashed border-slate-300 focus:border-emerald-600 focus:outline-none w-full py-1 resize-none print:border-none print:py-0"
                          />
                        </div>
                        <div className="border border-slate-300 rounded p-3 bg-slate-50 flex justify-between items-center">
                          <div>
                            <p className="text-[#64748B] text-[9px] uppercase font-bold tracking-widest mb-1 font-sans">
                              Patient ID / رقم الملف
                            </p>
                            <p className="font-mono font-bold text-sm text-black">
                              {displayPatient.id}
                            </p>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div className="border border-slate-300 rounded p-3 bg-slate-50">
                            <p className="text-[#64748B] text-[9px] uppercase font-bold tracking-widest mb-1 font-sans text-center">
                              Payment Method / طريقة الدفع
                            </p>
                            <select
                              value={paymentMethod}
                              onChange={(e) => {
                                const val = e.target.value;
                                setPaymentMethod(val);
                                localStorage.setItem(
                                  `payment_method_${recordKey}`,
                                  val,
                                );
                              }}
                              className="text-[11px] font-bold text-center bg-transparent border-none focus:outline-none w-full print:appearance-none cursor-pointer"
                            >
                              <option value="نقداً / Cash">نقداً / Cash</option>
                              <option value="بطاقة بنكية / Card">
                                بطاقة بنكية / Card
                              </option>
                              <option value="تحويل بنكي / Transfer">
                                تحويل بنكي / Transfer
                              </option>
                            </select>
                          </div>
                          <div className="border border-slate-300 rounded p-3 bg-slate-50">
                            <p className="text-[#64748B] text-[9px] uppercase font-bold tracking-widest mb-1 font-sans text-center">
                              Status / حالة المعاملة
                            </p>
                            <select
                              value={paymentStatus}
                              onChange={(e) => {
                                const val = e.target.value;
                                setPaymentStatus(val);
                                localStorage.setItem(
                                  `payment_status_${recordKey}`,
                                  val,
                                );
                              }}
                              className="text-[11px] font-bold text-center bg-transparent border-none focus:outline-none w-full print:appearance-none cursor-pointer"
                            >
                              <option value="مدفوعة بالكامل / Paid">
                                مدفوعة بالكامل / Paid
                              </option>
                              <option value="متبقي دفعة / Partial">
                                متبقي دفعة / Partial
                              </option>
                              <option value="مجانية / Complimentary">
                                مجانية / Complimentary
                              </option>
                            </select>
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-col justify-center items-center border border-slate-300 rounded p-3 bg-slate-50 h-full">
                        <p className="text-[#64748B] text-[9px] uppercase font-bold tracking-widest mb-2 font-sans">
                          Amount Paid / المبلغ المدفوع
                        </p>
                        <input
                          type="text"
                          value={billPrice}
                          onChange={(e) => {
                            const val = e.target.value;
                            setBillPrice(val);
                            localStorage.setItem(
                              `bill_price_${recordKey}`,
                              val,
                            );

                            const cleanVal = val.replace(/,/g, "").trim();
                            if (cleanVal === "10000" || cleanVal === "10") {
                              setBillPriceWords(
                                "عشرة آلاف دينار عراقي فقط لا غير",
                              );
                              localStorage.setItem(
                                `bill_price_words_${recordKey}`,
                                "عشرة آلاف دينار عراقي فقط لا غير",
                              );
                            } else if (
                              cleanVal === "15000" ||
                              cleanVal === "15"
                            ) {
                              setBillPriceWords(
                                "خمسة عشر ألف دينار عراقي فقط لا غير",
                              );
                              localStorage.setItem(
                                `bill_price_words_${recordKey}`,
                                "خمسة عشر ألف دينار عراقي فقط لا غير",
                              );
                            } else if (
                              cleanVal === "20000" ||
                              cleanVal === "20"
                            ) {
                              setBillPriceWords(
                                "عشرون ألف دينار عراقي فقط لا غير",
                              );
                              localStorage.setItem(
                                `bill_price_words_${recordKey}`,
                                "عشرون ألف دينار عراقي فقط لا غير",
                              );
                            } else if (
                              cleanVal === "25000" ||
                              cleanVal === "25"
                            ) {
                              setBillPriceWords(
                                "خمسة وعشرون ألف دينار عراقي فقط لا غير",
                              );
                              localStorage.setItem(
                                `bill_price_words_${recordKey}`,
                                "خمسة وعشرون ألف دينار عراقي فقط لا غير",
                              );
                            } else if (
                              cleanVal === "30000" ||
                              cleanVal === "30"
                            ) {
                              setBillPriceWords(
                                "ثلاثون ألف دينار عراقي فقط لا غير",
                              );
                              localStorage.setItem(
                                `bill_price_words_${recordKey}`,
                                "ثلاثون ألف دينار عراقي فقط لا غير",
                              );
                            } else if (
                              cleanVal === "50000" ||
                              cleanVal === "50"
                            ) {
                              setBillPriceWords(
                                "خمسون ألف دينار عراقي فقط لا غير",
                              );
                              localStorage.setItem(
                                `bill_price_words_${recordKey}`,
                                "خمسون ألف دينار عراقي فقط لا غير",
                              );
                            }
                          }}
                          className="font-black text-3xl text-black font-mono text-center bg-transparent border-b border-dashed border-slate-300 focus:border-emerald-600 focus:outline-none w-full py-1 text-center print:border-none print:py-0"
                        />
                        <p className="font-bold text-emerald-700 text-xs tracking-widest font-sans mt-1 text-center">
                          IQD / دينار عراقي
                        </p>
                        <input
                          type="text"
                          value={billPriceWords}
                          onChange={(e) => {
                            const val = e.target.value;
                            setBillPriceWords(val);
                            localStorage.setItem(
                              `bill_price_words_${recordKey}`,
                              val,
                            );
                          }}
                          className="text-[10px] font-bold text-center mt-2 border-t border-slate-200 pt-2 text-slate-700 bg-transparent border-b border-dashed border-slate-300 focus:border-emerald-600 focus:outline-none w-full print:border-none print:pt-2"
                        />
                      </div>
                    </div>

                    <div className="border-t border-black pt-3 mb-12">
                      <p className="text-[#64748B] text-[9px] uppercase font-bold tracking-widest mb-2 font-sans">
                        For Services Rendered / وذلك عن
                      </p>
                      <div className="flex flex-col gap-2">
                        <div
                          className="flex items-center gap-2 print:hidden"
                          dir="rtl"
                        >
                          <span className="text-[10px] text-slate-500 font-bold font-arabic">
                            اختر نوع الخدمة:
                          </span>
                          <select
                            value={
                              [
                                "Medical Consultation Visit / كشفية طبية",
                                "Minor Surgery / جراحة صغرى",
                                "Laser Therapy / علاج بالليزر",
                                "Dental Treatment / علاج الأسنان",
                              ].includes(billCategory)
                                ? billCategory
                                : "CUSTOM"
                            }
                            onChange={(e) => {
                              const val = e.target.value;
                              if (val !== "CUSTOM") {
                                setBillCategory(val);
                                localStorage.setItem(
                                  `bill_category_${recordKey}`,
                                  val,
                                );
                              }
                            }}
                            className="text-[10px] border border-slate-300 rounded px-2 py-1 bg-white font-sans text-slate-700 font-bold focus:outline-none focus:ring-1 focus:ring-emerald-600"
                          >
                            <option value="Medical Consultation Visit / كشفية طبية">
                              كشفية طبية / Medical Consultation Visit
                            </option>
                            <option value="Minor Surgery / جراحة صغرى">
                              جراحة صغرى / Minor Surgery
                            </option>
                            <option value="Laser Therapy / علاج بالليزر">
                              علاج بالليزر / Laser Therapy
                            </option>
                            <option value="Dental Treatment / علاج الأسنان">
                              علاج الأسنان / Dental Treatment
                            </option>
                            <option value="CUSTOM">
                              أخرى / Other (كتابة مخصصة)
                            </option>
                          </select>
                        </div>
                        <input
                          type="text"
                          value={billCategory}
                          onChange={(e) => {
                            const val = e.target.value;
                            setBillCategory(val);
                            localStorage.setItem(
                              `bill_category_${recordKey}`,
                              val,
                            );
                          }}
                          className="font-bold text-sm bg-emerald-50 text-emerald-900 border border-emerald-200 py-2 px-3 rounded font-sans uppercase focus:outline-none focus:ring-2 focus:ring-emerald-600 w-full print:border-none print:bg-transparent print:p-0 print:text-black"
                        />
                      </div>
                    </div>

                    <div className="flex justify-between items-end border-t-2 border-slate-200 pt-6 px-4">
                      <div className="text-center font-sans">
                        <p className="text-[#64748B] text-[9px] uppercase font-bold tracking-widest mb-4">
                          Attending Doctor / Reception
                        </p>
                        <p className="border-t border-black font-bold uppercase pt-1 px-4 text-[10px]">
                          Signature / Stamp
                        </p>
                      </div>
                      <div className="text-right font-sans">
                        <p className="text-[#64748B] text-[9px] uppercase font-bold tracking-widest mb-1">
                          Accountant/System Name
                        </p>
                        <p className="font-black uppercase text-[13px] text-indigo-800">
                          IQ-Doctor Financial System
                        </p>
                        <span
                          className="block font-bold text-[10px] font-arabic text-slate-800 mt-1"
                          style={{ fontFamily: "'Cairo', sans-serif" }}
                        >
                          الدكتور واثق الجلبي (Dr. Watheq Al-Chalabi Clinic)
                        </span>
                        <p className="font-mono text-[9px] text-slate-500 mt-1">
                          Date: {printDate}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {docType === "RECORD" && (
                <>
                  {/* Lab Trend Graphs */}

                  <div className="mb-6 print:mb-2 grid grid-cols-2 gap-4 print:p-2">
                    <LabTrendChart
                      orders={orders}
                      metricName="HbA1c (%)"
                      regex={/(?:HbA1c|A1C)[^\d]*([\d.]+)/i}
                      color="#f43f5e"
                    />
                    <LabTrendChart
                      orders={orders}
                      metricName="Cholesterol (mg/dL)"
                      regex={/(?:Cholesterol|CHOL)[^\d]*([\d.]+)/i}
                      color="#0ea5e9"
                    />
                  </div>

                  {/* GP SOAP Assessment Historical Timeline */}
                  <div className="mb-6 print:mb-2 space-y-3 print:space-y-0.5">
                    <h3 className="text-xs print:text-sm font-bold text-[#0F172A] uppercase tracking-wider border-b border-[#E2E8F0] pb-2 flex items-center gap-1.5">
                      <FileText className="w-4 h-4 text-[#64748B]" />
                      III. Attendant Physician Clinical Evaluation & SOAP Notes
                      / تقييم وملاحظات الطبيب المعالج السريرية
                    </h3>

                    <div className="space-y-3 print:space-y-0.5">
                      {/* Render Active Draft SOAP notes */}
                      {activeSession && (
                        <div className="border border-slate-300 bg-[#FCFDFE] p-4 print:p-2 rounded-lg space-y-3 print:space-y-0.5">
                          <div className="flex justify-between items-center text-[10px] print:text-[11px] text-slate-500 font-bold uppercase mb-1.5 border-b border-slate-200 pb-2">
                            <span className="text-[#1E293B] font-extrabold flex items-center gap-1.5">
                              <span className="w-2 h-2 rounded-full bg-indigo-600 animate-pulse print:hidden"></span>
                              Active Consultation Record
                            </span>
                            <span className="font-mono text-indigo-700 bg-indigo-50 border border-indigo-200 px-2 py-0.5 rounded text-[8.5px] uppercase tracking-wider">
                              Certified Entry
                            </span>
                          </div>

                          <div className="rounded-lg border border-slate-200 bg-white p-3 print:p-1.5 shadow-xs">
                            <textarea
                              rows={12}
                              value={recordClinicalNotes}
                              onChange={(e) => {
                                const val = e.target.value;
                                setRecordClinicalNotes(val);
                                localStorage.setItem(
                                  `rec_clinical_notes_${recordKey}`,
                                  val,
                                );
                              }}
                              dir="auto"
                              className="w-full text-sm print:text-[12px] font-medium leading-relaxed print:leading-tight text-slate-800 bg-transparent border-none p-0 focus:outline-none focus:ring-0 resize-y print:resize-none"
                              placeholder="اكتب التقييم والتقرير الطبي هنا باللغة العربية أو الإنجليزية..."
                            />
                            {activeSession.icd10Codes && (
                              <div className="mt-2.5 pt-2 border-t border-slate-100 flex justify-end">
                                <span className="inline-block bg-indigo-50 text-indigo-700 border border-indigo-200 px-2 py-0.5 rounded text-[9px] font-mono font-bold">
                                  ICD-10 Code Context:{" "}
                                  {activeSession.icd10Codes}
                                </span>
                              </div>
                            )}
                          </div>

                          {activeSession.prescriptions &&
                            activeSession.prescriptions.length > 0 && (
                              <div className="mt-3 bg-white border border-slate-200 rounded-lg overflow-hidden shadow-xs">
                                <div className="bg-slate-100 px-3 py-2 border-b border-slate-200 flex justify-between items-center">
                                  <span className="font-bold text-slate-800 uppercase text-[9px] tracking-wider flex items-center gap-1.5">
                                    <Activity className="w-3.5 h-3.5 text-indigo-600" />{" "}
                                    Prescribed Treatment Plan & Medications /
                                    الأدوية والعلاجات الموصوفة للمريض
                                  </span>
                                  <span className="text-[8px] bg-indigo-100 text-indigo-800 border border-indigo-200 px-1.5 py-0.5 rounded font-bold font-mono">
                                    OFFICIAL Rx
                                  </span>
                                </div>
                                <table className="w-full text-left text-[11px] print:text-[11px] border-collapse font-sans">
                                  <thead>
                                    <tr className="bg-slate-50 border-b border-slate-200 text-[#475569] uppercase tracking-wider text-[8px] font-extrabold">
                                      <th className="py-2 px-3 text-center w-8 border-r border-slate-200">
                                        #
                                      </th>
                                      <th className="py-2 px-3 border-r border-slate-200">
                                        Therapeutic Formulation & Strength / اسم
                                        المادة الطبية
                                      </th>
                                      <th className="py-2 px-3 text-center w-24 border-r border-slate-200">
                                        Dosage / الجرعة المقررة
                                      </th>
                                      <th className="py-2 px-3 text-center w-36 border-r border-slate-200">
                                        Frequency / التكرار اليومي
                                      </th>
                                      <th className="py-2 px-3 text-center w-24">
                                        Duration / مدة الاستخدام
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-slate-200 font-medium text-slate-800">
                                    {activeSession.prescriptions.map(
                                      (rx, idx) => (
                                        <tr
                                          key={idx}
                                          className="hover:bg-slate-50"
                                        >
                                          <td className="py-2 px-3 text-center border-r border-slate-200 font-bold">
                                            {idx + 1}
                                          </td>
                                          <td className="py-2 px-3 border-r border-slate-200 font-bold text-slate-900">
                                            {rx.name}
                                          </td>
                                          <td className="py-2 px-3 text-center border-r border-slate-200 font-semibold">
                                            {rx.dose || "—"}
                                          </td>
                                          <td className="py-2 px-3 text-center border-r border-slate-200 font-semibold text-xs print:text-[11px]">
                                            {rx.frequency || "—"}
                                          </td>
                                          <td className="py-2 px-3 text-center font-mono font-bold text-[10px] print:text-[11px]">
                                            {rx.duration || "—"}
                                          </td>
                                        </tr>
                                      ),
                                    )}
                                  </tbody>
                                </table>
                              </div>
                            )}

                          {activeSession.gpNotes.trim() && (
                            <div className="pt-2">
                              <strong className="text-[10px] print:text-[11px] text-slate-500 uppercase">
                                ADDITIONAL CLINICAL NOTES:
                              </strong>
                              <p className="text-sm print:text-[12px] whitespace-pre-wrap leading-relaxed print:leading-tight text-slate-800">
                                {activeSession.gpNotes}
                              </p>
                            </div>
                          )}
                        </div>
                      )}

                      {!(
                        activeSession &&
                        (activeSession.gpNotes.trim() ||
                          activeSession.soapSubjective)
                      ) && (
                        <p className="text-[10px] print:text-[11px] text-[#64748B] italic">
                          No active GP clinical consults documented.
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Patient Medical History */}
                  <div className="mb-6 print:mb-2 border-t border-[#E2E8F0] pt-6 print:pt-2 space-y-4 print:space-y-0.5">
                    <h3 className="text-xs print:text-sm font-bold text-[#0F172A] uppercase tracking-wider flex items-center gap-1.5">
                      <History className="w-4 h-4 text-[#64748B]" />
                      V. Patient Medical History Archives
                    </h3>

                    <div className="grid grid-cols-2 gap-6 print:p-2">
                      <div className="space-y-2 print:space-y-0.5">
                        <h4 className="text-[10px] print:text-[11px] font-bold text-slate-700 uppercase tracking-wider border-b border-slate-200 pb-1">
                          Past Surgeries
                        </h4>
                        {medicalHistory?.surgeries &&
                        medicalHistory.surgeries?.length > 0 ? (
                          medicalHistory.surgeries?.map((s) => (
                            <div
                              key={s.id}
                              className="text-xs print:text-sm bg-slate-50 p-2 rounded border border-slate-200"
                            >
                              <div className="font-bold text-slate-800">
                                {s.procedure}{" "}
                                <span className="font-mono text-[10px] print:text-[11px] text-slate-500 ml-1">
                                  ({s.year})
                                </span>
                              </div>
                              {s.notes && (
                                <div className="text-[10px] print:text-[13px] text-slate-600 mt-0.5">
                                  {s.notes}
                                </div>
                              )}
                            </div>
                          ))
                        ) : (
                          <p className="text-[10px] print:text-[11px] text-[#64748B] italic">
                            No historical surgeries reported.
                          </p>
                        )}
                      </div>

                      <div className="space-y-2 print:space-y-0.5">
                        <h4 className="text-[10px] print:text-[11px] font-bold text-slate-700 uppercase tracking-wider border-b border-slate-200 pb-1">
                          Chronic Conditions
                        </h4>
                        {medicalHistory?.chronicConditions &&
                        medicalHistory.chronicConditions?.length > 0 ? (
                          medicalHistory.chronicConditions?.map((cc) => (
                            <div
                              key={cc.id}
                              className="text-xs print:text-sm bg-slate-50 p-2 rounded border border-slate-200"
                            >
                              <div className="flex items-center gap-2 print:gap-1">
                                <span className="font-bold text-slate-800">
                                  {cc.condition}
                                </span>
                                <span
                                  className={`text-[8px] uppercase font-bold px-1 py-0.5 rounded border ${
                                    cc.status === "ACTIVE"
                                      ? "bg-rose-50 text-rose-700 border-rose-200"
                                      : cc.status === "MANAGED"
                                        ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                        : "bg-slate-100 text-slate-600 border-slate-200"
                                  }`}
                                >
                                  {cc.status}
                                </span>
                              </div>
                              <div className="font-mono text-[9px] text-slate-500 mt-1">
                                Diagnosed: {cc.diagnosedYear}
                              </div>
                              {cc.notes && (
                                <div className="text-[10px] print:text-[13px] text-slate-600 mt-0.5">
                                  {cc.notes}
                                </div>
                              )}
                            </div>
                          ))
                        ) : (
                          <p className="text-[10px] print:text-[11px] text-[#64748B] italic">
                            No active or managed conditions reported.
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Minor Operative Surgery Log */}
                  <div className="mb-6 print:mb-2 border-t border-[#E2E8F0] pt-6 print:pt-2 space-y-3 print:space-y-0.5">
                    <h3 className="text-xs print:text-sm font-bold text-[#0F172A] uppercase tracking-wider flex items-center gap-1.5">
                      <Layers className="w-4 h-4 text-[#64748B]" />
                      VI. Minor Surgical Operations Ledger
                    </h3>

                    <div className="space-y-3 print:space-y-0.5">
                      {activeSession && activeSession.minorSurgery.trim() && (
                        <div className="border border-slate-300 bg-[#FCFDFE] p-4 print:p-2 rounded-lg">
                          <div className="flex justify-between text-[10px] print:text-[11px] text-slate-500 font-bold uppercase mb-1.5">
                            <span className="text-slate-800 font-bold">
                              Current Surgery Session (Active Draft SOAP)
                            </span>
                            <span className="font-mono">
                              Unsigned Operation Draft
                            </span>
                          </div>
                          <p className="text-sm print:text-[12px] whitespace-pre-wrap leading-relaxed print:leading-tight text-slate-800 font-medium">
                            {activeSession.minorSurgery}
                          </p>
                        </div>
                      )}

                      {!(
                        activeSession && activeSession.minorSurgery.trim()
                      ) && (
                        <p className="text-[10px] print:text-[11px] text-[#64748B] italic">
                          No active minor surgery excise records logged on node.
                        </p>
                      )}
                    </div>
                  </div>

                  {/* CPOE Diagnostic Directives Ledger */}
                  <div className="border-t border-slate-300 pt-6 print:pt-2 space-y-3 print:break-inside-avoid">
                    <h3 className="text-xs print:text-sm font-bold text-[#0F172A] uppercase tracking-wider flex items-center gap-1.5 border-b pb-2">
                      <CheckCircle className="w-4 h-4 text-indigo-600" />
                      VI. Requested Laboratory Examinations & Diagnostic
                      Procedures / الفحوصات المختبرية والتشخيصية المطلوبة
                    </h3>

                    <div className="space-y-3 print:space-y-0.5">
                      {orders.filter((o) => o.patientId === displayPatient.id && o.department !== "PHARMACY").length >
                      0 ? (
                        <div className="border border-slate-200 rounded-lg overflow-hidden bg-white shadow-xs">
                          <table className="w-full text-left text-[11px] print:text-[11px] border-collapse font-sans">
                            <thead>
                              <tr className="bg-slate-50 border-b border-slate-200 text-[#475569] uppercase tracking-wider text-[8px] font-extrabold">
                                <th className="py-2 px-3 text-center w-8 border-r border-slate-200">
                                  #
                                </th>
                                <th className="py-2 px-3 border-r border-slate-200 w-32">
                                  Department / القسم المختص
                                </th>
                                <th className="py-2 px-3 border-r border-slate-200">
                                  Diagnostic Request & Instructions / طبيعة
                                  الفحص المطلوب
                                </th>
                                <th className="py-2 px-3 text-center w-24">
                                  Status / الحالة
                                </th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-200 font-medium text-slate-800">
                              {orders
                                .filter((o) => o.patientId === displayPatient.id && o.department !== "PHARMACY")
                                .map((order, idx) => (
                                  <tr
                                    key={order.id}
                                    className="hover:bg-slate-50"
                                  >
                                    <td className="py-2 px-3 text-center border-r border-slate-200 font-bold">
                                      {idx + 1}
                                    </td>
                                    <td className="py-2 px-3 border-r border-slate-200">
                                      <span
                                        className={`font-bold text-[9px] uppercase tracking-wider px-2 py-0.5 rounded border inline-block ${
                                          order.department === "LAB"
                                            ? "bg-purple-50 text-purple-700 border-purple-200"
                                            : order.department === "X_RAY"
                                              ? "bg-sky-50 text-sky-700 border-sky-200"
                                              : "bg-teal-50 text-teal-700 border-teal-200"
                                        }`}
                                      >
                                        {order.department}
                                      </span>
                                    </td>
                                    <td className="py-2 px-3 border-r border-slate-200">
                                      <div className="font-bold text-slate-900">
                                        "{order.instruction}"
                                      </div>
                                      {order.results && (
                                        <div className="mt-1.5 bg-slate-50 border border-slate-200 p-2 rounded text-[10px] print:text-[11px] font-mono text-slate-800 whitespace-pre-wrap">
                                          <span className="font-bold text-indigo-900 block uppercase text-[7.5px] tracking-wider mb-0.5">
                                            Clinical Findings & Results Decoded
                                            / النتائج السريرية المرصودة
                                          </span>
                                          {order.results}
                                        </div>
                                      )}
                                    </td>
                                    <td className="py-2 px-3 text-center">
                                      <span
                                        className={`font-bold text-[8px] uppercase tracking-widest px-1.5 py-0.5 rounded border inline-block ${
                                          order.status === "PENDING"
                                            ? "bg-amber-50 text-amber-700 border-amber-200"
                                            : "bg-emerald-50 text-emerald-700 border-emerald-200"
                                        }`}
                                      >
                                        {order.status}
                                      </span>
                                    </td>
                                  </tr>
                                ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <p className="text-[10px] print:text-[11px] text-[#64748B] italic">
                          No laboratory, radiography or pharmacy dispensaries
                          directed for this patient.
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Official Signatures Panel */}
                  <div className="mt-auto pt-6 print:pt-2 border-t-2 border-dashed border-[#CBD5E1] grid grid-cols-2 gap-12 print:gap-4 text-xs print:break-inside-avoid">
                    <div className="flex flex-col">
                      {physicianSignature ? (
                        <div className="flex flex-col justify-end text-center">
                          <img
                            src={physicianSignature}
                            alt="Physician Signature"
                            className="h-[64px] print:h-[54px] w-auto max-w-[150px] object-contain border-b border-black mb-1 mix-blend-multiply opacity-95"
                          />
                        </div>
                      ) : (
                        <div className="mb-2 text-center w-full">
                          <SignaturePad
                            label="Physician Signature"
                            onSave={setPhysicianSignature}
                            onClear={() => setPhysicianSignature(null)}
                          />
                          <div className="h-4 border-b border-slate-300 mb-2"></div>
                        </div>
                      )}
                      <div className="flex flex-col justify-end items-center text-center font-arabic">
                        <div
                          className="font-extrabold text-black mb-0.5 text-[12px] print:text-[13px] font-signature"
                          dir="rtl"
                          style={{ direction: "rtl" }}
                        >
                          {currentPhysicianAr}
                        </div>
                        <div
                          className="text-[9.5px] font-bold text-slate-700"
                          dir="rtl"
                          style={{ direction: "rtl" }}
                        >
                          {currentPhysicianTitleAr}
                        </div>
                        <div
                          className="text-[8.5px] text-slate-500 font-mono mb-1"
                          dir="rtl"
                          style={{ direction: "rtl" }}
                        >
                          رقم التسجيل: {currentRegNo}
                        </div>
                        <strong className="text-slate-800 block uppercase tracking-wide text-[9px] mt-1">
                          ATTENDING CLINICIAN SIGNATURE
                        </strong>
                        <span className="text-slate-500 font-medium text-[10px] print:text-[11px]">
                          {currentPhysicianEn} • Reg: {currentRegNo}
                        </span>
                      </div>
                    </div>
                    <div className="flex flex-col">
                      {patientSignature ? (
                        <div className="flex flex-col justify-end text-center">
                          <img
                            src={patientSignature}
                            alt="Patient Signature"
                            className="h-[48px] print:h-[28px] print:h-[36px] object-contain border-b border-slate-300 mb-2 w-full"
                          />
                        </div>
                      ) : (
                        <div className="mb-2 text-center w-full">
                          <SignaturePad
                            label="Patient Signature"
                            onSave={setPatientSignature}
                            onClear={() => setPatientSignature(null)}
                          />
                          <div className="h-4 border-b border-slate-300 mb-2"></div>
                        </div>
                      )}
                      <div className="text-center">
                        <strong className="text-slate-800 block uppercase tracking-wide text-[9px]">
                          PATIENT UNDERTAKING / RECORD CONSENT
                        </strong>
                        <span className="text-slate-500 font-medium text-[10px] print:text-[11px]">
                          Secure Digital Consent Decoded
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* EXPERT COMMENTARY: Thick solid black divider representing official sign-off with attestation label */}
                  <div className="border-t-2 border-black w-full print:break-inside-avoid">
                    <div className="text-center py-2 space-y-1">
                      <p className="font-extrabold text-slate-900 text-[11px] print:text-sm uppercase tracking-widest font-sans">
                        Given upon patient's request / أُعطي بناءً على طلب
                        المريض
                      </p>
                      <p className="text-[9.5px] text-slate-500 font-mono">
                        Official Document Verification Code: IQDOC-SECURE-
                        {displayPatient.id.toUpperCase()}-
                        {new Date().getTime().toString(36).toUpperCase()}
                      </p>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Custom Telegram share & PDF preview modal */}
            {isGeneratingPdf && (
              <div className="fixed inset-0 z-[100] bg-slate-900/80 backdrop-blur-xs flex flex-col items-center justify-center p-6 print:p-2 text-center text-white no-print">
                <div className="w-12 h-12 border-4 border-t-sky-500 border-r-transparent border-b-sky-600 border-l-transparent rounded-full animate-spin mb-4 print:mb-1"></div>
                <h3 className="text-sm print:text-sm font-bold text-slate-150">
                  جاري معالجة وتوليد ورقة الـ PDF الطبي وتنبيه التليجرام...
                </h3>
                <p className="text-[11px] print:text-sm text-slate-400 mt-1 max-w-xs">
                  نقوم بمحاكاة وتجميع المستند الطبي لإنتاج ملف PDF عالي الدقة.
                </p>
              </div>
            )}

            {sharePdfData && (
              <div className="fixed inset-0 z-[100] bg-slate-950/85 backdrop-blur-md flex items-center justify-center p-4 print:p-2 no-print select-text">
                <div
                  onDragEnter={handleDrag}
                  onDragOver={handleDrag}
                  onDragLeave={handleDrag}
                  onDrop={handleDrop}
                  className={`bg-white rounded-xl border border-slate-200 shadow-2xl overflow-y-auto w-full max-w-md max-h-[90vh] p-4 text-center space-y-3 animate-in fade-in zoom-in-95 duration-250 relative ${
                    dragActive ? "ring-2 ring-sky-500 bg-sky-50/10" : ""
                  }`}
                >
                  {/* Header Title */}
                  <div className="flex items-center justify-between border-b border-slate-150 pb-2.5">
                    <div className="flex items-center gap-1.5 text-sky-600">
                      <span className="w-2 rounded-full h-2 bg-sky-500 animate-pulse"></span>
                      <span className="text-xs font-bold text-slate-700 font-sans tracking-wide">
                        الملف جاهز للمشاركة / Ready for {sharePlatform}
                      </span>
                    </div>
                    <button
                      onClick={() => {
                        if (sharePdfData.pdfBlobUrl) {
                          URL.revokeObjectURL(sharePdfData.pdfBlobUrl);
                        }
                        setSharePdfData(null);
                      }}
                      className="text-slate-400 hover:text-slate-600 transition p-1 hover:bg-slate-100 rounded-md cursor-pointer border-0"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Informative description */}
                  <p className="text-[11px] text-slate-600 leading-relaxed text-right font-sans">
                    تم تكوين المستند الطبي كـ PDF بنجاح. يمكنك معاينة نص التنبيه
                    وتعديله أدناه قبل المتابعة.
                  </p>

                  {/* Text Preview Editable Box */}
                  <div className="space-y-1 text-right">
                    <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                      نص التنبيه ومعاينة الإرسال (اضغط للتعديل):
                    </label>
                    <textarea
                      value={shareMessageText}
                      onChange={(e) => setShareMessageText(e.target.value)}
                      rows={4}
                      className="w-full text-xs font-sans p-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-sky-500 bg-slate-50/70 text-right outline-none resize-none leading-relaxed"
                      placeholder="اكتب التنبيه هنا..."
                    />
                  </div>

                  {/* High-visibility Attached Document indicator */}
                  <div className="flex items-center gap-2 p-2 bg-sky-50 border border-sky-100 rounded-lg text-sky-800 text-xs font-bold justify-between">
                    <div className="flex items-center gap-1.5 truncate">
                      <span className="text-sm">📎</span>
                      <span
                        className="truncate max-w-[200px] font-mono text-[10px]"
                        dir="ltr"
                      >
                        {sharePdfData.filename}
                      </span>
                    </div>
                    <span className="bg-sky-600 text-white text-[8px] px-1.5 py-0.5 rounded-md uppercase tracking-wide font-mono">
                      PDF Attached
                    </span>
                  </div>

                  {/* Alternate File Custom Dropzone */}
                  <div className="relative border border-dashed border-sky-200 rounded-lg p-2.5 text-center bg-sky-50/5 hover:bg-sky-50/15 transition-all">
                    <input
                      type="file"
                      id="share-attachment-upload"
                      className="hidden"
                      accept="image/*,application/pdf,text/plain"
                      onChange={handleChange}
                      disabled={isConvertingAttachment}
                    />
                    <label
                      htmlFor="share-attachment-upload"
                      className={`flex flex-col items-center justify-center gap-0.5 cursor-pointer ${
                        isConvertingAttachment
                          ? "pointer-events-none opacity-50"
                          : ""
                      }`}
                    >
                      <div className="p-1 px-3 bg-sky-50 text-sky-700 rounded-md flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider">
                        <Upload className="w-3 h-3" />
                        إرفاق مستند بديل / Custom File
                      </div>
                    </label>

                    {isConvertingAttachment && (
                      <div className="absolute inset-0 bg-white/95 rounded-lg flex flex-col items-center justify-center">
                        <div className="w-4 h-4 border-2 border-t-sky-600 border-r-transparent border-b-sky-700 border-l-transparent rounded-full animate-spin"></div>
                      </div>
                    )}
                  </div>

                  {attachmentError && (
                    <div className="text-[9px] bg-rose-50 border border-rose-100 text-rose-600 p-1.5 rounded-md text-right font-sans">
                      ⚠️ {attachmentError}
                    </div>
                  )}

                  {/* PDF Preview Frame */}
                  <div className="bg-slate-50 border border-slate-150 rounded-lg p-2 space-y-2">
                    {/* Live PDF frame */}
                    <div className="w-full border border-slate-200 rounded-md overflow-hidden h-24 bg-slate-100 relative shadow-inner">
                      <iframe
                        src={`${sharePdfData.pdfBlobUrl}#toolbar=0&navpanes=0&scrollbar=0`}
                        className="w-full h-full border-0 select-none"
                        title="Clinical PDF Preview Rendered"
                      />
                      <div className="absolute inset-0 bg-transparent pointer-events-none" />
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      {/* Direct Download */}
                      <button
                        type="button"
                        onClick={() => {
                          const link = document.createElement("a");
                          link.href = sharePdfData.pdfBlobUrl;
                          link.download = sharePdfData.filename;
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                        }}
                        className="text-[9px] bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold py-1 px-2 rounded border border-slate-200 flex items-center justify-center gap-1 transition cursor-pointer"
                      >
                        <Printer className="w-3 h-3" />
                        تحميل الـ PDF يدوياً
                      </button>

                      {/* Copy Alert Content */}
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            await navigator.clipboard.writeText(
                              shareMessageText,
                            );
                            alert("تم نسخ نص التنبيه بنجاح!");
                          } catch (_) {
                            alert("فشل نسخ النص.");
                          }
                        }}
                        className="text-[9px] bg-sky-50 hover:bg-sky-100 text-sky-700 font-bold py-1 px-2 rounded border border-sky-200 flex items-center justify-center gap-1 transition cursor-pointer"
                      >
                        <Copy className="w-3 h-3" />
                        نسخ نص الرسالة
                      </button>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="space-y-2 pt-1">
                    {!isConvertingAttachment ? (
                      <div className="space-y-2 animate-in fade-in slide-in-from-bottom-4 duration-300">
                        <button
                          id="share-icon-btn"
                          onClick={async () => {
                            // 1. Copy text to clipboard as a fallback
                            try {
                              await navigator.clipboard.writeText(
                                shareMessageText,
                              );
                            } catch (_) {}

                            // Try native Web Share API to attach the actual file
                            let nativeShareSuccess = false;
                            if (
                              sharePdfData.blob &&
                              navigator.share &&
                              navigator.canShare
                            ) {
                              try {
                                const file = new File(
                                  [sharePdfData.blob],
                                  sharePdfData.filename,
                                  { type: "application/pdf" },
                                );
                                if (navigator.canShare({ files: [file] })) {
                                  await navigator.share({
                                    title: "Clinical Document",
                                    text: shareMessageText,
                                    files: [file],
                                  });
                                  nativeShareSuccess = true;
                                }
                              } catch (err) {
                                console.log(
                                  "Native share failed or canceled by user",
                                  err,
                                );
                                // If the user cancelled, `err.name` is usually "AbortError"
                                if (
                                  err instanceof Error &&
                                  err.name === "AbortError"
                                ) {
                                  return; // Just stop, user aborted
                                }
                              }
                            }

                            // Fallback: If Web Share API is not supported or failed, open web link
                            if (!nativeShareSuccess) {
                              let finalUrl = sharePdfData.shareUrl;
                              if (sharePlatform === "whatsapp") {
                                finalUrl = `https://wa.me/${sharePdfData.shareUrl.split("/").pop()?.split("?")[0].replace("+", "")}?text=${encodeURIComponent(shareMessageText)}`;
                              } else {
                                finalUrl = sharePdfData.shareUrl;
                              }
                              window.open(finalUrl, "_blank");
                            }

                            // Automatically close modal seamlessly
                            if (sharePdfData.pdfBlobUrl) {
                              URL.revokeObjectURL(sharePdfData.pdfBlobUrl);
                            }
                            setSharePdfData(null);
                          }}
                          className={`w-full text-white font-extrabold py-2.5 px-4 rounded-lg text-xs shadow-md active:scale-95 transition-all cursor-pointer border-0 duration-200 flex items-center justify-center gap-2 ${sharePlatform === "whatsapp" ? "bg-[#25D366] hover:bg-[#20ba5a] shadow-emerald-500/20" : "bg-[#0088cc] hover:bg-[#0077b5] shadow-sky-500/20"}`}
                          title="اضغط للمشاركة والعودة للسجل"
                        >
                          <Share2 className="w-4 h-4 text-white" />
                          <span>إرسال (نسخ النص والفتح) / Send</span>
                        </button>
                        <div className="text-center">
                          <span
                            className="text-[9px] text-slate-400 block"
                            dir="ltr"
                          >
                            Copies text to clipboard, and opens patient's chat
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center py-2 text-sky-500 gap-1">
                        <div className="w-4 h-4 border-2 border-t-sky-600 border-r-transparent border-b-sky-700 border-l-transparent rounded-full animate-spin"></div>
                        <span className="text-[8.5px] font-bold uppercase tracking-wider text-sky-600">
                          Awaiting PDF conversion...
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {showGmailModal && (
              <div className="fixed inset-0 z-[110] bg-slate-950/85 backdrop-blur-sm flex items-center justify-center p-4 print:p-2 no-print select-text">
                <div className="bg-white rounded-xl border border-slate-200 shadow-2xl overflow-y-auto w-full max-w-lg max-h-[90vh] p-4 text-right space-y-3 animate-in fade-in zoom-in-95 duration-250 relative">
                  {/* Header Title with Arabic Translation */}
                  <div className="flex items-center justify-between border-b border-slate-150 pb-3">
                    <div className="flex items-center gap-2 text-rose-600">
                      <Mail className="w-5 h-5 animate-pulse" />
                      <span className="text-sm font-bold text-slate-800 font-sans tracking-wide">
                        إرسال البريد الإلكتروني الطبي / Gmail Delivery Portal
                      </span>
                    </div>
                    <button
                      onClick={() => {
                        setShowGmailModal(false);
                        setGmailErrorMessage(null);
                        setGmailSuccessMessage(null);
                      }}
                      className="text-slate-400 hover:text-slate-600 transition p-1 hover:bg-slate-100 rounded-md cursor-pointer"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Secure Auth Sync Dashboard */}
                  <div className="p-3 bg-slate-50 border border-slate-150 rounded-lg flex items-center justify-between gap-3 text-xs">
                    <div className="flex items-center gap-2">
                      <span
                        className={`w-2.5 h-2.5 rounded-full ${isGoogleWorkspaceAuthenticated ? "bg-emerald-500 animate-pulse" : "bg-amber-500"}`}
                      ></span>
                      <div className="text-right">
                        <span className="font-bold text-slate-700 block">
                          {isGoogleWorkspaceAuthenticated
                            ? `متصل: ${googleUser?.email || "حساب العيادة"}`
                            : "بريد Google الطبي غير مربوط"}
                        </span>
                        <span className="text-[10px] text-slate-500 block">
                          {isGoogleWorkspaceAuthenticated
                            ? "تمت المصادقة بنجاح على بريد العيادة"
                            : "يرجى ربط حساب الطبيب لإرسال السجلات للمرضى"}
                        </span>
                      </div>
                    </div>
                    <div>
                      {isGoogleWorkspaceAuthenticated ? (
                        <button
                          onClick={handleDisconnectGmail}
                          className="text-[10px] text-red-600 hover:text-red-700 font-bold flex items-center gap-1 cursor-pointer border-0 bg-transparent"
                        >
                          <LogOut className="w-3 h-3" />
                          إلغاء الربط / Disconnect
                        </button>
                      ) : (
                        <button
                          onClick={handleConnectGmail}
                          disabled={isGoogleWorkspaceLoggingIn}
                          className="bg-slate-900 hover:bg-slate-800 text-white px-3 py-1.5 rounded text-[10px] font-bold flex items-center gap-1 cursor-pointer transition disabled:opacity-50 border-0"
                        >
                          <LogIn className="w-3 h-3 text-white" />
                          {isGoogleWorkspaceLoggingIn
                            ? "جاري الاتصال..."
                            : "ربط الحساب / Connect Google"}
                        </button>
                      )}
                    </div>
                  </div>

                  {gmailErrorMessage && (
                    <div className="p-3 bg-rose-50 border border-rose-150 text-rose-800 text-xs rounded-lg flex items-start gap-2 text-right">
                      <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-rose-600" />
                      <div>
                        <strong className="font-bold">
                          تنبيه أمني / Connection Alert:
                        </strong>
                        <p className="mt-0.5">{gmailErrorMessage}</p>
                      </div>
                    </div>
                  )}

                  {gmailSuccessMessage && (
                    <div className="p-3 bg-emerald-50 border border-emerald-150 text-emerald-800 text-xs rounded-lg flex items-start gap-2 text-right">
                      <CheckCircle className="w-4 h-4 shrink-0 mt-0.5 text-emerald-600" />
                      <div>
                        <strong className="font-bold">
                          نجاح الإرسال / Despatched:
                        </strong>
                        <p className="mt-0.5">{gmailSuccessMessage}</p>
                      </div>
                    </div>
                  )}

                  {/* Form Controls */}
                  <div className="space-y-3 text-xs text-right">
                    {/* Recipient Input */}
                    <div className="space-y-1">
                      <label className="block font-bold text-slate-700">
                        البريد الإلكتروني للمستلم / Patient Email Address *
                      </label>
                      <input
                        type="email"
                        value={gmailRecipient}
                        onChange={(e) => setGmailRecipient(e.target.value)}
                        placeholder="example@patient-mail.com"
                        className="w-full text-left px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-rose-500 font-sans tracking-wide"
                        dir="ltr"
                        disabled={isGmailSending}
                      />

                      {/* Save Checkbox */}
                      <label className="flex items-center gap-2 mt-1 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={saveEmailToPatientFile}
                          onChange={(e) =>
                            setSaveEmailToPatientFile(e.target.checked)
                          }
                          className="rounded border-slate-300 text-rose-600 focus:ring-rose-500 h-3.5 w-3.5 cursor-pointer"
                          disabled={isGmailSending}
                        />
                        <span className="text-[10px] text-slate-500 font-semibold">
                          حفظ هذا العنوان بشكل دائم في السجل الصحي للمريض الحالي
                        </span>
                      </label>
                    </div>

                    {/* Subject Input */}
                    <div className="space-y-1">
                      <label className="block font-bold text-slate-700">
                        عنوان الرسالة / Subject
                      </label>
                      <input
                        type="text"
                        value={gmailSubject}
                        onChange={(e) => setGmailSubject(e.target.value)}
                        placeholder="موضوع الرسالة الطبية"
                        className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-rose-500 text-slate-850 font-sans"
                        disabled={isGmailSending}
                      />
                    </div>

                    {/* Aesthetic Interactive Browser Email Preview */}
                    <div className="space-y-1.5 pt-1">
                      <label className="block font-bold text-slate-700">
                        معاينة شكل الرسالة / Clinical Email Live Preview
                      </label>
                      <div className="border border-slate-200 rounded-lg overflow-hidden bg-slate-50 text-[10px]">
                        {/* Browser Control Mock Header */}
                        <div
                          className="bg-slate-100 px-3 py-1.5 border-b border-slate-200 flex items-center justify-between"
                          dir="ltr"
                        >
                          <div className="flex items-center gap-1.5">
                            <span className="w-2.5 h-2.5 rounded-full bg-slate-300"></span>
                            <span className="w-2.5 h-2.5 rounded-full bg-slate-300"></span>
                            <span className="w-2.5 h-2.5 rounded-full bg-slate-300"></span>
                          </div>
                          <span className="text-slate-500 text-[9px] font-mono select-none">
                            iq-doctor.med.mail-node.com
                          </span>
                          <div></div>
                        </div>
                        {/* Embedded preview frame */}
                        <div className="p-3 max-h-[140px] overflow-y-auto bg-slate-100 select-text">
                          <div
                            className="bg-white p-3 border border-slate-200 rounded shadow-xs text-right"
                            dangerouslySetInnerHTML={{
                              __html: generateWelcomeLetterHtml(),
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="pt-2 border-t border-slate-150 flex items-center gap-3 justify-end">
                    <button
                      type="button"
                      onClick={() => {
                        setShowGmailModal(false);
                        setGmailErrorMessage(null);
                        setGmailSuccessMessage(null);
                      }}
                      className="px-4 py-2 border border-slate-200 hover:bg-slate-50 text-slate-700 rounded-lg text-xs font-bold transition cursor-pointer bg-white"
                      disabled={isGmailSending}
                    >
                      إلغاء / Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleSendGmailEmail}
                      disabled={
                        isGmailSending || !isGoogleWorkspaceAuthenticated
                      }
                      className="bg-rose-600 hover:bg-rose-500 text-white font-bold py-2 px-6 rounded-lg text-xs shadow-md shadow-rose-500/10 active:scale-95 transition flex items-center gap-1.5 cursor-pointer disabled:opacity-50 border-0"
                    >
                      {isGmailSending ? (
                        <>
                          <div className="w-3.5 h-3.5 border-2 border-t-white border-r-transparent border-b-white border-l-transparent rounded-full animate-spin"></div>
                          <span>جاري الإرسال الرقمي...</span>
                        </>
                      ) : (
                        <>
                          <Mail className="w-4 h-4 text-white" />
                          <span>
                            إرسال المستند بالبريد / Transmit Medical Mail
                          </span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Closes PRINT CANVAS CONTAINER WORKSPACE (Adds smooth scrolling overlay with shadow A4 clipping) */}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
